import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { Y, T, inp, CREDIT, LABEL_FONT } from "../theme";
import { KlingLogo, Pill, FocusTextarea, videoPromptLimit, imagePromptLimit, CopyBtn, ResPill, RES_COLORS, CreditIcon, downloadVideo, ModelIcon, rasterizeSvgBlobToPng, isSvgBlob, authDownloadAsJpg } from "./shared";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../useTheme";
import { api, auth, serverNow, isJobSentToProvider, JOB_SENT_TO_PROVIDER_HINT } from "../firebase";
import { useNotify, useHeaderRight, useConfirm, useLoadingLock, useScrollToTop } from "../App";
import PageLoader from "./PageLoader";
import { TutorialOverlay, VIDEO_STEPS, IMAGE_STEPS } from "./Tutorial";
import { IcoGenerate } from "../icons";
import { KLING_EFFECTS } from "../kling-effects-registry";

// Indexed lookup so render code can resolve scene -> { label, units,
// priceUsd, type, category } in O(1) instead of scanning the 270-entry
// array each call.
const KLING_EFFECT_BY_SCENE = new Map(KLING_EFFECTS.map(e => [e.scene, e]));
const getKlingEffect = (scene) => KLING_EFFECT_BY_SCENE.get(scene) || null;
// Cheapest / priciest effect in USD - drives the price-range hint on the
// Kling Effects model card. Derived from the registry so it stays correct
// when the parser regenerates after a Kling pricing change.
const KLING_EFFECT_PRICE_MIN_USD = Math.min(...KLING_EFFECTS.map(e => e.priceUsd));
const KLING_EFFECT_PRICE_MAX_USD = Math.max(...KLING_EFFECTS.map(e => e.priceUsd));

// Yellow-filled range slider
let _yrCounter = 0;
function RangeSlider({ min, max, step, value, onChange, title, style = {} }) {
  const [cls] = React.useState(() => `yr${_yrCounter++}`);
  const pct = ((value - min) / (max - min)) * 100;
  // Track unfilled color: T.overlay12 instead of T.text3 because the solid
  // slate-teal text3 reads as a hard dark line on the bright yellow page in
  // light mode. overlay12 gives 10% black in light / 12% white in dark, so
  // the unfilled track stays subtle without disappearing in either mode.
  const bg = `linear-gradient(to right,${Y} ${pct}%,${T.overlay12} ${pct}%)`;
  return (
    <div style={{ ...style, flex:1, display:"flex", alignItems:"center" }}>
      <style>{`
        .${cls}{-webkit-appearance:none;appearance:none;outline:none;cursor:pointer;width:100%;height:16px;border-radius:2px;margin:0;padding:0;background:transparent}
        .${cls}::-webkit-slider-runnable-track{height:4px;border-radius:2px;background:${bg}}
        .${cls}::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:50%;background:${Y};cursor:pointer;border:none;box-shadow:0 1px 3px rgba(0,0,0,0.25);margin-top:-6px}
        .${cls}::-moz-range-track{height:4px;border-radius:2px;background:${T.overlay12}}
        .${cls}::-moz-range-thumb{width:12px;height:12px;border-radius:50%;background:${Y};cursor:pointer;border:none;box-shadow:0 1px 3px rgba(0,0,0,0.25)}
        .${cls}::-moz-range-progress{background:${Y};height:4px;border-radius:2px}
      `}</style>
      <input type="range" className={cls} min={min} max={max} step={step} value={value}
        onChange={onChange} title={title}/>
    </div>
  );
}

const CYBER_BARS = 7;
// Mirror of PageLoader's CyberBars palette so light mode uses turquoise
// shades (the yellow alpha gradient disappears on the yellow page).
const MINI_TURQ_SHADES = [
  "rgba(0,107,118,0.25)",
  "rgba(0,127,138,0.40)",
  "rgba(0,150,150,0.55)",
  "rgba(0,170,150,0.65)",
  "rgba(0,184,148,0.75)",
  "rgba(6,202,160,0.85)",
  "rgba(6,214,160,0.95)",
];
function miniBarColor(mode, i, leadIdx) {
  if (i === leadIdx) return mode === "light" ? "#06D6A0" : Y;
  if (mode === "light") return MINI_TURQ_SHADES[i % MINI_TURQ_SHADES.length];
  return `rgba(238,255,68,${0.15 + (i/CYBER_BARS)*0.25})`;
}

export function MiniCyberBars() {
  const [tick, setTick] = useState(0);
  const { mode } = useTheme();
  useEffect(() => { const id = setInterval(() => setTick(t => t + 1), 120); return () => clearInterval(id); }, []);
  return (
    <div style={{display:"flex",alignItems:"flex-end",gap:2,height:20}}>
      {Array.from({length:CYBER_BARS}).map((_,i) => {
        const phase = (tick + i * 2) % (CYBER_BARS * 2);
        const h = phase < CYBER_BARS ? 6 + (i % 3) * 5 : 3;
        return <div key={i} style={{width:2,height:h,borderRadius:1,background:miniBarColor(mode, i, tick%CYBER_BARS),transition:"height 0.12s ease, background 0.12s ease"}}/>;
      })}
    </div>
  );
}

// Track an element's rendered width via ResizeObserver. Returns
// [callbackRef, width]. Width is 0 until the first measurement lands;
// consumers should guard their downstream math with `width > 0`.
function useElementSize() {
  const [width, setWidth] = useState(0);
  const observerRef = useRef(null);
  const elRef = useRef(null);
  const setRef = useCallback((node) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    elRef.current = node;
    if (!node) return;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width;
        if (typeof w === 'number') setWidth(w);
      });
      ro.observe(node);
      observerRef.current = ro;
    }
  }, []);
  useEffect(() => () => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);
  return [setRef, width];
}

function useNarrow(bp = 800) {
  const [n, setN] = useState(window.innerWidth <= bp);
  const fsRef = useRef(false);
  useEffect(() => {
    const onResize = () => {
      if (document.fullscreenElement || document.webkitFullscreenElement) { fsRef.current = true; return; }
      if (fsRef.current) return; // skip resize events right after fullscreen exit
      setN(window.innerWidth <= bp);
    };
    const onFsChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        // Exiting fullscreen — ignore resize events for 500ms while orientation settles
        fsRef.current = true;
        setTimeout(() => { fsRef.current = false; setN(window.innerWidth <= bp); }, 500);
      }
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      window.removeEventListener("resize", onResize);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, [bp]);
  return n;
}

// Kling API pricing: p5, p10, p5a, p10a are in UNITS (points); cost = units * user's ppv
// Per-second models (V3, Omni, O1): upsNA, upsA (units/sec), cost = ups * dur * ppv
// Source: Kling pricing page (screenshots in /kling-pricing-screens/)
const MODELS = [
  // Sorted: resolution desc (4K → 1080p → 720p → 480p), Grok before Kling, version desc
  // 4K — V3 only (audio included free at this tier)
  {id:"kling-3.0-4k",       label:"Kling 3.0 4K",       perSec:true, upsNA:3.0, upsA:3.0, minDur:3, maxDur:15, res:"4K",   audio:true},
  {id:"kling-3.0-omni-4k",  label:"Kling 3.0 Omni 4K",  perSec:true, upsNA:3.0, upsA:3.0, minDur:3, maxDur:15, res:"4K",   audio:true},
  // 1080p — Kling newest first, 3.0 before Omni
  {id:"kling-3.0-pro",      label:"Kling 3.0 Pro",      perSec:true, upsNA:0.8, upsA:1.2, minDur:3, maxDur:15, res:"1080p", audio:true},
  {id:"kling-3.0-omni-pro", label:"Kling 3.0 Omni Pro", perSec:true, upsNA:0.8, upsA:1.0, minDur:3, maxDur:15, res:"1080p", audio:true},
  // Kling 3.0 Turbo (Kling's newer API): cheaper, native audio always on
  // (audioFixed), first frame only. $0.14/s 1080p, $0.112/s 720p.
  {id:"kling-3.0-turbo-1080p", label:"Kling 3.0 Turbo", perSec:true, upsNA:1.0, upsA:1.0, minDur:3, maxDur:15, res:"1080p", audio:true, audioFixed:true},
  // Kling O1 / O1 Pro accept the sound parameter without erroring but
  // the resulting videos consistently come back without an audio track,
  // so we mark them audio:false until Kling officially adds support.
  {id:"kling-o1-pro",       label:"Kling O1 Pro",       perSec:true, upsNA:0.8, upsA:1.2, minDur:3, maxDur:10, res:"1080p"},
  {id:"kling-2.6-pro",      label:"Kling 2.6 Pro",      p5:2.5, p10:5.0, p5a:5.0, p10a:10.0, res:"1080p", audio:true},
  {id:"kling-2.5-turbo-pro",label:"Kling 2.5T Pro",     p5:2.5, p10:5.0, res:"1080p"},
  {id:"kling-2.1-master",   label:"Kling 2.1 Master",   p5:10.0, p10:20.0, res:"1080p"},
  {id:"kling-2.1-pro",      label:"Kling 2.1 Pro",      p5:3.5, p10:7.0, res:"1080p"},
  {id:"kling-1.6-pro",      label:"Kling 1.6 Pro",      p5:3.5, p10:7.0, res:"1080p"},
  {id:"kling-1.5-pro",      label:"Kling 1.5 Pro",      p10:7.0, fixedDur:10, res:"1080p"},
  // 720p - Grok first, then Kling newest first, 3.0 before Omni
  {id:"grok-imagine-720p",   label:"Grok Imagine HD",   grok:true, costPerSec:0.07, minDur:1, maxDur:15, res:"720p"},
  {id:"kling-3.0",          label:"Kling 3.0",          perSec:true, upsNA:0.6, upsA:0.9, minDur:3, maxDur:15, res:"720p",  audio:true},
  {id:"kling-3.0-omni",     label:"Kling 3.0 Omni",    perSec:true, upsNA:0.6, upsA:0.8, minDur:3, maxDur:15, res:"720p",  audio:true},
  {id:"kling-3.0-turbo-720p", label:"Kling 3.0 Turbo 720p", perSec:true, upsNA:0.8, upsA:0.8, minDur:3, maxDur:15, res:"720p", audio:true, audioFixed:true},
  {id:"kling-o1",           label:"Kling O1",           perSec:true, upsNA:0.6, upsA:0.9, minDur:3, maxDur:10, res:"720p"},
  {id:"kling-2.6",          label:"Kling 2.6",          p5:1.5, p10:3.0, res:"720p"},
  {id:"kling-2.5-turbo",    label:"Kling 2.5 Turbo",    p5:1.5, p10:3.0, res:"720p"},
  {id:"kling-2.1",          label:"Kling 2.1",          p5:2.0, p10:4.0, res:"720p"},
  {id:"kling-2.0-master",   label:"Kling 2.0 Master",   p5:10.0, p10:20.0, res:"720p"},
  {id:"kling-1.6",          label:"Kling 1.6",          p5:2.0, p10:4.0, res:"720p"},
  {id:"kling-1.5",          label:"Kling 1.5",          p5:2.0, fixedDur:5, res:"720p"},
  // ── Kling Effects pseudo-model ──────────────────────────────────────────
  // Routes to /v1/videos/effects with a chosen effect_scene from the 270-
  // effect catalogue. Per-effect pricing (no per-second formula). Kling's
  // effects pipeline outputs 720p (verified against produced jobs), so the
  // card shows a normal 720p pill rather than a bespoke badge. Surfaces in
  // the picker only when vidInputMode === 'image2video' (effects always
  // take image input).
  {id:"kling-effects",      label:"Kling Effects",      effects:true, res:"720p"},
  // 480p
  {id:"grok-imagine",        label:"Grok Imagine",      grok:true, costPerSec:0.05, minDur:1, maxDur:15, res:"480p"},
  // ── Google Gemini Omni Flash ─────────────────────────────────────────────
  // Google's replacement for Veo 3.1 (the Veo previews shut down
  // 2026-10-22). 3-10s at any whole second, 16:9 / 9:16, audio generated by
  // default. Billed per token and charged from the reported usage; these
  // per-second rates are the pre-submit estimate, measured on real jobs
  // (video tokens/s: 360p ~1.9k, 720p 5,792, 1080p 8,688, 4K 17,376 at
  // $17.50/1M) plus headroom for thinking and reference-image input tokens
  // so the estimate never lands under the charge. Mirrors GOOGLE_VIDEO_COST
  // in katana-backend/src/poller.js.
  {id:"google-omni-flash-4k",      label:"Gemini Omni Flash 4K",    google:true, geminiOmni:true, costPerSec:0.32,  minDur:3, maxDur:10, res:"4K",    audio:true},
  {id:"google-omni-flash-1080p",   label:"Gemini Omni Flash",       google:true, geminiOmni:true, costPerSec:0.16,  minDur:3, maxDur:10, res:"1080p", audio:true},
  {id:"google-omni-flash-720p",    label:"Gemini Omni Flash 720p",  google:true, geminiOmni:true, costPerSec:0.11,  minDur:3, maxDur:10, res:"720p",  audio:true},
  {id:"google-omni-flash-360p",    label:"Gemini Omni Flash 360p",  google:true, geminiOmni:true, costPerSec:0.04,  minDur:3, maxDur:10, res:"360p",  audio:true},
  // ── Google Veo 3.1 family ───────────────────────────────────────────────
  // Per Google's Gemini API pricing (audio is included in the rate -
  // no separate adder). Resolution affects price, especially on Fast
  // (4K 3x 720p) and Lite (1080p 60% > 720p). Veo 3.1 Lite has no 4K.
  // Lite 1080p is locked to 8s by Google.
  // 4K (Standard + Fast - no Lite 4K)
  {id:"google-veo-3.1-4k",         label:"Veo 3.1 4K",         google:true, costPerSec:0.60, minDur:4, maxDur:8, durStep:2, res:"4K",    audio:true},
  {id:"google-veo-3.1-fast-4k",    label:"Veo 3.1 Fast 4K",    google:true, costPerSec:0.30, minDur:4, maxDur:8, durStep:2, res:"4K",    audio:true},
  // 1080p (Standard / Fast / Lite). Lite 1080p is duration-locked to 8s.
  {id:"google-veo-3.1-1080p",      label:"Veo 3.1",            google:true, costPerSec:0.40, minDur:4, maxDur:8, durStep:2, res:"1080p", audio:true},
  {id:"google-veo-3.1-fast-1080p", label:"Veo 3.1 Fast",       google:true, costPerSec:0.12, minDur:4, maxDur:8, durStep:2, res:"1080p", audio:true},
  {id:"google-veo-3.1-lite-1080p", label:"Veo 3.1 Lite",       google:true, costPerSec:0.08, fixedDur:8,                             res:"1080p", audio:true},
  // 720p (Standard / Fast / Lite)
  {id:"google-veo-3.1-720p",       label:"Veo 3.1 720p",       google:true, costPerSec:0.40, minDur:4, maxDur:8, durStep:2, res:"720p",  audio:true},
  {id:"google-veo-3.1-fast-720p",  label:"Veo 3.1 Fast 720p",  google:true, costPerSec:0.10, minDur:4, maxDur:8, durStep:2, res:"720p",  audio:true},
  {id:"google-veo-3.1-lite-720p",  label:"Veo 3.1 Lite 720p",  google:true, costPerSec:0.05, minDur:4, maxDur:8, durStep:2, res:"720p",  audio:true},
];
const isPerSec = (m) => !!m?.perSec; // per-second pricing models (V3, Omni, O1)
const supportsCfg = (id) => (id && id.startsWith('kling-1.')) || id === 'kling-3.0' || id === 'kling-3.0-pro' || id === 'kling-3.0-4k'; // V1.x + V3 image2video
const supportsRatio = () => true; // All models now support aspect ratio selection (crop on submit)
const supportsAudio = (m) => !!m?.audio;
// Models that support an optional end frame (image_tail / image_list end_frame).
// Per Kling Models capability map: V3 (all modes), V3-Omni (all modes), and the
// Pro variants of V2.6 / V2.5-Turbo / V2.1 / V1.6 / V1.5. Masters and O1 do not.
// Google Veo 3.1 Standard + Fast also support a lastFrame, but only at 720p
// (per Vertex AI's first-and-last-frame docs); 1080p / 4K Veo entries reject.
// Veo Lite does not support last frame.
const END_FRAME_MODELS = new Set([
  'kling-3.0', 'kling-3.0-pro', 'kling-3.0-4k',
  'kling-3.0-omni', 'kling-3.0-omni-pro', 'kling-3.0-omni-4k',
  'kling-2.6-pro', 'kling-2.5-turbo-pro', 'kling-2.1-pro',
  'kling-1.6-pro', 'kling-1.5-pro',
  'google-veo-3.1-720p', 'google-veo-3.1-fast-720p',
  // Gemini Omni: start + end frame interpolation at every resolution.
  'google-omni-flash-360p', 'google-omni-flash-720p', 'google-omni-flash-1080p', 'google-omni-flash-4k',
]);
const supportsEndFrame = (id) => END_FRAME_MODELS.has(id);
// End-frame-only is only actually supported on kling-1.5 PRO and kling-1.6 PRO.
// Per the Kling capability map, only those two models have a separate "end
// frame" capability row. Tested empirically: v3 (and likely v2.x pro) reject
// end-only with "image can not be null" regardless of whether the field is
// omitted or sent as an empty string. The general docs' "at least one of image
// or image_tail" wording only applies to those two v1.x pro models.
const END_ONLY_MODELS = new Set(['kling-1.5-pro', 'kling-1.6-pro']);
const supportsEndFrameOnly = (id) => END_ONLY_MODELS.has(id);
// Start frame is required EXCEPT when the model can run end-frame-only
// (kling 1.5/1.6 Pro) or supports text-to-video (kling 3.0 family, etc.) -
// for text-capable models an empty drop becomes a text-only generation
// (with a confirm dialog at submit), so the drop itself is optional.
const requiresStartFrame = (id) => !supportsEndFrameOnly(id) && !supportsText2Video(id);

// Image-gen resolutions ("1k", "2k", "4k") mapped to image-context
// res-keys. ResPill resolves these via RES_COLORS to image-specific
// colors (1K=blue, 2K=purple, 4K=gold) while the displayed label is
// stripped back to "1K"/"2K"/"4K" by resLabel in shared.js. Keeping
// the Img prefix here means image pills don't collide with the video
// "4K" key, which keeps its orange-red.
function imgResLabel(r) {
  if (r === '1k') return 'Img1K';
  if (r === '2k') return 'Img2K';
  if (r === '4k') return 'Img4K';
  return (r || '').toString().toUpperCase();
}

// Compact label for an input mode, used by job cards. Returns
// { short, long } or null if the mode isn't recognized (e.g. very old jobs
// without inputMode set at all).
function modeLabel(inputMode) {
  switch (inputMode) {
    case 'image2video': return { short: 'I → V', long: 'Image → Video' };
    case 'ref2video':   return { short: 'R → V', long: 'References → Video' };
    case 'text2video':  return { short: 'T → V', long: 'Text → Video' };
    case 'ref2image':   return { short: 'R → I', long: 'References → Image' };
    case 'text2image':  return { short: 'T → I', long: 'Text → Image' };
    default: return null;
  }
}

// Color palette for reference images. Each ref tile and its matching
// <<<image_N>>> quick-link pill share the same color so users can visually
// pair them. Deterministic by index (idx % length), so the Nth reference
// always gets the same shade. Alternates turquoise / yellow; cycles after
// 10 (the max ref count Kling Omni supports).
//
// Slot 1 is ALWAYS turquoise, slot 2 is ALWAYS yellow/gold; slots 3-10 cycle
// through a distinct multi-hue range so additional references stay easy to
// tell apart. Only the gold slot is theme-split (dark uses a neon yellow that
// pops on the dark page; light uses a warm golden that reads on the light
// grey-turquoise page). Turquoise and the color range are shared across modes.
const REF_RANGE = [
  '#FF6FB5', // slot 3  - pink           (user-specified)
  '#3D9DFF', // slot 4  - blue           (user-specified)
  '#B26BFF', // slot 5  - purple         (user-specified)
  '#3FD46A', // slot 6  - green          (user-specified)
  '#FF3DCB', // slot 7  - magenta        (user-specified)
  '#7A5CFF', // slot 8  - indigo         (user-specified)
  '#FF8A3D', // slot 9  - orange         (user-specified)
  '#0FA89A', // slot 10 - deep turquoise (assistant pick - 2nd turquoise shade, darker than slot 1)
];
const REF_COLORS_DARK = ['#1FE3C8', '#EEFF00', ...REF_RANGE];   // turquoise / neon yellow
const REF_COLORS_LIGHT = ['#1FE3C8', '#FFD21F', ...REF_RANGE];  // turquoise / warm gold
const refColor = (idx) => {
  const pal = T._mode === 'light' ? REF_COLORS_LIGHT : REF_COLORS_DARK;
  return pal[idx % pal.length];
};

// Reference-image prompt syntax per model. Kling Omni endpoints accept
// <<<image_N>>> (1-indexed); Grok's image-edits endpoint accepts <IMAGE_N>
// (0-indexed). Veo 3.1 Standard + Fast don't define a token syntax of
// their own - the model matches the prompt's natural-language descriptions
// to the supplied ASSET references on its own - but bare "image_N" labels
// in the prompt are useful disambiguators for the user (e.g. "the woman
// from image_1 wearing the dress from image_2"). We surface them through
// the same pill UI as Kling Omni for parity. Other endpoints take refs
// positionally and ignore any in-prompt syntax; refTokenFor returns null
// for those.
const KLING_OMNI_TOKEN_MODELS = new Set([
  'kling-3.0-omni-img', 'kling-image-o1-img',
  'kling-3.0-omni', 'kling-3.0-omni-pro', 'kling-3.0-omni-4k',
  'kling-o1', 'kling-o1-pro',
]);
// Veo 3.1 Standard + Fast (all resolutions). Lite has no reference support
// upstream so it never gets the token UI either.
const VEO_TOKEN_MODELS = new Set([
  'google-veo-3.1-720p',       'google-veo-3.1-1080p',      'google-veo-3.1-4k',
  'google-veo-3.1-fast-720p',  'google-veo-3.1-fast-1080p', 'google-veo-3.1-fast-4k',
]);
// Gemini Omni's docs address reference images as <IMAGE_REF_N> (0-indexed).
const GEMINI_OMNI_MODELS = new Set(['google-omni-flash-360p', 'google-omni-flash-720p', 'google-omni-flash-1080p', 'google-omni-flash-4k']);
function refTokenFor(modelId, idx) {
  if (KLING_OMNI_TOKEN_MODELS.has(modelId)) return `<<<image_${idx+1}>>>`;
  if (GEMINI_OMNI_MODELS.has(modelId)) return `<IMAGE_REF_${idx}>`;
  // Both Grok image variants accept multi-image edits with <IMAGE_N>
  // syntax in the prompt per xAI's image-edits docs.
  if (String(modelId || '').startsWith('grok-imagine-image')) return `<IMAGE_${idx}>`;
  // Veo: bare "image_N" - no wrapping, since Veo treats it as plain
  // English. Matches Kling Omni's 1-indexed convention so the pill UI
  // reads consistently across providers.
  if (VEO_TOKEN_MODELS.has(modelId)) return `image_${idx+1}`;
  return null;
}
function refLabelFor(modelId, idx) {
  if (KLING_OMNI_TOKEN_MODELS.has(modelId)) return `image_${idx+1}`;
  if (GEMINI_OMNI_MODELS.has(modelId)) return `IMAGE_REF_${idx}`;
  if (String(modelId || '').startsWith('grok-imagine-image')) return `IMAGE_${idx}`;
  if (VEO_TOKEN_MODELS.has(modelId)) return `image_${idx+1}`;
  return `${idx+1}`;
}
const supportsRefToken = (id) => refTokenFor(id, 0) !== null;

// Video input-mode capabilities per model. text2video and ref2video are
// optional; image2video is supported by every model in MODELS so it's
// always implicitly true. ref2video also reports the maximum number of
// reference images supported.
const OMNI_VIDEO_MODELS = new Set([
  'kling-3.0-omni','kling-3.0-omni-pro','kling-3.0-omni-4k',
  'kling-o1','kling-o1-pro',
]);
const TEXT2VIDEO_MODELS = new Set([
  ...OMNI_VIDEO_MODELS,
  'kling-1.6','kling-1.6-pro',
  'kling-2.0-master','kling-2.1-master',
  'kling-2.5-turbo','kling-2.5-turbo-pro',
  'kling-2.6','kling-2.6-pro',
  'kling-3.0','kling-3.0-pro','kling-3.0-4k',
  'kling-3.0-turbo-720p','kling-3.0-turbo-1080p',
  // Grok Imagine supports text-to-video, image-to-video, and ref-to-video
  // through /v1/videos/generations. See xAI video docs.
  'grok-imagine','grok-imagine-720p',
  // Gemini Omni: text, image (first / last frame) and reference input.
  'google-omni-flash-360p', 'google-omni-flash-720p', 'google-omni-flash-1080p', 'google-omni-flash-4k',
]);
const supportsText2Video = (id) => TEXT2VIDEO_MODELS.has(id);
// Capability gate used by the model selector to disable rows that don't
// support the currently-selected input mode. image2video is supported by
// every model in MODELS so it's always true.
const modelSupportsInputMode = (id, mode) => {
  // Kling Effects takes image input (single image or dual portraits,
  // depending on the chosen effect scene). It conceptually straddles
  // image2video and ref2video, so it's offered in BOTH sub-tabs with the
  // identical effects UI; only text2video (no image) is unsupported.
  if (id === 'kling-effects') return mode === 'image2video' || mode === 'ref2video';
  if (mode === 'text2video') return supportsText2Video(id);
  if (mode === 'ref2video')  return supportsRef2Video(id);
  return true; // image2video — every model supports it
};
// Non-omni multi-image2video is kling-1.6 family. Grok Imagine accepts a
// `reference_images` array in the same /generations endpoint. Google Veo 3.1
// Standard + Fast accept up to 3 ASSET reference images (Lite does not).
const VEO_REF2VIDEO_MODELS = new Set([
  'google-veo-3.1-720p', 'google-veo-3.1-1080p', 'google-veo-3.1-4k',
  'google-veo-3.1-fast-720p', 'google-veo-3.1-fast-1080p', 'google-veo-3.1-fast-4k',
]);
const supportsRef2Video = (id) =>
  OMNI_VIDEO_MODELS.has(id)
  || id === 'kling-1.6' || id === 'kling-1.6-pro'
  || id === 'grok-imagine' || id === 'grok-imagine-720p'
  || VEO_REF2VIDEO_MODELS.has(id)
  || GEMINI_OMNI_MODELS.has(id);
const refMaxForVideo = (id) => {
  if (OMNI_VIDEO_MODELS.has(id)) return 7;
  if (id === 'grok-imagine' || id === 'grok-imagine-720p') return 5; // xAI doesn't publish a max; mirror their image-edit cap
  if (VEO_REF2VIDEO_MODELS.has(id)) return 3; // Veo 3.1 caps at 3 ASSET refs
  if (GEMINI_OMNI_MODELS.has(id)) return 3;    // no published cap; matches Veo until tested higher
  return 4;
};
// Some upstream APIs cap duration depending on the input mode even when
// the model itself supports longer clips. Grok Imagine's reference-to-video
// path tops out at 10s while its image2video / text2video go to 15s.
// Veo 3.1 pins duration to 8s for 1080p / 4k output (those resolutions are
// 8s-only) and whenever ASSET references or a last-frame image are in use
// (per Google's docs); we mirror that here so the slider / pills collapse
// to the single allowed value rather than letting the user pick something
// that would be silently coerced upstream.
const veoLocksDur8 = (modelId, mode, hasVidRefs, hasEndFrame) => {
  if (!modelId || !modelId.startsWith('google-veo')) return false;
  // 1080p and 4k are 8-second-only on Veo, in every mode.
  if (modelId.includes('1080p') || modelId.includes('4k')) return true;
  if (modelId.includes('lite')) return false; // Lite 720p: no refs / end-frame
  if (mode === 'ref2video' && hasVidRefs) return true;
  if (mode === 'image2video' && hasEndFrame && END_FRAME_MODELS.has(modelId)) return true;
  return false;
};
const effectiveMaxDuration = (modelId, mode, opts = {}) => {
  const m = MODELS.find(x => x.id === modelId);
  const cap = m?.maxDur || 10;
  if ((modelId === 'grok-imagine' || modelId === 'grok-imagine-720p') && mode === 'ref2video') {
    return Math.min(cap, 10);
  }
  if (veoLocksDur8(modelId, mode, opts.hasVidRefs, opts.hasEndFrame)) return 8;
  return cap;
};
const effectiveMinDuration = (modelId, mode, opts = {}) => {
  const m = MODELS.find(x => x.id === modelId);
  if (veoLocksDur8(modelId, mode, opts.hasVidRefs, opts.hasEndFrame)) return 8;
  return m?.minDur ?? 1;
};
const getResolution = (id) => { const m = MODELS.find(x => x.id === id); return m?.res || (id?.includes('-pro') ? '1080p' : '720p'); };
const getDimensions = (modelId, aspectRatio) => {
  const res = getResolution(modelId);
  const base = res === "4K" ? 2160 : res === "1080p" ? 1080 : res === "480p" ? 480 : res === "360p" ? 360 : 720;
  const ratio = aspectRatio && aspectRatio !== "auto" && supportsRatio(modelId) ? aspectRatio : "16:9";
  const [w, h] = ratio.split(":").map(Number);
  if (w > h) return `${base * w / h}x${base}`;
  if (h > w) return `${base}x${base * h / w}`;
  return `${base}x${base}`;
};
const isKlingModel = (id) => !id || id.startsWith('kling-');
const isGrokModel = (id) => id && id.startsWith('grok-');
const isGoogleModel = (id) => id && id.startsWith('google-');
// Estimates round UP (Math.ceil) so the credit count we show pre-submit is
// never less than what the user actually pays. A user who sees "12" and
// gets billed "14" would lose trust; the reverse (estimate slightly high,
// actual slightly lower) is fine.
//
// Grok video bills $0.002 per input image (refs AND the start frame for
// image-to-video). Verified: 15s text2video = 525cr ($1.05), 15s
// image2video = 526cr (+$0.002 for the start frame); 10s/3-ref ref2video
// = 353cr ($0.706 = $0.70 + 3*$0.002).
//
// Conversion goes through integer micro-dollars before dividing by the
// 2000 µ$/credit rate. Otherwise FP slop in expressions like 6*0.07
// (= 0.42000000000000004 in JS) ceils to one credit too high.
function ceilCredits(usd) {
  return Math.ceil(Math.round(usd * 1e6) / 2000);
}
function calcPrice(m, dur, audio, klingPpv, refsCount = 0, hasStartFrame = false) {
  if (!m) return "0";
  if (m.google) {
    // Audio is included in the per-second rate; the audio flag
    // doesn't change the price for any Veo model.
    return ceilCredits(m.costPerSec * dur).toString();
  }
  if (m.grok) {
    const inputImages = (refsCount || 0) + (hasStartFrame ? 1 : 0);
    return ceilCredits(m.costPerSec * dur + 0.002 * inputImages).toString();
  }
  const ppv = klingPpv || 0.14;
  if (m.perSec) return ceilCredits(((audio && m.upsA) ? m.upsA : m.upsNA) * dur * ppv).toString();
  if (audio && m.p5a) return ceilCredits((dur <= 5 ? m.p5a : m.p10a) * ppv).toString();
  const units = dur <= 5 ? (m.p5 ?? m.p10) : (m.p10 ?? m.p5);
  return ceilCredits(units * ppv).toString();
}
function modelPriceRange(m, klingPpv, ctx = {}) {
  // ctx: { mode, hasVidRefs, hasEndFrame } - lets the picker reflect
  // duration locks that the user can't override from this UI state.
  // E.g. Veo 3.1 Standard/Fast in ref2video mode with refs already
  // attached locks dur to 8s, so the price range collapses to a single
  // value rather than the model's intrinsic 4..8s span.
  if (m.google) {
    // Veo: range spans shortest -> longest duration. Audio is included
    // in the per-second rate (no separate adder per Google's pricing).
    if (m.fixedDur) {
      const flat = m.fixedDur * m.costPerSec;
      return [ceilCredits(flat), ceilCredits(flat)];
    }
    if (veoLocksDur8(m.id, ctx.mode, ctx.hasVidRefs, ctx.hasEndFrame)) {
      const flat = 8 * m.costPerSec;
      return [ceilCredits(flat), ceilCredits(flat)];
    }
    const lo = (m.minDur || 4) * m.costPerSec;
    const hi = (m.maxDur || 8) * m.costPerSec;
    return [ceilCredits(lo), ceilCredits(hi)];
  }
  if (m.grok) return [ceilCredits(m.costPerSec*5), ceilCredits(m.costPerSec*15)];
  if (m.effects) {
    // Effects don't use the units * ppv model - each scene has its own
    // flat USD price. Show the catalogue's cheapest -> priciest span.
    return [ceilCredits(KLING_EFFECT_PRICE_MIN_USD), ceilCredits(KLING_EFFECT_PRICE_MAX_USD)];
  }
  const ppv = klingPpv || 0.14;
  if (m.perSec) {
    const upsLo = m.upsNA, upsHi = m.upsA || m.upsNA;
    const lo = m.minDur || 3, hi = m.maxDur || 10;
    return [ceilCredits(upsLo*lo*ppv), ceilCredits(upsHi*hi*ppv)];
  }
  const allUnits = [m.p5, m.p10, m.p5a, m.p10a].filter(v => v != null);
  return [ceilCredits(Math.min(...allUnits)*ppv), ceilCredits(Math.max(...allUnits)*ppv)];
}
const ModelPriceHint = ({ m, klingPpv, ctx }) => {
  const [lo, hi] = modelPriceRange(m, klingPpv, ctx);
  // Collapse to a single number when the model has only one possible price
  // (fixedDur, single-duration p5/p10, or any case where lo === hi). The
  // dash-separated form is misleading when there's no actual range to pick
  // between - e.g. Veo Lite 1080p, or any Veo entry whose duration is
  // locked by the active sub-mode (refs / end-frame).
  if (lo === hi) {
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:2,color:CREDIT,fontSize:9,fontFamily:"monospace"}}>
        <CreditIcon size={9} color={CREDIT}/>{lo.toLocaleString()}
      </span>
    );
  }
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:2,color:CREDIT,fontSize:9,fontFamily:"monospace"}}>
      <CreditIcon size={9} color={CREDIT}/>{lo.toLocaleString()}–<CreditIcon size={9} color={CREDIT}/>{hi.toLocaleString()}
    </span>
  );
};
// Canonical aspect-ratio order used everywhere a ratio picker renders:
//   1) 1:1 first
//   2) portrait orientations next, closest-to-square first (e.g. 4:5
//      before 3:4 before 2:3 before 9:16)
//   3) landscape orientations last, also closest-to-square first (e.g.
//      5:4 before 4:3 before 3:2 before 16:9)
// Accepts either bare strings ("16:9") or objects with a .val field
// (the video tab's RATIOS arrays), so the same helper sorts both.
function sortRatios(ratios) {
  const keyOf = (item) => {
    const s = typeof item === 'string' ? item : (item && item.val) || '';
    const [w, h] = s.split(':').map(Number);
    if (!w || !h) return [3, 0];     // unknown - dump at the end
    const o = w / h;
    if (o === 1) return [0, 0];       // square
    if (o < 1)   return [1, -o];      // portrait, descending o (4:5 < 3:4 < 9:16)
    return       [2, o];              // landscape, ascending o
  };
  return [...ratios].sort((a, b) => {
    const ka = keyOf(a), kb = keyOf(b);
    return ka[0] - kb[0] || ka[1] - kb[1];
  });
}

// V1.x Kling: only natively supported ratios
const KLING_V1_RATIOS = [
  {val:"1:1",  title:"Square 1:1"},
  {val:"9:16", title:"Portrait 9:16"},
  {val:"16:9", title:"Landscape 16:9"},
];
// V2+ Kling (auto + all crop ratios including 4:5, 5:4)
const KLING_RATIOS = [
  {val:"1:1",  title:"Square 1:1"},
  {val:"4:5",  title:"Portrait 4:5"},
  {val:"3:4",  title:"Portrait 3:4"},
  {val:"2:3",  title:"Portrait 2:3"},
  {val:"9:16", title:"Portrait 9:16"},
  {val:"5:4",  title:"Landscape 5:4"},
  {val:"4:3",  title:"Standard 4:3"},
  {val:"3:2",  title:"Photo 3:2"},
  {val:"16:9", title:"Landscape 16:9"},
];
// Grok: no auto, no 4:5/5:4
const GROK_RATIOS = [
  {val:"1:1",  title:"Square 1:1"},
  {val:"3:4",  title:"Portrait 3:4"},
  {val:"2:3",  title:"Portrait 2:3"},
  {val:"9:16", title:"Portrait 9:16"},
  {val:"4:3",  title:"Standard 4:3"},
  {val:"3:2",  title:"Photo 3:2"},
  {val:"16:9", title:"Landscape 16:9"},
];
// Veo 3.1: only 16:9 / 9:16 are supported (offered in every input mode -
// 9:16 is confirmed working in text2video and image2video; references mode
// is enabled for testing too).
const VEO_RATIOS = [
  {val:"16:9", title:"Landscape 16:9"},
  {val:"9:16", title:"Portrait 9:16"},
];
// Kling Effects: we crop the input image(s) ourselves before submitting,
// so every aspect ratio we can render a crop preview for is offered (plus
// Auto, which keeps the original framing). Not limited to the upstream
// endpoint's native ratio set like normal Kling video. Pre-sorted to match
// the manual ordering of the other RATIOS arrays (square, portraits
// closest-to-square first, then landscapes closest-to-square first).
const KLING_EFFECTS_RATIOS = [
  {val:"1:1",  title:"Square 1:1"},
  {val:"4:5",  title:"Portrait 4:5"},
  {val:"3:4",  title:"Portrait 3:4"},
  {val:"2:3",  title:"Portrait 2:3"},
  {val:"9:16", title:"Portrait 9:16"},
  {val:"1:2",  title:"Tall 1:2"},
  {val:"5:4",  title:"Landscape 5:4"},
  {val:"4:3",  title:"Standard 4:3"},
  {val:"3:2",  title:"Photo 3:2"},
  {val:"16:9", title:"Landscape 16:9"},
  {val:"2:1",  title:"Wide 2:1"},
  {val:"21:9", title:"Cinematic 21:9"},
];
const isV1Model = (id) => id && id.startsWith('kling-1.');
function getRatiosForModel(id) {
  let r;
  if (isGrokModel(id))      r = GROK_RATIOS;
  else if (isV1Model(id))   r = KLING_V1_RATIOS;
  else                      r = KLING_RATIOS;
  return sortRatios(r);
}
// Video aspect ratios actually accepted by the upstream endpoint, given
// (model, inputMode). The Omni endpoint and the non-omni text2video /
// multi-image2video endpoints only accept 1:1 / 9:16 / 16:9. The full
// 9-ratio set (with crops like 4:5, 3:2, etc.) is only valid for V2+
// non-omni image2video, where we pre-crop the start frame server-side.
// V1.x uses the same 3-ratio set across all modes (per Kling V1 docs).
function getVideoRatios(modelId, inputMode) {
  if (modelId === 'kling-effects') return KLING_EFFECTS_RATIOS;
  if (isGoogleModel(modelId)) {
    // Veo: only 16:9 / 9:16, offered in every mode.
    return VEO_RATIOS;
  }
  if (isGrokModel(modelId)) return GROK_RATIOS;
  if (isV1Model(modelId))   return KLING_V1_RATIOS;
  // V2+ Kling
  if (OMNI_VIDEO_MODELS.has(modelId)) return KLING_V1_RATIOS;
  if (inputMode === 'image2video') return KLING_RATIOS;
  return KLING_V1_RATIOS;
}
// "Auto" only makes sense for image2video on V2+ non-omni Kling, where the
// backend pre-crops the start frame and Kling derives ratio from the image.
function videoSupportsAutoRatio(modelId, inputMode) {
  if (modelId === 'kling-effects') return true;
  return !!modelId && !isGrokModel(modelId) && !isGoogleModel(modelId) && !isV1Model(modelId)
      && !OMNI_VIDEO_MODELS.has(modelId)
      && inputMode === 'image2video';
}
// Aspect ratio icon shapes, drawn as solid filled rectangles with sharp
// corners. Centered in the 24×24 viewBox; the long side maxes at 22 with
// the short side computed from the actual ratio so neighboring chips stay
// in proportion (e.g. 9:16 reads visibly wider than 1:2, since 9/16 > 1/2).
const RATIO_SHAPES = {
  "1:1":  <rect x="2"  y="2"  width="20" height="20"/>,
  // Portraits — long side = 22, width = 22 * (w/h) for the labeled w:h.
  "4:5":  <rect x="3"  y="1"  width="18" height="22"/>,  // 18/22 ≈ 0.82 (4/5 = 0.8)
  "3:4":  <rect x="4"  y="1"  width="16" height="22"/>,  // 16/22 ≈ 0.73 (3/4 = 0.75)
  "2:3":  <rect x="5"  y="1"  width="14" height="22"/>,  // 14/22 ≈ 0.64 (2/3 = 0.67)
  "9:16": <rect x="6"  y="1"  width="13" height="22"/>,  // 13/22 ≈ 0.59 (9/16 = 0.5625)
  "1:2":  <rect x="6"  y="1"  width="11" height="22"/>,  // 11/22 = 0.5
  // Landscapes — long side = 22.
  "5:4":  <rect x="1"  y="2"  width="22" height="18"/>,
  "4:3":  <rect x="1"  y="3"  width="22" height="16"/>,
  "3:2":  <rect x="1"  y="4"  width="22" height="14"/>,
  "16:9": <rect x="1"  y="5"  width="22" height="13"/>,  // 13/22 ≈ 0.59 (9/16 = 0.5625)
  "2:1":  <rect x="1"  y="6"  width="22" height="11"/>,
  "21:9": <rect x="1"  y="7"  width="22" height="9"/>,   // 9/22 ≈ 0.41 (9/21 = 0.43)
};

// ── Crop preview overlay ────────────────────────────────────────────────────
function CropOverlay({ containerSize, imgNatW, imgNatH, ratio, cropPos, onCropPosChange }) {
  const dragRef = React.useRef(null);
  if (!imgNatW || !imgNatH) return null;

  const cSize = containerSize;
  const imgAR = imgNatW / imgNatH;
  let displayW, displayH;
  if (imgAR >= 1) { displayW = cSize; displayH = cSize / imgAR; }
  else            { displayH = cSize; displayW = cSize * imgAR; }
  const imgLeft = (cSize - displayW) / 2;
  const imgTop  = (cSize - displayH) / 2;

  const inset = 0;
  const borderStyle = {position:'absolute',boxShadow:`inset 0 0 0 1px ${Y}`,pointerEvents:'none',zIndex:2};

  // Auto or no ratio: border around whole image
  if (!ratio || ratio === 'auto') {
    return <div style={{...borderStyle,left:imgLeft+inset,top:imgTop+inset,width:displayW-inset*2,height:displayH-inset*2}}/>;
  }

  const [rw, rh] = ratio.split(':').map(Number);
  const targetAR = rw / rh;
  const srcAR = imgNatW / imgNatH;

  // Same ratio — no crop needed
  if (Math.abs(srcAR - targetAR) < 0.01) {
    return <div style={{...borderStyle,left:imgLeft+inset,top:imgTop+inset,width:displayW-inset*2,height:displayH-inset*2}}/>;
  }

  let cropW, cropH, isHorizontal;
  if (srcAR > targetAR) {
    cropH = displayH - inset*2; cropW = cropH * targetAR; isHorizontal = true;
  } else {
    cropW = displayW - inset*2; cropH = cropW / targetAR; isHorizontal = false;
  }

  const maxOffset = isHorizontal ? (displayW - inset*2) - cropW : (displayH - inset*2) - cropH;
  const cropLeft = imgLeft + inset + (isHorizontal ? maxOffset * cropPos : 0);
  const cropTop  = imgTop + inset + (isHorizontal ? 0 : maxOffset * cropPos);
  const canDrag = maxOffset > 1;

  const startDrag = (clientX, clientY) => {
    dragRef.current = { startX: clientX, startY: clientY, startPos: cropPos };
  };
  const moveDrag = (clientX, clientY) => {
    if (!dragRef.current) return;
    const { startX, startY, startPos } = dragRef.current;
    const delta = isHorizontal ? (clientX - startX) / maxOffset : (clientY - startY) / maxOffset;
    onCropPosChange(Math.max(0, Math.min(1, startPos + delta)));
  };
  const endDrag = () => { dragRef.current = null; };

  const onMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    startDrag(e.clientX, e.clientY);
    const onMove = (ev) => moveDrag(ev.clientX, ev.clientY);
    const onUp = () => { endDrag(); window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const onTouchStart = (e) => {
    e.stopPropagation();
    const t = e.touches[0];
    startDrag(t.clientX, t.clientY);
    const onMove = (ev) => { ev.preventDefault(); moveDrag(ev.touches[0].clientX, ev.touches[0].clientY); };
    const onEnd = () => { endDrag(); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  };

  const dim = 'rgba(0,0,0,0.55)';
  // Four dim rectangles around the crop area (top, bottom, left, right)
  const dimTop    = { position:'absolute', left:0, top:0, width:cSize, height:cropTop, background:dim, zIndex:2, pointerEvents:'none' };
  const dimBottom = { position:'absolute', left:0, top:cropTop+cropH, width:cSize, height:cSize-(cropTop+cropH), background:dim, zIndex:2, pointerEvents:'none' };
  const dimLeft   = { position:'absolute', left:0, top:cropTop, width:cropLeft, height:cropH, background:dim, zIndex:2, pointerEvents:'none' };
  const dimRight  = { position:'absolute', left:cropLeft+cropW, top:cropTop, width:cSize-(cropLeft+cropW), height:cropH, background:dim, zIndex:2, pointerEvents:'none' };

  return (<>
    <div style={dimTop}/><div style={dimBottom}/><div style={dimLeft}/><div style={dimRight}/>
    <div
      onMouseDown={canDrag ? onMouseDown : undefined}
      onTouchStart={canDrag ? onTouchStart : undefined}
      onClick={e => e.stopPropagation()}
      style={{
        position:'absolute', left:cropLeft, top:cropTop, width:cropW, height:cropH,
        boxShadow:`inset 0 0 0 1px ${Y}`,
        zIndex:3, cursor: canDrag ? 'grab' : 'default', touchAction:'none',
      }}
    />
  </>);
}

// Kling rejects reference images whose shortest side is under 300px or whose
// aspect ratio is outside ~1:2.5 - 2.5:1 (surfaces as "Image pixel is invalid"
// or "Image aspect ratio is invalid" in task_status_msg). The Grok image
// endpoints don't enforce the same rules, so we only apply this to Kling.
const KLING_REF_RULES = { minSide: 300, maxAspectMag: 2.5 };

function refRulesFor(modelMeta) {
  if (!modelMeta) return null;
  // Kling flavors: omni, gen, multi  -  all share the same I/O constraints.
  if (modelMeta.flavor === 'omni' || modelMeta.flavor === 'gen' || modelMeta.flavor === 'multi') {
    return KLING_REF_RULES;
  }
  return null;
}

// Kling enforces the same min-side / aspect-ratio rules on start and end
// frames as on reference images ("Image pixel is invalid" / "Image aspect
// ratio is invalid"). Veo and Grok don't, so we only gate Kling video models.
function klingFrameRulesFor(modelId) {
  return modelId && modelId.startsWith('kling-') ? KLING_REF_RULES : null;
}

function loadImageDims(blobUrl) {
  return new Promise((resolve) => {
    if (!blobUrl) { resolve(null); return; }
    const im = new Image();
    im.onload  = () => resolve({ w: im.naturalWidth || 0, h: im.naturalHeight || 0 });
    im.onerror = () => resolve(null);
    im.src = blobUrl;
  });
}

function validateRefDims(dims, rules) {
  if (!rules || !dims || !dims.w || !dims.h) return null;
  const minSide = Math.min(dims.w, dims.h);
  if (minSide < rules.minSide) {
    return `Too small (${dims.w}x${dims.h}); each side must be at least ${rules.minSide}px.`;
  }
  const mag = Math.max(dims.w, dims.h) / Math.max(1, Math.min(dims.w, dims.h));
  if (mag > rules.maxAspectMag) {
    return `Aspect ratio too extreme (${dims.w}x${dims.h}); must be within 1:${rules.maxAspectMag} - ${rules.maxAspectMag}:1.`;
  }
  return null;
}

// Find the closest supported Grok ratio for a given image aspect ratio
// Uses log-scale comparison so portrait and landscape distances are symmetric
function closestRatio(w, h) {
  if (!w || !h) return "16:9";
  const srcAR = w / h;
  const ratios = [
    { val:"1:1",  ar:1 },    { val:"4:5",  ar:4/5 },
    { val:"3:4",  ar:3/4 },  { val:"2:3",  ar:2/3 },
    { val:"9:16", ar:9/16 }, { val:"5:4",  ar:5/4 },
    { val:"4:3",  ar:4/3 },  { val:"3:2",  ar:3/2 },
    { val:"16:9", ar:16/9 },
  ];
  const logSrc = Math.log(srcAR);
  let best = ratios[0], bestDiff = Infinity;
  for (const r of ratios) {
    const diff = Math.abs(logSrc - Math.log(r.ar));
    if (diff < bestDiff) { bestDiff = diff; best = r; }
  }
  return best.val;
}

const IconSave = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const IconLoad = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>;

export default function GenerateView({ onDone, onSaveLoad }) {
  const narrow = useNarrow(800);
  const wide = !useNarrow(960);
  // Load / Save labels collapse first since they're the least essential —
  // hits before the top-level tab labels start crowding.
  const compactTopBar = useNarrow(720);
  // Phone-width Generate rows (<=500px) are too crowded (Clear + balance +
  // cost + Generate, sometimes Buy Credits) to fit the Clear text label, so
  // it goes icon-only there; tablets / landscape / desktop keep the label.
  const crowdedRow = useNarrow(500);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollToTop = useScrollToTop();
  // creditBalance is what a new generation can spend: the balance minus
  // credits on hold for generations still in progress (the backend refuses
  // a submit that doesn't fit).
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditOnHold, setCreditOnHold] = useState(0);
  const [studioKlingPpv, setStudioKlingPpv] = useState(0.14);
  const [studioKlingImagePpv, setStudioKlingImagePpv] = useState(0.0035);
  const [maxConcurrent, setMaxConcurrent] = useState(2);
  // Server-driven Model Control: per-platform lists of disabled and
  // recommended model IDs. Empty arrays fall back to the frontend defaults.
  const [studioVideoDisabled,    setStudioVideoDisabled]    = useState([]);
  const [studioVideoRecommended, setStudioVideoRecommended] = useState([]);
  const [studioImageDisabled,    setStudioImageDisabled]    = useState([]);
  const [studioImageRecommended, setStudioImageRecommended] = useState([]);
  const [activeJobCount, setActiveJobCount] = useState(0);
  const [jobsReady, setJobsReady] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  // "Always preload last used settings" account pref (default ON = the app's
  // original behavior). prefsFetched gates the auto-restore until getSettings
  // resolves, so we know whether to preload or open blank.
  const [alwaysPreload, setAlwaysPreload] = useState(true);
  const [prefsFetched,  setPrefsFetched]  = useState(false);
  const [img,       setImg]       = useState(null);   // blob URL for display
  const [file,      setFile]      = useState(null);   // File object (null if using existing gcsPath)
  const [gcsPath,   setGcsPath]   = useState(null);   // existing GCS path (when reusing an image)
  // Optional end frame (image_tail) — only used by models that support it
  const [endImg,     setEndImg]     = useState(null);
  const [endFile,    setEndFile]    = useState(null);
  const [endGcsPath, setEndGcsPath] = useState(null);
  const [endImgNatW, setEndImgNatW] = useState(0);
  const [endImgNatH, setEndImgNatH] = useState(0);
  const [endCropPos, setEndCropPos] = useState(0.5);
  const [endDrag,    setEndDrag]    = useState(false);
  const [prompt,    setPrompt]    = useState("");
  const [negPrompt, setNegPrompt] = useState("");
  const [dur,       setDur]       = useState(5);
  const [model,     setModel]     = useState("kling-3.0-omni");
  const [ratio,     setRatio]     = useState("1:1");
  const [audio,     setAudio]     = useState(false);
  const [cfgScale,  setCfgScale]  = useState(0.5);
  const [imgNatW,   setImgNatW]   = useState(0);
  const [imgNatH,   setImgNatH]   = useState(0);
  const [cropPos,   setCropPos]   = useState(0.5);
  const [drag,      setDrag]      = useState(false);
  const [stage,     setStage]     = useState("idle");
  const [modelOpen, setModelOpen] = useState(false);
  const [modelDropTop, setModelDropTop] = useState(0);
  // Kling Effects state. klingEffect is the chosen effect_scene string (e.g.
  // 'color_mixing'); null when no effect picked yet. effectsPickerOpen
  // controls the drawer. Only meaningful when model === 'kling-effects'.
  const [klingEffect, setKlingEffect] = useState(null);
  const [effectsPickerOpen, setEffectsPickerOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState('start'); // 'start' | 'end'
  const [newestJobId, setNewestJobId] = useState(null);
  const [presetPanel, setPresetPanel] = useState(null); // null | "save" | "load"
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  // Mirror state for image-mode presets (separate dropdown so the two
  // tabs don't share each other's saved settings).
  const [imgPresetPanel, setImgPresetPanel] = useState(null);
  const [imgPresets, setImgPresets] = useState([]);
  const [imgPresetName, setImgPresetName] = useState("");
  const ref = useRef();
  const endRef = useRef();
  const goRef = useRef();
  const modelSelRef = useRef();
  const promptTaRef = useRef(null);
  const imgPromptTaRef = useRef(null);
  // Track rendered widths of the start/end frame boxes so the CropOverlay can
  // size to whatever the responsive layout resolved to (boxes are square via
  // aspect-ratio, so width is also their height).
  const [startFrameRef, startFrameSize] = useElementSize();
  const [endFrameRef,   endFrameSize]   = useElementSize();

  // ─── Image generation mode (Phase 2) ────────────────────────────────────────
  // Mode tabs at the top of the page. The Image mode renders an entirely
  // separate panel below; the existing video state above is untouched so
  // switching modes is non-destructive.
  // Generate tab + sub-tab are URL-driven so they can be deep-linked.
  // URL shapes:
  //   /generate                       -> redirected to last-used top-level
  //                                      mode (or /generate/video by default)
  //   /generate/video                 -> redirected to /generate/video/img2vid
  //   /generate/video/img2vid         -> mode=video, vidInputMode=image2video
  //   /generate/video/ref2vid         -> mode=video, vidInputMode=ref2video
  //   /generate/video/txt2vid         -> mode=video, vidInputMode=text2video
  //   /generate/image                 -> mode=image  (single page; raster
  //                                      and vector models share one picker)
  //   /generate/image/raster|vector   -> legacy URLs from the prior sub-tab
  //                                      design; collapsed to /generate/image
  //
  // Tab clicks call setMode / setVidInputMode, which navigate() to the
  // corresponding URL; mode + vidInputMode are derived from the path so
  // the URL is always authoritative.
  const _segs = location.pathname.split('/').filter(Boolean);
  const mode = _segs[1] === 'image' ? 'image' : 'video';
  // URL token <-> internal vidInputMode value. Keep tokens short
  // (img2vid/ref2vid/txt2vid) so URLs are scannable; map to the longer
  // internal values that match the rest of the codebase.
  const VID_URL_TO_MODE = { img2vid: 'image2video', ref2vid: 'ref2video', txt2vid: 'text2video' };
  const VID_MODE_TO_URL = { image2video: 'img2vid', ref2video: 'ref2vid', text2video: 'txt2vid' };
  const vidInputMode = VID_URL_TO_MODE[_segs[2]] || 'ref2video';
  const setMode = useCallback((next) => {
    if (next === 'image') {
      navigate(`/generate/image`);
    } else {
      let sub = 'ref2vid';
      try {
        const stored = localStorage.getItem('katana.generateVideoSubTab');
        if (stored && VID_URL_TO_MODE[stored]) sub = stored;
      } catch {}
      if (_segs[1] === 'video' && VID_URL_TO_MODE[_segs[2]]) sub = _segs[2];
      navigate(`/generate/video/${sub}`);
    }
    try { localStorage.setItem('katana.generateMode', next); } catch {}
  }, [navigate, _segs]);
  const setVidInputMode = useCallback((next) => {
    const token = VID_MODE_TO_URL[next] || 'ref2vid';
    try { localStorage.setItem('katana.generateVideoSubTab', token); } catch {}
    navigate(`/generate/video/${token}`);
  }, [navigate]);

  // Mount + URL-change effect: normalize bare paths to canonical URLs
  // and collapse the legacy /generate/image/raster | /generate/image/vector
  // sub-paths to plain /generate/image now that raster + vector models
  // live in one picker.
  useEffect(() => {
    if (_segs[0] !== 'generate') return;
    if (_segs.length === 1) {
      let last = 'video';
      try { if (localStorage.getItem('katana.generateMode') === 'image') last = 'image'; } catch {}
      let target;
      if (last === 'image') {
        target = `/generate/image`;
      } else {
        let sub = 'ref2vid';
        try {
          const stored = localStorage.getItem('katana.generateVideoSubTab');
          if (stored && VID_URL_TO_MODE[stored]) sub = stored;
        } catch {}
        target = `/generate/video/${sub}`;
      }
      navigate(target, { replace: true });
    } else if (_segs[1] === 'image' && (_segs[2] === 'raster' || _segs[2] === 'vector')) {
      // Legacy sub-tab URL: collapse to /generate/image so the address
      // bar matches the new single-picker UX.
      navigate(`/generate/image`, { replace: true });
    } else if (_segs[1] === 'video' && !_segs[2]) {
      let sub = 'ref2vid';
      try {
        const stored = localStorage.getItem('katana.generateVideoSubTab');
        if (stored && VID_URL_TO_MODE[stored]) sub = stored;
      } catch {}
      navigate(`/generate/video/${sub}`, { replace: true });
    } else if (_segs[1] === 'image' || _segs[1] === 'video') {
      // Sync localStorage to whatever URL the user is currently on so
      // clicking the side-nav "Generate" later (which goes to bare
      // /generate) returns them to the right top-level mode.
      try {
        localStorage.setItem('katana.generateMode', _segs[1]);
        if (_segs[1] === 'video' && _segs[2] && VID_URL_TO_MODE[_segs[2]]) {
          localStorage.setItem('katana.generateVideoSubTab', _segs[2]);
        }
      } catch {}
    }
  }, [location.pathname]); // eslint-disable-line

  // First-time defaults for users with no saved generate settings. The
  // useEffect below that restores from `generateImageSettings` overrides
  // these for returning users.
  const [imgModel, setImgModel] = useState('google-nano-banana-2.1');
  const [imgPrompt, setImgPrompt] = useState('');
  const [imgNegPrompt, setImgNegPrompt] = useState('');
  const [imgRatio, setImgRatio] = useState('1:1');
  const [imgResolution, setImgResolution] = useState('2k');
  const [imgN, setImgN] = useState(1);
  const [imgRefs, setImgRefs] = useState([]); // [{ blobUrl, gcsPath, file, dims }]
  // Count of references currently being rehydrated from a previous job /
  // preset. While > 0, the form's references grid shows that many skeleton
  // tiles (with a spinner) so refs don't pop in unannounced when the blob
  // fetches resolve.
  const [imgRefsRestoring, setImgRefsRestoring] = useState(0);
  const [imgRefPickerOpen, setImgRefPickerOpen] = useState(false);
  const [imgRefDrag, setImgRefDrag] = useState(false); // drag-over highlight for the image refs grid
  const [imgStage, setImgStage] = useState('idle');
  const [imgNewestJobId, setImgNewestJobId] = useState(null);
  const [imgRefreshKey, setImgRefreshKey] = useState(0);
  const [imgModelOpen, setImgModelOpen] = useState(false);
  const [imgSettingsLoaded, setImgSettingsLoaded] = useState(false);
  // Optional generation knobs (gated by capability flags on the selected
  // IMAGE_MODELS entry):
  //   imgRaw              - bool toggle. FLUX 1.1 Ultra "raw" mode produces
  //                         less-processed, more natural-looking outputs.
  //   imgImagePromptStrength - 0..1. FLUX 1.1 Ultra remix blend factor.
  //   imgSeed             - string (parsed to int server-side). For
  //                         reproducibility across BFL + Imagen models.
  const [imgRaw, setImgRaw] = useState(false);
  const [imgImagePromptStrength, setImgImagePromptStrength] = useState(0.5);
  const [imgSeed, setImgSeed] = useState('');
  // 'Latest' routes to BFL's `-preview` endpoint variant (e.g.
  // flux-2-pro-preview); 'Stable' uses the fixed snapshot endpoint.
  // Default is Stable (false) so outputs are reproducible across time.
  const [imgUseLatest, setImgUseLatest] = useState(false);

  // Vectorize-specific state. Only relevant when imgModel ===
  // 'recraft-vectorize'; the UI swaps prompt + refs for a single-image
  // drop zone plus SVG-control knobs.
  //   vectorizeSrc      - object URL for inline display (optional)
  //   vectorizeGcsPath  - the source image's GCS path for submission
  //   vectorizeFile     - File object for fresh uploads (cleared after upload)
  //   vectorizeDrag     - true while a drag is hovering over the drop zone
  //   recraftSvgCompression - bool toggle, defaults off
  //   recraftMaxNumShapes   - integer 1..100; 100 means "no limit" (sent
  //                           as null to the backend). Slider default is 100.
  const [vectorizeSrc,         setVectorizeSrc]         = useState(null);
  const [vectorizeGcsPath,     setVectorizeGcsPath]     = useState(null);
  const [vectorizeFile,        setVectorizeFile]        = useState(null);
  const [vectorizeDrag,        setVectorizeDrag]        = useState(false);
  // Inline error string for the vectorize source, mirroring the
  // startFrameError pattern Kling uses: when set, the source-image zone
  // gets a red border + warning triangle and an explainer pill renders
  // below. The image still loads into the preview so the user can see
  // what they picked, but submit is blocked until vectorizeError clears.
  const [vectorizeError,       setVectorizeError]       = useState(null);
  const [recraftSvgCompression,setRecraftSvgCompression]= useState(false);
  const [recraftMaxNumShapes,  setRecraftMaxNumShapes]  = useState(100);
  // V3 Vector Edit (imageToImage) strength: 0 = nearly identical to source,
  // 1 = minimal similarity. 0.4 is a moderate edit and a sensible default.
  const [recraftStrength,      setRecraftStrength]      = useState(0.4);

  // Validate Recraft's Vectorize constraints against any Blob-or-File.
  // Returns null on success or an error string on rejection. SVGs aren't
  // accepted by the upstream endpoint, but the callers below rasterize
  // them client-side to PNG before calling this validator, so the SVG
  // case never lands here in practice.
  //   - PNG / JPG / WEBP only
  //   - <=5MB byte size
  //   - 256-4096px on each side, <=16MP total
  // Optional `nameHint` lets file uploads use the filename ext when MIME
  // is missing (e.g. on some HEIC drops); picker blobs have a real
  // content-type so they ignore it.
  const _validateVectorizeBlob = useCallback(async (blob, nameHint) => {
    const ct = String(blob.type || '').toLowerCase();
    const okType = ct.startsWith('image/') || (nameHint && /\.(png|jpe?g|webp|svg)$/i.test(nameHint));
    if (!okType) return 'Source image must be PNG, JPG, WEBP, or SVG.';
    if (ct.includes('svg')) return 'Source must be a raster image (PNG/JPG/WEBP), not an SVG.';
    if (blob.size > 5 * 1024 * 1024) {
      return `Source image must be under 5MB (this one is ${(blob.size/1024/1024).toFixed(1)}MB).`;
    }
    const probeUrl = URL.createObjectURL(blob);
    try {
      const dims = await new Promise((resolve) => {
        const probe = new Image();
        probe.onload  = () => resolve({ w: probe.naturalWidth, h: probe.naturalHeight });
        probe.onerror = () => resolve(null);
        probe.src = probeUrl;
      });
      if (!dims || !dims.w || !dims.h) return 'Could not read image dimensions. Try a different file.';
      const mp = (dims.w * dims.h) / 1e6;
      if (mp > 16) return `Image is too large (${mp.toFixed(1)}MP at ${dims.w}×${dims.h}). Recraft caps Vectorize at 16MP.`;
      if (Math.max(dims.w, dims.h) > 4096) return `Image dimension too large (${dims.w}×${dims.h}). Max 4096px on each side.`;
      if (Math.min(dims.w, dims.h) < 256) return `Image dimension too small (${dims.w}×${dims.h}). Min 256px on each side.`;
      return null;
    } finally {
      URL.revokeObjectURL(probeUrl);
    }
  }, []);

  // Validate + commit a Vectorize source file. SVG drops are auto-
  // rasterized to PNG client-side (preserving transparency) since
  // Recraft's vectorize / imageToImage endpoints both expect raster
  // input. Loads the resulting blob into the preview regardless of
  // validation outcome, then either clears vectorizeError on pass or
  // sets it on fail.
  const handleVectorizeFile = useCallback(async (file) => {
    if (!file) return;
    let blob = file;
    let displayName = file.name || 'input.png';
    try {
      if (await isSvgBlob(file)) {
        const png = await rasterizeSvgBlobToPng(file);
        // Wrap in a File so api.uploadImage downstream sees a name.
        blob = new File([png], displayName.replace(/\.svg$/i, '.png'), { type: 'image/png' });
        displayName = blob.name;
      }
    } catch {
      setVectorizeError('Could not rasterize the SVG. Try a different file.');
      return;
    }
    const previewUrl = URL.createObjectURL(blob);
    setVectorizeSrc(prev => { try { if (prev) URL.revokeObjectURL(prev); } catch {} return previewUrl; });
    setVectorizeFile(blob);
    setVectorizeGcsPath(null);
    const err = await _validateVectorizeBlob(blob, displayName);
    setVectorizeError(err);
  }, [_validateVectorizeBlob]);

  // Video input-mode sub-tabs (Phase 2b): image-to-video (default), ref-to-video,
  // text-to-video. Now URL-driven via the routing block above; vidInputMode
  // and setVidInputMode are derived from location.pathname there. Models
  // that don't support a given mode still hide the tab in the picker.
  const [vidRefs, setVidRefs] = useState([]); // [{ blobUrl, gcsPath, file }]
  // Same skeleton-counter pattern as imgRefsRestoring, but for the video
  // panel's references grid.
  const [vidRefsRestoring, setVidRefsRestoring] = useState(0);
  const [vidRefPickerOpen, setVidRefPickerOpen] = useState(false);
  const [vidRefDrag, setVidRefDrag] = useState(false); // drag-over highlight for the video refs grid

  // Reset crop position when aspect ratio changes
  useEffect(() => { setCropPos(0.5); setEndCropPos(0.5); }, [ratio]);

  // Read end-frame natural dimensions
  useEffect(() => {
    if (!endImg) { setEndImgNatW(0); setEndImgNatH(0); return; }
    const el = new Image();
    el.onload = () => { setEndImgNatW(el.naturalWidth); setEndImgNatH(el.naturalHeight); };
    el.src = endImg;
  }, [endImg]);

  // Reliably read image natural dimensions whenever image source changes
  useEffect(() => {
    if (!img) { setImgNatW(0); setImgNatH(0); return; }
    const el = new Image();
    el.onload = () => { setImgNatW(el.naturalWidth); setImgNatH(el.naturalHeight); };
    el.src = img;
  }, [img]);

  const sel   = MODELS.find(m => m.id === model);
  // Kling rejects submissions whose start/end frames are too small or whose
  // aspect ratio is outside ~1:2.5 - 2.5:1. Surfacing that pre-submit (instead
  // of letting it come back as an opaque "Image pixel is invalid" task error)
  // saves the user a round-trip and a stuck job slot.
  const klingFrameRules = klingFrameRulesFor(model);
  const startFrameError = (img && imgNatW && imgNatH)
    ? validateRefDims({ w: imgNatW, h: imgNatH }, klingFrameRules)
    : null;
  const endFrameError = (endImg && endImgNatW && endImgNatH)
    ? validateRefDims({ w: endImgNatW, h: endImgNatH }, klingFrameRules)
    : null;
  const framesHaveError = !!(startFrameError || endFrameError);
  // The same Kling min-side / aspect rules apply to ref2video reference
  // images (the upstream API rejects out-of-spec refs the same way it
  // rejects out-of-spec frames). Per-ref errors drive the red-triangle
  // indicator on each tile; the aggregate gates goVideo against submit.
  const vidRefErrors = vidRefs.map(r => validateRefDims(r.dims, klingFrameRules));
  const vidRefsHaveError = vidRefErrors.some(Boolean);
  // Credits on hold still count here so prices keep showing while a
  // generation runs; cannotAfford below uses only what's spendable.
  const hasCredits = creditBalance + creditOnHold > 0;
  const noCredits = !hasCredits;
  const noPrompt = !prompt || !prompt.trim();
  const modelAvailable = sel && hasCredits;
  // Kling Effects pseudo-model: cost comes from the picked effect's
  // per-scene price, not from the model + duration formula. When no
  // effect is picked yet the price is "0" (the Generate button is
  // disabled separately).
  const isEffectsModel = model === 'kling-effects';
  const klingEffectObj = isEffectsModel ? getKlingEffect(klingEffect) : null;
  const price = !modelAvailable
    ? "0"
    : isEffectsModel
      ? (klingEffectObj ? ceilCredits(klingEffectObj.priceUsd).toString() : "0")
      : calcPrice(sel, dur, audio, studioKlingPpv, vidRefs.length, !!img);
  const v3sel = isPerSec(sel);
  const busy  = stage === "up" || stage === "sub";
  const atMaxJobs = activeJobCount >= maxConcurrent;
  const hasImage = !!img || (supportsEndFrame(model) && !!endImg && supportsEndFrameOnly(model));
  // For dual-image effects (kiss_pro etc.) the user needs BOTH images set
  // before Generate is enabled. Single-image effects just need img.
  const effectInputsReady = !isEffectsModel
    || !klingEffectObj
    || (klingEffectObj.type === 'dual' ? (!!img && !!endImg) : !!img);
  // btnBg/btnCol are no longer mode-aware here; the actual disabled check
  // (which IS sub-mode aware) lives where the button is rendered. We just
  // express the "ready" colors here.
  const iconC = T.text3;

  const { notify } = useNotify();
  const confirm = useConfirm();
  const setErrMsg = (msg) => msg ? notify("error", msg, 6000) : null;
  const setHeaderRight = useHeaderRight();
  // Subscribe to theme so the header-right registration re-fires on toggle.
  // Without this, T.* values captured into the JSX go stale until the next
  // dep change (or a page refresh).
  const { mode: themeMode } = useTheme();

  // cannotAfford gates the "Buy Credits" call-to-action on the generate
  // button row (the balance lives there now, not in the header).
  const cannotAfford = creditBalance <= 0 || (price && parseInt(price) > creditBalance);

  // Check if user has API keys and credits
  useEffect(() => {
    api.getSettings()
      .then(data => {
        setCreditBalance(data.creditAvailable ?? data.creditBalance ?? 0);
        setCreditOnHold(data.creditOnHold ?? 0);
        setStudioKlingPpv(data.studioKlingPointValue ?? 0.14);
        setStudioKlingImagePpv(data.studioKlingImagePointValue ?? 0.0035);
        setMaxConcurrent(data.maxConcurrentJobs ?? 2);
        if (Array.isArray(data.videoModelDisabled))    setStudioVideoDisabled(data.videoModelDisabled);
        if (Array.isArray(data.videoModelRecommended)) setStudioVideoRecommended(data.videoModelRecommended);
        if (Array.isArray(data.imageModelDisabled))    setStudioImageDisabled(data.imageModelDisabled);
        if (Array.isArray(data.imageModelRecommended)) setStudioImageRecommended(data.imageModelRecommended);
        setAlwaysPreload(data.alwaysPreloadLastUsedSettings !== false);
        setPrefsFetched(true);
      })
      .catch(() => { setPrefsFetched(true); });
  }, []);

  // Eagerly hydrate the OTHER panel's "previous job settings" alongside the
  // visible one. Without this, a user who left the app on Image mode (mode
  // is persisted in localStorage) would see an empty Video form when they
  // switch tabs - because the video <RecentJobs/> never mounted, so its
  // onReady never fired. We pull the most recent job for each output type
  // up-front and run it through the existing handlers; their internal
  // settingsLoaded / imgSettingsLoaded guards make the later RecentJobs
  // onReady callback a no-op once we've already restored.
  useEffect(() => {
    if (!prefsFetched) return;  // wait until getSettings tells us the pref
    let cancelled = false;
    if (!alwaysPreload) {
      // Pref is OFF: open the Generate page blank for BOTH tabs. Reveal the
      // panels without restoring the most-recent job (Reuse and saved presets
      // still work - those are explicit user actions, not this auto-restore).
      // All the form's useState defaults stand in for "blank".
      setJobsReady(true);
      setSettingsLoaded(true);
      setImgSettingsLoaded(true);
      return;
    }
    api.listJobs(1, undefined, 'video').then(({ jobs = [] } = {}) => {
      if (!cancelled) handleJobsReady(jobs);
    }).catch(() => { if (!cancelled) handleJobsReady([]); });
    api.listJobs(1, undefined, 'image').then(({ jobs = [] } = {}) => {
      if (!cancelled) handleImageJobsReady(jobs);
    }).catch(() => { if (!cancelled) handleImageJobsReady([]); });
    return () => { cancelled = true; };
  }, [prefsFetched]); // eslint-disable-line

  // Clear the CURRENT tab's generation inputs: input images, prompt, negative
  // prompt, seed, and (video, where CFG exists) reset CFG to its 0.5 default.
  // Model / aspect ratio / duration / resolution / count are left untouched.
  const clearForm = useCallback(() => {
    if (mode === 'image') {
      setImgPrompt('');
      setImgNegPrompt('');
      setImgSeed('');
      setImgRefs([]);
      setImgRefsRestoring(0);
      // Vectorize / V3 Vector Edit source image
      setVectorizeSrc(prev => { try { if (prev) URL.revokeObjectURL(prev); } catch {} return null; });
      setVectorizeFile(null);
      setVectorizeGcsPath(null);
      setVectorizeError(null);
    } else {
      setPrompt('');
      setNegPrompt('');
      setCfgScale(0.5);
      // start frame
      setImg(null); setFile(null); setGcsPath(null);
      setImgNatW(0); setImgNatH(0); setCropPos(0.5);
      // end frame
      setEndImg(null); setEndFile(null); setEndGcsPath(null);
      setEndImgNatW(0); setEndImgNatH(0); setEndCropPos(0.5);
      // reference images
      setVidRefs([]); setVidRefsRestoring(0);
    }
  }, [mode]); // eslint-disable-line

  // Compact Clear button shared by both Generate rows. Icon + label, dropping
  // to icon-only on phone-width rows (crowdedRow) so it fits the mobile row.
  // Sits at the far left of the row (marginRight:auto pushes balance, cost
  // and Generate to the right).
  const renderClearBtn = () => (
    <button onClick={clearForm} title="Clear inputs"
      style={{marginRight:'auto',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:5,height:narrow?30:34,padding:narrow?'0 8px':'0 11px',boxSizing:'border-box',borderRadius:999,background:'transparent',border:'1px solid #FF6FB5',color:'#FF6FB5',cursor:'pointer',flexShrink:0,transition:'all 0.15s'}}
      onMouseEnter={narrow ? undefined : e=>{e.currentTarget.style.background='#FF6FB5';e.currentTarget.style.color=T.dark;}}
      onMouseLeave={narrow ? undefined : e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='#FF6FB5';}}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/><path d="M22 21H7"/><path d="m5 11 9 9"/></svg>
      {!crowdedRow && <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:700,letterSpacing:0.5,textTransform:'uppercase',position:'relative',top:1}}>Clear</span>}
    </button>
  );

  // Refresh balances after a job completes (credits charged, API balance changed)
  const refreshBalances = useCallback(() => {
    api.getSettings().then(data => {
      setCreditBalance(data.creditAvailable ?? data.creditBalance ?? 0);
      setCreditOnHold(data.creditOnHold ?? 0);
      setStudioKlingPpv(data.studioKlingPointValue ?? 0.14);
      setStudioKlingImagePpv(data.studioKlingImagePointValue ?? 0.0035);
    }).catch(() => {});
  }, []);
  // ...and after each submit, which puts the new job's cost on hold.
  useEffect(() => {
    if (refreshKey || imgRefreshKey) refreshBalances();
  }, [refreshKey, imgRefreshKey, refreshBalances]);

  // Called by RecentJobs when jobs finish loading — populate from most recent job.
  // Holds settingsLoaded false until every async hydrate (ref images, source
  // frame, end frame) resolves, so the PageLoader keeps the panel hidden
  // until the form is fully rehydrated rather than flashing empty boxes.
  const handleJobsReady = useCallback((jobs) => {
    setJobsReady(true);
    if (settingsLoaded) return;
    // If a job is being reused (Library Reuse navigates here, or the
    // in-page job-card Reuse), the reuse handler has already populated
    // the form - don't clobber it with the most-recent-job auto-restore.
    // Just mark settings loaded so the panel reveals.
    if (reuseConsumedRef.current) { setSettingsLoaded(true); return; }
    // Respect the "always preload last used settings" pref. Wait until it's
    // known (prefsFetched) before deciding; if off, open blank (just reveal).
    // This gates the RecentJobs onReady path too, not only the eager effect.
    if (!prefsFetched) return;
    if (!alwaysPreload) { setSettingsLoaded(true); return; }
    if (!jobs || jobs.length === 0) { setSettingsLoaded(true); return; }
    const job = jobs[0]; // most recent
    if (job.prompt !== undefined)         setPrompt(job.prompt || "");
    if (job.negativePrompt !== undefined) setNegPrompt(job.negativePrompt || "");
    if (job.model && MODELS.find(m => m.id === job.model)) setModel(job.model);
    if (job.duration) {
      // Clamp against the loaded model's actual max (e.g. kling-o1-pro
      // is 10s) so a stale duration from a previous submission doesn't
      // exceed the new model's allowed range.
      const targetModel = (job.model && MODELS.find(m => m.id === job.model)) || sel;
      const cap = targetModel?.maxDur ?? 15;
      const lo  = targetModel?.minDur ?? 1;
      setDur(Math.max(lo, Math.min(cap, Number(job.duration))));
    }
    if (job.aspectRatio) {
      const isGrok = job.model && job.model.startsWith('grok-');
      if (isGrok && job.aspectRatio === 'auto' && job.imageWidth && job.imageHeight) {
        setRatio(closestRatio(job.imageWidth, job.imageHeight));
      } else if (isGrok && job.aspectRatio === 'auto') {
        setRatio("16:9");
      } else {
        setRatio(job.aspectRatio);
      }
    }
    if (job.audio !== undefined)          setAudio(!!job.audio);
    if (job.cfgScale !== undefined)       setCfgScale(job.cfgScale);
    if (job.model === 'kling-effects' && job.klingEffectScene) {
      setKlingEffect(job.klingEffectScene);
    }
    if (job.inputMode) {
      // NEVER call setVidInputMode here - it navigates and would clobber
      // whatever sub-tab URL the user is currently on. Common bug shape:
      // user is on /generate/video/ref2vid, page reloads, the most
      // recent video job is an image2video one, and this auto-restore
      // navigates them to /generate/video/img2vid. The URL is always
      // the source of truth for vidInputMode; auto-restore only writes
      // localStorage so a later bare /generate/video remembers the
      // last-used sub-tab.
      const token = VID_MODE_TO_URL[job.inputMode];
      if (token) try { localStorage.setItem('katana.generateVideoSubTab', token); } catch {}
    }

    const hydrators = [];
    if (Array.isArray(job.referenceImageGcsPaths) && job.referenceImageGcsPaths.length > 0) {
      // Prime the skeleton counter immediately so the references grid
      // shows that many spinner tiles before the blob fetches start.
      setVidRefs([]);
      setVidRefsRestoring(job.referenceImageGcsPaths.length);
      hydrators.push((async () => {
        try {
          const items = await restoreRefsFromGcsPaths(job.referenceImageGcsPaths);
          setVidRefs(items);
        } catch {}
        setVidRefsRestoring(0);
      })());
    }
    if (job.imageGcsPath) {
      setGcsPath(job.imageGcsPath);
      const imgUrl = api.getImageUrl(job.id);
      hydrators.push((async () => {
        try {
          const user = auth.currentUser;
          if (!user) return;
          const token = await user.getIdToken();
          const res = await fetch(imgUrl, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) return;
          const blob = await res.blob();
          setImg(URL.createObjectURL(blob));
          setFile(null);
        } catch {}
      })());
    }
    if (job.endImageGcsPath) {
      setEndGcsPath(job.endImageGcsPath);
      const endUrl = api.getEndImageUrl(job.id);
      hydrators.push((async () => {
        try {
          const user = auth.currentUser;
          if (!user) return;
          const token = await user.getIdToken();
          const res = await fetch(endUrl, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) return;
          const blob = await res.blob();
          setEndImg(URL.createObjectURL(blob));
          setEndFile(null);
        } catch {}
      })());
    }

    if (hydrators.length === 0) { setSettingsLoaded(true); return; }
    Promise.all(hydrators).finally(() => setSettingsLoaded(true));
  }, [settingsLoaded, prefsFetched, alwaysPreload]);

  // When switching models, clamp duration to valid range. Aspect-ratio
  // auto-correction is handled reactively below (covers both model changes
  // and sub-mode switches).
  const handleModelChange = (newModel) => {
    setModel(newModel);
    setModelOpen(false);
    // Kling Effects defaults to Auto aspect (no crop) on selection; the
    // user can opt into a ratio and we crop the input on our end.
    if (newModel === 'kling-effects') setRatio('auto');
    const m = MODELS.find(x => x.id === newModel);
    if (m?.geminiOmni) {
      // Gemini Omni: any whole second in [minDur, maxDur] at every resolution.
      const lo = m.minDur ?? 3;
      const hi = m.maxDur ?? 10;
      if (dur < lo) setDur(lo);
      else if (dur > hi) setDur(hi);
    } else if (m?.google) {
      // Veo: discrete durations (4/6/8 via durStep). 1080p / 4k (and Lite
      // 1080p via fixedDur) are 8s-only; everything else snaps to the
      // nearest step within [minDur, maxDur].
      if (m.fixedDur || m.res === '1080p' || m.res === '4K') {
        setDur(8);
      } else {
        const lo = m.minDur ?? 4;
        const hi = m.maxDur ?? 8;
        const step = m.durStep || 1;
        let next = Math.max(lo, Math.min(hi, dur));
        next = lo + Math.round((next - lo) / step) * step;
        if (next !== dur) setDur(next);
      }
    } else if (m?.grok) {
      // Grok: clamp to the model's own range. The ref2video sub-mode
      // imposes its own 10s cap separately via effectiveMaxDuration.
      const lo = m.minDur ?? 1;
      const hi = m.maxDur ?? 15;
      if (dur < lo) setDur(lo);
      else if (dur > hi) setDur(hi);
      setAudio(false); // Grok doesn't support audio toggle
    } else if (m?.perSec) {
      // perSec models clamp to the model's own min/max - kling-o1-pro
      // for example caps at 10s while kling-3.0 allows 15s.
      const lo = m.minDur ?? 3;
      const hi = m.maxDur ?? 10;
      if (dur < lo) setDur(lo);
      else if (dur > hi) setDur(hi);
    } else if (m?.fixedDur) {
      // Single-duration model (e.g. V1.5 = 5s only, V1.5 Pro = 10s only)
      setDur(m.fixedDur);
    } else {
      // Fixed: snap to 5 or 10
      if (dur !== 5 && dur !== 10) setDur(dur <= 7 ? 5 : 10);
    }
    // Sync audio to the model: Google Veo bundles audio (no toggle, no
    // generateAudio knob on the Gemini API), so Standard / Fast force
    // it on and Lite forces it off. Grok already gets cleared above.
    // Other models (Kling) keep whatever the user had as long as the
    // model still supports it; turn off when it doesn't.
    if (m?.google || m?.audioFixed) setAudio(!!m.audio);
    else if (!m?.audio) setAudio(false);
  };

  const isImageFile = (f) => f.type.startsWith("image/") || /\.(heic|heif|avif|webp|tiff?|bmp|jpe?g|png|gif)$/i.test(f.name);
  const handleFile = async (f) => {
    if (!f || !isImageFile(f)) { console.warn("handleFile rejected:", f?.name, f?.type); return; }
    console.log("handleFile:", f.name, f.type, f.size);
    // Show local preview immediately (works for JPEG/PNG/GIF; HEIC etc. will use server preview)
    const localUrl = URL.createObjectURL(f);
    setImg(localUrl);
    setFile(null); // will be null since we upload immediately
    setGcsPath(null);
    // A fresh image for an effect resets the crop to Auto so the user
    // re-decides framing per image rather than inheriting a stale ratio.
    if (model === 'kling-effects') setRatio('auto');
    // Upload immediately and store the GCS path
    try {
      const uploaded = await api.uploadImage(f);
      console.log("Upload result:", uploaded);
      setGcsPath(uploaded.gcsPath);
      // For formats the browser can't preview (HEIC, etc.), fetch the server-converted JPEG as preview
      if (uploaded.imageId && !f.type.match(/^image\/(jpeg|png|gif|svg)/)) {
        const preview = await fetchImageBlob(uploaded.imageId);
        if (preview) setImg(preview);
      }
    } catch (e) {
      console.error("Image upload failed:", e);
      notify("error", `Image upload failed: ${e.message}`, 5000);
      // Keep the blob URL for display, set file for deferred upload
      setFile(f);
    }
  };

  // Select an existing image from the picker or a job card. The picker
  // only shows images that already live on GCS, so we reuse the gcsPath
  // directly instead of round-tripping the bytes back through
  // /upload-image (which was rejecting 4K PNGs as 'File too large' and
  // is unnecessary work even when it succeeded).
  const handleSelectExisting = useCallback((blobUrl, existingGcsPath) => {
    setImg(blobUrl);
    setPickerOpen(false);
    setFile(null);
    setGcsPath(existingGcsPath || null);
    if (model === 'kling-effects') setRatio('auto');
  }, [model]);

  // Pick an existing image for the end-frame slot (mirrors handleSelectExisting)
  const handleSelectEndExisting = useCallback((blobUrl, existingGcsPath) => {
    setEndImg(blobUrl);
    setPickerOpen(false);
    setEndFile(null);
    setEndGcsPath(existingGcsPath || null);
    if (model === 'kling-effects') setRatio('auto');
  }, [model]);

  // Pick an existing image as the Vectorize source. The picker passes
  // a blob URL and the existing GCS path; we keep both so submission
  // can skip the upload step (gcsPath is truthy). Pre-validates the
  // bytes against the same Recraft constraints handleVectorizeFile
  // applies, so the user can't slip a 6MB or 20MP existing image past
  // the front end and discover it's rejected after submitting.
  const handleSelectVectorizeExisting = useCallback(async (blobUrl, existingGcsPath) => {
    setPickerOpen(false);
    try {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      // SVG outputs (from previous Recraft jobs) get rasterized to PNG
      // client-side and re-uploaded so we have a raster GCS path the
      // downstream Vectorize / imageToImage endpoints will accept.
      // Existing raster picks reuse their existing GCS path with no
      // re-upload (the original `existingGcsPath` is fine).
      if (await isSvgBlob(blob)) {
        const pngBlob = await rasterizeSvgBlobToPng(blob);
        const file = new File([pngBlob], 'rasterized.png', { type: 'image/png' });
        let uploadedPath = null;
        try {
          const up = await api.uploadImage(file);
          uploadedPath = up.gcsPath;
        } catch (e) {
          setVectorizeError('Could not upload the rasterized SVG. Try again.');
          return;
        }
        const previewUrl = URL.createObjectURL(pngBlob);
        try { if (vectorizeSrc) URL.revokeObjectURL(vectorizeSrc); } catch {}
        setVectorizeSrc(previewUrl);
        setVectorizeGcsPath(uploadedPath);
        setVectorizeFile(null);
        const err = await _validateVectorizeBlob(pngBlob);
        setVectorizeError(err);
        return;
      }
      // Raster pick: keep the existing GCS path so we skip the upload.
      try { if (vectorizeSrc) URL.revokeObjectURL(vectorizeSrc); } catch {}
      setVectorizeSrc(blobUrl);
      setVectorizeGcsPath(existingGcsPath || null);
      setVectorizeFile(null);
      const err = await _validateVectorizeBlob(blob);
      setVectorizeError(err);
    } catch {
      setVectorizeError('Could not read the selected image. Try a different one.');
    }
  }, [vectorizeSrc, _validateVectorizeBlob]);

  // End-frame upload handler (mirrors handleFile)
  const handleEndFile = async (f) => {
    if (!f || !isImageFile(f)) return;
    const localUrl = URL.createObjectURL(f);
    setEndImg(localUrl);
    setEndFile(null);
    setEndGcsPath(null);
    if (model === 'kling-effects') setRatio('auto');
    try {
      const uploaded = await api.uploadImage(f);
      setEndGcsPath(uploaded.gcsPath);
      if (uploaded.imageId && !f.type.match(/^image\/(jpeg|png|gif|svg)/)) {
        const preview = await fetchImageBlob(uploaded.imageId);
        if (preview) setEndImg(preview);
      }
    } catch (e) {
      console.error("End image upload failed:", e);
      notify("error", `End image upload failed: ${e.message}`, 5000);
      setEndFile(f);
    }
  };
  const clearEndFrame = () => {
    setEndImg(null); setEndFile(null); setEndGcsPath(null);
    setEndImgNatW(0); setEndImgNatH(0); setEndCropPos(0.5);
  };
  // Auto-clear end frame if model changes to one that doesn't support it
  useEffect(() => {
    if (!supportsEndFrame(model) && (endImg || endGcsPath || endFile)) {
      clearEndFrame();
    }
  }, [model, endImg, endGcsPath, endFile]);

  // ── Multi-image distribution: Start + End frames ───────────────────────────
  // A multi-file drop / file-browse can carry several images. Spread them
  // across the Start + End slots: the slot the user targeted gets the first
  // image, the other slot (when the model - or a dual Kling Effect - has
  // one) gets the next. Extra images beyond the available slots are
  // ignored. Picking an already-stored image from the picker grid stays
  // one-at-a-time and goes through handleSelectExisting instead.
  const framesAcceptEnd = () =>
    supportsEndFrame(model) || (isEffectsModel && klingEffectObj?.type === 'dual');
  const handleFramesFiles = (fileList, primary = 'start') => {
    const files = Array.from(fileList || []).filter(isImageFile);
    if (files.length === 0) return;
    const order = primary === 'end' && framesAcceptEnd() ? ['end', 'start'] : ['start', 'end'];
    let i = 0;
    for (const slot of order) {
      if (i >= files.length) break;
      if (slot === 'end' && !framesAcceptEnd()) continue;
      if (slot === 'start') handleFile(files[i++]);
      else handleEndFile(files[i++]);
    }
  };

  // Reuse all settings from a job
  const handleReuseJob = useCallback((job, imageBlobUrl) => {
    // Switch the Image Input Mode toggle to match the reused job so the
    // form shows the same drop zone it was generated with. text2video
    // isn't a toggle position, so land it on References (the default);
    // the auto-correct effect bounces ref2video -> Start / End Frame if
    // the reused model can't actually take references.
    setVidInputMode(job.inputMode === 'image2video' ? 'image2video' : 'ref2video');

    // Populate / clear the start frame. Clearing it on a ref- or
    // text-only job stops a stale frame from a prior job lingering in
    // the form after reuse.
    if (imageBlobUrl && job.imageGcsPath) {
      setImg(imageBlobUrl);
      setFile(null);
      setGcsPath(job.imageGcsPath);
    } else {
      setImg(null);
      setFile(null);
      setGcsPath(null);
      setImgNatW(0);
      setImgNatH(0);
      setCropPos(0.5);
    }
    // Populate / clear video reference images (ref2video). Mirrors the
    // image-gen reuse path: prime the skeleton restoring counter, then
    // rehydrate blobs + dims from the saved GCS paths so the colored
    // ref tiles (and their Kling validation) match the original job.
    if (Array.isArray(job.referenceImageGcsPaths) && job.referenceImageGcsPaths.length > 0) {
      setVidRefs([]);
      setVidRefsRestoring(job.referenceImageGcsPaths.length);
      (async () => {
        try {
          const items = await restoreRefsFromGcsPaths(job.referenceImageGcsPaths);
          setVidRefs(items);
        } catch {}
        setVidRefsRestoring(0);
      })();
    } else {
      setVidRefs([]);
    }
    // Populate end frame, if the job had one
    if (job.endImageGcsPath) {
      setEndGcsPath(job.endImageGcsPath);
      const endUrl = api.getEndImageUrl(job.id);
      (async () => {
        try {
          const user = auth.currentUser;
          if (!user) return;
          const token = await user.getIdToken();
          const res = await fetch(endUrl, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) return;
          const blob = await res.blob();
          setEndImg(URL.createObjectURL(blob));
          setEndFile(null);
        } catch {}
      })();
    } else {
      clearEndFrame();
    }
    // Populate settings
    if (job.prompt !== undefined) setPrompt(job.prompt || "");
    if (job.negativePrompt !== undefined) setNegPrompt(job.negativePrompt || "");
    // Only adopt the model if it's actually a video model (MODELS list).
    // Image-only models (e.g. grok-imagine-image, kling-*-img) would otherwise
    // poison the video tab's state and cause submissions to route incorrectly.
    if (job.model && MODELS.find(m => m.id === job.model)) setModel(job.model);
    if (job.duration) {
      const targetModel = (job.model && MODELS.find(m => m.id === job.model)) || sel;
      const cap = targetModel?.maxDur ?? 15;
      const lo  = targetModel?.minDur ?? 1;
      setDur(Math.max(lo, Math.min(cap, Number(job.duration))));
    }
    if (job.aspectRatio) {
      const isGrok = job.model && job.model.startsWith('grok-');
      // If reusing a Grok job that had auto, pick closest ratio from image dims
      if (isGrok && job.aspectRatio === 'auto' && job.imageWidth && job.imageHeight) {
        setRatio(closestRatio(job.imageWidth, job.imageHeight));
      } else if (isGrok && job.aspectRatio === 'auto') {
        setRatio("16:9");
      } else {
        setRatio(job.aspectRatio);
      }
    }
    if (job.audio !== undefined) setAudio(!!job.audio);
    if (job.cfgScale !== undefined) setCfgScale(job.cfgScale);
    // Restore the picked Kling Effect when reusing an effects job so the
    // form re-presents the same scene rather than asking the user to
    // pick again.
    if (job.model === 'kling-effects' && job.klingEffectScene) {
      setKlingEffect(job.klingEffectScene);
    }
    // Reveal the prefilled form by scrolling the page's real scroll
    // container - window.scrollTo is a no-op since the Studio shell
    // scrolls inside a position:fixed overflow div.
    scrollToTop();
  }, [scrollToTop]); // eslint-disable-line

  // Reuse an image-gen job's settings into the Generate Image form.
  // Mirrors handleImageJobsReady's restore logic but always fires (no
  // first-load guard) and rehydrates references through the skeleton-tile
  // restoring counter so the form refs don't pop in.
  const handleReuseImageJob = useCallback((job /* , _unused */) => {
    setMode('image');
    if (job.model) {
      const meta = IMAGE_MODELS.find(m => m.id === job.model);
      if (meta) {
        setImgModel(job.model);
      }
    }
    if (job.prompt !== undefined) setImgPrompt(job.prompt || '');
    if (job.negativePrompt !== undefined) setImgNegPrompt(job.negativePrompt || '');
    if (job.aspectRatio) setImgRatio(job.aspectRatio);
    if (job.imageResolution) setImgResolution(job.imageResolution);
    if (job.n) setImgN(Math.min(4, Math.max(1, Number(job.n))));
    if (Array.isArray(job.referenceImageGcsPaths) && job.referenceImageGcsPaths.length > 0) {
      setImgRefs([]);
      setImgRefsRestoring(job.referenceImageGcsPaths.length);
      (async () => {
        try {
          const items = await restoreRefsFromGcsPaths(job.referenceImageGcsPaths);
          setImgRefs(items);
        } catch {}
        setImgRefsRestoring(0);
      })();
    } else {
      setImgRefs([]);
    }
    // Source image restore for Vectorize / V3 Vector Edit jobs. The
    // job's source raster (which may itself be a rasterized SVG from a
    // prior pick) lives at job.imageGcsPath; fetch it back into the
    // preview slot so reuse mirrors the previous submission instead of
    // showing an empty source-image zone.
    const usesSourceImage = job.model === 'recraft-vectorize' || job.model === 'recraft-v3-vector-edit';
    if (usesSourceImage && job.imageGcsPath) {
      setVectorizeGcsPath(job.imageGcsPath);
      setVectorizeFile(null);
      setVectorizeError(null);
      const imgUrl = api.getImageUrl(job.id);
      (async () => {
        try {
          const user = auth.currentUser;
          if (!user) return;
          const token = await user.getIdToken();
          const res = await fetch(imgUrl, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) return;
          const blob = await res.blob();
          setVectorizeSrc(prev => { try { if (prev) URL.revokeObjectURL(prev); } catch {} return URL.createObjectURL(blob); });
        } catch {}
      })();
    } else {
      setVectorizeSrc(prev => { try { if (prev) URL.revokeObjectURL(prev); } catch {} return null; });
      setVectorizeFile(null);
      setVectorizeGcsPath(null);
      setVectorizeError(null);
    }
    scrollToTop();
  }, [scrollToTop]); // eslint-disable-line

  // Cross-page reuse: LibraryView's Reuse button navigates to /generate/...
  // with `state.reuseJob` set. Dispatch to the appropriate handler and
  // clear the state so a refresh / back / forward doesn't re-fire it.
  const reuseConsumedRef = useRef(false);
  useEffect(() => {
    const reuseJob = location.state && location.state.reuseJob;
    if (!reuseJob || reuseConsumedRef.current) return;
    reuseConsumedRef.current = true;
    // Replace the history entry so reload/back doesn't trigger the same
    // reuse again. Keeps the URL the user landed on.
    navigate(location.pathname, { replace: true, state: null });

    if (reuseJob.outputType === 'image') {
      handleReuseImageJob(reuseJob);
      return;
    }
    // Video reuse: handleReuseJob expects the source image as a blob URL
    // (mirrors the in-page Reuse button's behavior in RecentJobs). Skip
    // the fetch when there's no source image (text2video / ref2video).
    if (!reuseJob.imageGcsPath) {
      handleReuseJob(reuseJob, null);
      return;
    }
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) { handleReuseJob(reuseJob, null); return; }
        const token = await user.getIdToken();
        const res = await fetch(api.getImageUrl(reuseJob.id), { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { handleReuseJob(reuseJob, null); return; }
        const blob = await res.blob();
        handleReuseJob(reuseJob, URL.createObjectURL(blob));
      } catch { handleReuseJob(reuseJob, null); }
    })();
  }, [location.state, location.pathname, navigate, handleReuseJob, handleReuseImageJob]);

  const go = async () => {
    // Kling Effects path. Routes to /v1/videos/effects with a pre-designed
    // scene. Single-image effects need img; dual-image effects (15 scenes
    // like kiss_pro / hug_pro) need img + endImg. No prompt, no duration,
    // no ratio, no audio, no cfg - the scene picks everything.
    if (model === 'kling-effects') {
      const eff = getKlingEffect(klingEffect);
      if (!eff) {
        setErrMsg('Pick a Kling Effect first.');
        return;
      }
      if (!img && !file) {
        setErrMsg('Add an image to use this effect.');
        return;
      }
      if (eff.type === 'dual' && !endImg && !endFile) {
        setErrMsg('This effect needs two images. Add the second image.');
        return;
      }
      if (atMaxJobs) {
        setErrMsg(`You can only have ${maxConcurrent} jobs processing at once. Please wait for a job to finish.`);
        return;
      }
      if (creditBalance <= 0) {
        setErrMsg('You need credits to generate videos. Buy credits in the Credits page.');
        return;
      }
      const effCredits = ceilCredits(eff.priceUsd);
      if (effCredits > creditBalance) {
        setErrMsg('Not enough credits');
        return;
      }
      setErrMsg(null);
      setStage('up');
      try {
        let imageGcsPath = gcsPath;
        if (file) {
          const uploaded = await api.uploadImage(file);
          imageGcsPath = uploaded.gcsPath;
          setGcsPath(imageGcsPath);
          setFile(null);
        }
        let endPath = endGcsPath;
        if (eff.type === 'dual' && endFile) {
          const uploaded = await api.uploadImage(endFile);
          endPath = uploaded.gcsPath;
          setEndGcsPath(endPath);
          setEndFile(null);
        }
        setStage('sub');
        const result = await api.submitJob({
          outputType: 'video',
          inputMode: 'image2video',
          model: 'kling-effects',
          klingEffectScene: klingEffect,
          imageGcsPath,
          endImageGcsPath: eff.type === 'dual' ? (endPath || null) : null,
          referenceImageGcsPaths: [],
          // The user picks an aspect ratio for the effect; the backend
          // crops the input image(s) to it (server-side cropToRatio,
          // same as normal video) before handing them to Kling. 'auto'
          // = no crop, original framing. prompt / duration / audio /
          // cfg are still ignored by the effects pipeline.
          prompt: '',
          aspectRatio: supportsRatio(model) ? ratio : 'auto',
          cropPosition: cropPos,
          endCropPosition: endCropPos,
        });
        setNewestJobId(result.jobId);
        setStage('done');
        setRefreshKey(k => k + 1);
        const scrollToJob = (attempts = 0) => {
          const el = document.querySelector(`[data-job-id="${result.jobId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          else if (attempts < 50) setTimeout(() => scrollToJob(attempts + 1), 100);
        };
        scrollToJob();
        setTimeout(() => { setStage('idle'); }, 1500);
      } catch (e) {
        setStage('idle');
        setErrMsg(e.message || 'Generation failed');
      }
      return;
    }

    // Effective input mode follows the user's active sub-tab whenever
    // that tab has the content it needs. We only fall back to a
    // different mode if the active tab is empty - otherwise stale state
    // from a previously-visited sub-tab (e.g. references left over on
    // ref2video while the user is now on image2video with start/end
    // frames set) would silently override the user's explicit choice.
    let effectiveMode = vidInputMode;
    if (effectiveMode === 'image2video') {
      const hasFrame = !!img || (supportsEndFrame(model) && !!endImg);
      if (!hasFrame) {
        if (vidRefs.length > 0 && supportsRef2Video(model)) effectiveMode = 'ref2video';
        else if (supportsText2Video(model))                 effectiveMode = 'text2video';
      }
    } else if (effectiveMode === 'ref2video') {
      if (vidRefs.length === 0) {
        if (img)                            effectiveMode = 'image2video';
        else if (supportsText2Video(model)) effectiveMode = 'text2video';
      }
    }
    // text2video: trust the tab; references in state from another
    // sub-tab don't drag the submission back to ref2video.

    // Validation per effective mode.
    if (effectiveMode === 'image2video') {
      const hasAnyImage = img || (supportsEndFrame(model) && endImg);
      if (!hasAnyImage) {
        setErrMsg("Add a start frame (or end frame) - this model can't generate video from a prompt alone.");
        return;
      }
      if (requiresStartFrame(model) && !img && !endImg) return;
      if (startFrameError) {
        setErrMsg(`Start frame: ${startFrameError} Replace it before submitting.`);
        return;
      }
      if (endFrameError) {
        setErrMsg(`End frame: ${endFrameError} Replace it before submitting.`);
        return;
      }
    } else if (effectiveMode === 'ref2video') {
      if (vidRefs.length === 0) {
        setErrMsg('Add at least one reference image, or switch to a different mode.');
        return;
      }
      if (vidRefsHaveError) {
        setErrMsg("One or more reference images don't meet the model's size / aspect requirements. Replace them before submitting.");
        return;
      }
    }
    if (!prompt || !prompt.trim()) {
      setErrMsg("Please add a prompt describing the video you want to generate.");
      return;
    }
    // The prompt box's maxLength stops typing past the cap but doesn't trim a
    // longer prompt carried over from a model with a higher limit.
    const vidLim = videoPromptLimit(model).prompt;
    if (prompt.length > vidLim) {
      setErrMsg(`This model accepts prompts up to ${vidLim.toLocaleString()} characters; yours is ${prompt.length.toLocaleString()}. Shorten it before submitting.`);
      return;
    }
    if (atMaxJobs) {
      setErrMsg(`You can only have ${maxConcurrent} jobs processing at once. Please wait for a job to finish.`);
      return;
    }
    if (creditBalance <= 0) {
      setErrMsg("You need credits to generate videos. Buy credits in the Credits page.");
      return;
    }
    if (price && parseInt(price) > creditBalance) {
      setErrMsg("Not enough credits");
      return;
    }
    // text2video has no start frame / references. It's only reached when
    // the chosen side was left empty on a text-capable model - make the
    // user explicitly opt in so an empty drop never silently produces a
    // prompt-only video they didn't intend.
    if (effectiveMode === 'text2video') {
      const ok = await confirm({
        title: 'Generate from text only?',
        message: 'No start frame or reference images are set, so this video will be generated from your prompt alone. Add an image first if you wanted image- or reference-driven video.',
        confirmLabel: 'Generate from text',
        cancelLabel: 'Go back',
      });
      if (!ok) return;
    }
    setErrMsg(null);
    // Auto-save generation settings on every attempt
    api.saveGenerateSettings({ prompt, negativePrompt: negPrompt, model, duration: dur, aspectRatio: ratio, audio, cfgScale }).catch(() => {});
    setStage("up");
    try {
      let imageGcsPath = null;
      let finalEndPath = null;
      let refPaths = [];

      if (effectiveMode === 'image2video') {
        imageGcsPath = gcsPath;
        if (file) {
          const uploaded = await api.uploadImage(file);
          imageGcsPath = uploaded.gcsPath;
          setGcsPath(imageGcsPath);
          setFile(null);
        }
        finalEndPath = supportsEndFrame(model) ? endGcsPath : null;
        if (supportsEndFrame(model) && endFile) {
          const uploaded = await api.uploadImage(endFile);
          finalEndPath = uploaded.gcsPath;
          setEndGcsPath(finalEndPath);
          setEndFile(null);
        }
        if (!imageGcsPath && !finalEndPath) {
          setStage("idle");
          setErrMsg("No image selected");
          return;
        }
        if (requiresStartFrame(model) && !imageGcsPath) {
          setStage("idle");
          setErrMsg(supportsEndFrame(model)
            ? "This model needs a starting image (only Kling 1.5 Pro and 1.6 Pro support end-frame-only)."
            : "This model requires a starting image.");
          return;
        }
      } else if (effectiveMode === 'ref2video') {
        for (const r of vidRefs) {
          if (r.gcsPath) { refPaths.push(r.gcsPath); continue; }
          if (r.file) {
            const up = await api.uploadImage(r.file);
            refPaths.push(up.gcsPath);
          }
        }
      }
      // text2video: no image work needed.

      setStage("sub");

      // Final-clamp duration against the model + sub-mode cap as a
      // defense in depth so stale state from job/preset reuse can't
      // sneak past the slider's range and reach the upstream API.
      // Also forces 8s on Veo whenever ASSET refs or a last-frame are
      // attached - matches the server-side coercion in submitVeoJob.
      const submitOpts = {
        hasVidRefs: refPaths.length > 0,
        hasEndFrame: !!finalEndPath,
      };
      const submitDur = Math.min(
        Math.max(dur, effectiveMinDuration(model, effectiveMode, submitOpts)),
        effectiveMaxDuration(model, effectiveMode, submitOpts),
      );

      // Submit job (backend reads image from GCS for Kling)
      const result = await api.submitJob({
        outputType: 'video',
        inputMode: effectiveMode,
        imageGcsPath,
        endImageGcsPath: finalEndPath || null,
        referenceImageGcsPaths: refPaths,
        prompt,
        negativePrompt: negPrompt,
        duration:    submitDur,
        model,
        aspectRatio: supportsRatio(model) ? ratio : "auto",
        cropPosition: cropPos,
        endCropPosition: endCropPos,
        audio,
        cfgScale,
      });

      setNewestJobId(result.jobId);
      setStage("done");
      setRefreshKey(k => k + 1);
      // Smooth scroll to the new job card once it lands in the DOM. The job list
      // refresh is async, so poll briefly until the element appears (up to 5s).
      const scrollToJob = (attempts = 0) => {
        const el = document.querySelector(`[data-job-id="${result.jobId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (attempts < 50) {
          setTimeout(() => scrollToJob(attempts + 1), 100);
        }
      };
      scrollToJob();
      // Keep all settings as-is — just reset stage after brief success indication
      setTimeout(() => { setStage("idle"); }, 1500);

      // If backend cropped the image, fetch and display the cropped version immediately
      if (result.imageGcsPath && result.imageGcsPath !== imageGcsPath) {
        setGcsPath(result.imageGcsPath);
        // Fetch the cropped image to display
        try {
          const user = auth.currentUser;
          if (user) {
            const token = await user.getIdToken();
            const imgUrl = api.getImageUrl(result.jobId);
            delete _blobCache[imgUrl];
            const imgRes = await fetch(imgUrl, { headers: { Authorization: `Bearer ${token}` } });
            if (imgRes.ok) {
              const blob = await imgRes.blob();
              const blobUrl = URL.createObjectURL(blob);
              _blobCache[imgUrl] = blobUrl;
              setImg(blobUrl);
            }
          }
        } catch {}
      }

    } catch (e) {
      setStage("idle");
      setErrMsg(e.message || "Generation failed");
    }
  };
  goRef.current = go;

  // ─── Video sub-mode helpers ─────────────────────────────────────────────────
  const vidRefMax = refMaxForVideo(model);
  // Auto-correct sub-mode when the model changes: if current mode is no longer
  // supported, fall back to image2video (always supported).
  useEffect(() => {
    // Don't auto-correct until settings have hydrated. On a reload the
    // sub-tab is URL-driven (e.g. /generate/video/ref2vid -> ref2video)
    // but `model` is still the useState default (kling-3.0) until the
    // saved model is restored - and kling-3.0 doesn't support ref2video,
    // so running now would wrongly bounce the user to image2video before
    // their real (ref-capable) model loads. Once settingsLoaded flips,
    // this runs with the restored model + handles later picker changes.
    if (!settingsLoaded) return;
    // Use modelSupportsInputMode (not supportsRef2Video) so kling-effects,
    // which is intentionally offered for both References AND Start/End
    // Frame, isn't bounced off the References toggle.
    // text2video is no longer a user-selectable position - it's inferred
    // at submit from an empty drop. Never let the toggle rest on it:
    // coerce to References (or Start/End Frame if refs are unsupported).
    if (vidInputMode === 'text2video') setVidInputMode(modelSupportsInputMode(model, 'ref2video') ? 'ref2video' : 'image2video');
    if (vidInputMode === 'ref2video'  && !modelSupportsInputMode(model, 'ref2video'))  setVidInputMode('image2video');
    if (vidRefs.length > vidRefMax) setVidRefs(r => r.slice(0, vidRefMax));
  }, [model, settingsLoaded]); // eslint-disable-line

  // Clamp duration if the current value exceeds the effective max for the
  // active model+mode (e.g. Grok ref2video caps at 10s even though the
  // model's own max is 15). Also snaps to 8s when Veo locks the duration
  // due to references or a last-frame image being attached.
  useEffect(() => {
    const opts = {
      hasVidRefs: vidRefs.length > 0,
      hasEndFrame: !!(endGcsPath || endImg),
    };
    const max = effectiveMaxDuration(model, vidInputMode, opts);
    const min = effectiveMinDuration(model, vidInputMode, opts);
    if (dur > max) setDur(max);
    else if (dur < min) setDur(min);
  }, [model, vidInputMode, vidRefs.length, endGcsPath, endImg]); // eslint-disable-line

  // Auto-correct model when the input mode changes and the current model
  // doesn't support the new mode. Prefer the first recommended model that
  // does, falling back to the first MODELS entry that does.
  useEffect(() => {
    if (modelSupportsInputMode(model, vidInputMode)) return;
    const recommendedIds = studioVideoRecommended.length > 0
      ? studioVideoRecommended
      : ['kling-3.0-pro', 'kling-3.0-omni-pro', 'grok-imagine-720p', 'kling-3.0', 'kling-3.0-omni'];
    const disabledSet = new Set(studioVideoDisabled);
    const pick = recommendedIds.find(id => !disabledSet.has(id) && modelSupportsInputMode(id, vidInputMode))
              || MODELS.find(m => !disabledSet.has(m.id) && modelSupportsInputMode(m.id, vidInputMode))?.id;
    if (pick && pick !== model) setModel(pick);
  }, [vidInputMode, studioVideoRecommended, studioVideoDisabled]); // eslint-disable-line

  // Auto-correct aspect ratio when (model, sub-mode) changes the allowed list.
  // If the current ratio is no longer valid (or 'auto' isn't allowed), pick
  // the closest valid one based on the start frame's natural dimensions.
  useEffect(() => {
    const allowed = getVideoRatios(model, vidInputMode);
    const allowAuto = videoSupportsAutoRatio(model, vidInputMode);
    const currentValid = (ratio === 'auto' && allowAuto) || allowed.some(r => r.val === ratio);
    if (currentValid) return;
    const best = closestRatio(imgNatW, imgNatH);
    const bestValid = allowed.some(r => r.val === best);
    setRatio(bestValid ? best : allowed[0].val);
  }, [model, vidInputMode]); // eslint-disable-line

  const addVidRefFromBlob = useCallback(async (blobUrl, existingGcsPath) => {
    setVidRefPickerOpen(false);
    // The picker shows images the user already owns (uploads + generated
    // outputs from completed image-gen jobs), so existingGcsPath is always
    // a valid path the backend can read. No re-upload needed. dims drives
    // the Kling min-side / aspect validation (see vidRefErrors).
    const dims = await loadImageDims(blobUrl);
    if (existingGcsPath) {
      setVidRefs(r => [...r, { blobUrl, gcsPath: existingGcsPath, file: null, dims }]);
      return;
    }
    setVidRefs(r => [...r, { blobUrl, gcsPath: null, file: null, dims }]);
  }, []);
  const addVidRefFromFile = useCallback(async (f) => {
    setVidRefPickerOpen(false);
    if (!f || !isImageFile(f)) return;
    const localUrl = URL.createObjectURL(f);
    const dims = await loadImageDims(localUrl);
    try {
      const uploaded = await api.uploadImage(f);
      setVidRefs(r => [...r, { blobUrl: localUrl, gcsPath: uploaded.gcsPath, file: null, dims }]);
    } catch (e) {
      notify('error', `Reference upload failed: ${e.message}`, 5000);
      setVidRefs(r => [...r, { blobUrl: localUrl, gcsPath: null, file: f, dims }]);
    }
  }, [notify]);
  const removeVidRef = (idx) => setVidRefs(r => r.filter((_, i) => i !== idx));
  // Multi-image add for the video references grid. A drop / browse-selection
  // can carry several images; add as many as fit under the model's ref cap
  // (refMaxForVideo) and silently ignore the overflow.
  // Backfill dims for any vidRef that arrived without them (older
  // job-reuse flows wrote refs before dims were tracked). Mirrors the
  // imgRefs backfill - same loadImageDims helper, one ref at a time so
  // re-renders are bounded.
  useEffect(() => {
    const missing = vidRefs.findIndex(r => r.blobUrl && !r.dims);
    if (missing < 0) return;
    let cancelled = false;
    (async () => {
      const dims = await loadImageDims(vidRefs[missing].blobUrl);
      if (cancelled || !dims) return;
      setVidRefs(rs => rs.map((r, i) => i === missing ? { ...r, dims } : r));
    })();
    return () => { cancelled = true; };
  }, [vidRefs]);
  const addVidRefFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(isImageFile);
    const room = refMaxForVideo(model) - vidRefs.length;
    for (const f of files.slice(0, Math.max(0, room))) {
      await addVidRefFromFile(f);
    }
  };

  // ─── Image-mode helpers ─────────────────────────────────────────────────────
  // Image-generation models. Three flavors map to three Kling endpoints + Grok.
  // Resolution support varies; the UI gates the resolution pills off these.
  const IMAGE_MODELS = [
    // Authored order roughly mirrors the picker sort, but the picker
    // re-sorts via `sortModels` so this array can be edited in any order.
    //
    // Sort priority (applied in the picker):
    //   1. Reference-image support: 2+ refs > 1 ref > text-only (from maxRefs)
    //   2. Max available resolution descending (4k > 2k > 1k > svg)
    //   3. Provider: Gemini > FLUX > Kling > Grok > Recraft
    //   4. qualityRank descending (Max/Ultra > Pro > standard > Fast/budget)
    //
    // Per-model aspect-ratio lists are drawn from each provider's docs.
    // "Auto" isn't offered for any image model — there's no source frame
    // to derive from for text2image, and even Omni's auto mode just
    // guesses, which isn't great UX vs an explicit pick. Aspect-ratio
    // order within each entry: square first, then portrait (closest to
    // 1:1 first), then landscape (closest to 1:1 first).

    // ── 4K tier ──────────────────────────────────────────────────────
    { id:'google-nano-banana-pro', label:'Nano Banana Pro',  flavor:'google',resolutions:['1k','2k','4k'], maxRefs:5,  ratios:['1:1','4:5','3:4','2:3','9:16','5:4','4:3','3:2','16:9','21:9'], audio:false, qualityRank:8 },
    { id:'google-nano-banana-2.1', label:'Nano Banana 2.1',  flavor:'google',resolutions:['1k','2k','4k'], maxRefs:5,  ratios:['1:1','4:5','3:4','2:3','9:16','5:4','4:3','3:2','16:9','21:9'], audio:false, qualityRank:6 },
    // OpenAI GPT Image 2.5 (the models behind ChatGPT Images). Quality
    // tiers are separate entries; up to 16 refs upstream, capped at 8 here.
    { id:'openai-image-2.5-sunburst', label:'GPT Image 2.5 Sunburst', flavor:'openai', resolutions:['1k','2k','4k'], maxRefs:8, ratios:['1:1','4:5','3:4','2:3','9:16','1:2','5:4','4:3','3:2','16:9','2:1','21:9'], audio:false, qualityRank:9 },
    { id:'openai-image-2.5-flare-hq', label:'GPT Image 2.5 Flare HQ', flavor:'openai', resolutions:['1k','2k','4k'], maxRefs:8, ratios:['1:1','4:5','3:4','2:3','9:16','1:2','5:4','4:3','3:2','16:9','2:1','21:9'], audio:false, qualityRank:7 },
    { id:'openai-image-2.5-flare',    label:'GPT Image 2.5 Flare',    flavor:'openai', resolutions:['1k','2k','4k'], maxRefs:8, ratios:['1:1','4:5','3:4','2:3','9:16','1:2','5:4','4:3','3:2','16:9','2:1','21:9'], audio:false, qualityRank:4 },
    { id:'kling-3.0-omni-img',     label:'Kling 3.0 Omni',   flavor:'omni',  resolutions:['1k','2k','4k'], maxRefs:10, ratios:['1:1','3:4','2:3','9:16','4:3','3:2','16:9','21:9'], audio:false, qualityRank:10 },

    // ── 2K tier ──────────────────────────────────────────────────────
    { id:'bfl-flux-2-max',         label:'FLUX.2 Max',       flavor:'bfl',   resolutions:['1k','2k'],      maxRefs:8,  ratios:['1:1','3:4','4:3','9:16','16:9','2:3','3:2'], audio:false, refSummary:'Up to 8 input images for editing / fusion', supportsSeed:true, qualityRank:10 },
    { id:'bfl-flux-2-pro',         label:'FLUX.2 Pro',       flavor:'bfl',   resolutions:['1k','2k'],      maxRefs:8,  ratios:['1:1','3:4','4:3','9:16','16:9','2:3','3:2'], audio:false, refSummary:'Up to 8 input images for editing / fusion', supportsSeed:true, supportsLatestVariant:true, qualityRank:8 },
    { id:'bfl-flux-2-flex',        label:'FLUX.2 Flex',      flavor:'bfl',   resolutions:['1k','2k'],      maxRefs:8,  ratios:['1:1','3:4','4:3','9:16','16:9','2:3','3:2'], audio:false, refSummary:'Up to 8 input images for editing / fusion', supportsSeed:true, qualityRank:7 },
    { id:'kling-image-o1-img',     label:'Kling Image O1',   flavor:'omni',  resolutions:['1k','2k'],      maxRefs:10, ratios:['1:1','3:4','2:3','9:16','4:3','3:2','16:9','21:9'], audio:false, qualityRank:10 },
    { id:'kling-3.0-img',          label:'Kling 3.0',        flavor:'gen',   resolutions:['1k','2k'],      maxRefs:1,  ratios:['1:1','3:4','2:3','9:16','4:3','3:2','16:9','21:9'], audio:false, qualityRank:7 },
    { id:'grok-imagine-image-2-hq',label:'Grok Imagine 2.0 HQ', flavor:'grok', resolutions:['1k','2k'],   maxRefs:5,  ratios:['1:1','3:4','2:3','9:16','9:19.5','9:20','1:2','4:3','3:2','16:9','19.5:9','20:9','2:1','21:9','5:2'], audio:false, qualityRank:8 },
    { id:'grok-imagine-image-2',   label:'Grok Imagine 2.0', flavor:'grok',  resolutions:['1k','2k'],      maxRefs:5,  ratios:['1:1','3:4','2:3','9:16','9:19.5','9:20','1:2','4:3','3:2','16:9','19.5:9','20:9','2:1','21:9','5:2'], audio:false, qualityRank:7 },
    { id:'grok-imagine-image',     label:'Grok Imagine',     flavor:'grok',  resolutions:['1k','2k'],      maxRefs:5,  ratios:['1:1','3:4','2:3','9:16','1:2','4:3','3:2','16:9','2:1'], audio:false, qualityRank:5 },

    // ── 1K tier ──────────────────────────────────────────────────────
    { id:'google-nano-banana-2-lite', label:'Nano Banana 2 Lite', flavor:'google',resolutions:['1k'],       maxRefs:5,  ratios:['1:1','4:5','3:4','2:3','9:16','5:4','4:3','3:2','16:9','21:9'], audio:false, qualityRank:2 },
    { id:'google-nano-banana',     label:'Nano Banana',      flavor:'google',resolutions:['1k'],           maxRefs:5,  ratios:['1:1','4:5','3:4','2:3','9:16','5:4','4:3','3:2','16:9','21:9'], audio:false, qualityRank:1 },
    { id:'kling-2.1-img',          label:'Kling 2.1',        flavor:'gen',   resolutions:['1k'],           maxRefs:1,  ratios:['1:1','3:4','2:3','9:16','4:3','3:2','16:9','21:9'], audio:false, qualityRank:5 },
    { id:'kling-2.1-multi-img',    label:'Kling 2.1 Multi',  flavor:'multi', resolutions:['1k'],           maxRefs:4,  ratios:['1:1','3:4','2:3','9:16','4:3','3:2','16:9','21:9'], audio:false, qualityRank:4 },

    // ── Vector (SVG) - Recraft.ai ────────────────────────────────────
    // Vector models share a synthetic `resolutions:['svg']` so the
    // resolution-pill UI hides itself; output is resolution-independent.
    // V3 Vector Edit takes a source image + prompt; Vectorize takes a
    // raster image and produces an SVG approximation (no prompt).
    { id:'recraft-v4-pro-vector',  label:'Recraft v4 Pro Vector',  flavor:'recraft',output:'vector',inputType:'prompt',resolutions:['svg'],maxRefs:0,ratios:['1:1','5:4','4:5','3:4','2:3','9:16','4:3','3:2','16:9','1:2','2:1'], audio:false, refSummary:'SVG output', qualityRank:10 },
    { id:'recraft-v4-vector',      label:'Recraft v4 Vector',      flavor:'recraft',output:'vector',inputType:'prompt',resolutions:['svg'],maxRefs:0,ratios:['1:1','5:4','4:5','3:4','2:3','9:16','4:3','3:2','16:9','1:2','2:1'], audio:false, refSummary:'SVG output', qualityRank:8 },
    { id:'recraft-v3-vector',      label:'Recraft v3 Vector',      flavor:'recraft',output:'vector',inputType:'prompt',resolutions:['svg'],maxRefs:0,ratios:['1:1','5:4','4:5','3:4','2:3','9:16','4:3','3:2','16:9','1:2','2:1'], audio:false, refSummary:'SVG output', qualityRank:5 },
    { id:'recraft-v3-vector-edit', label:'Recraft v3 Vector Edit', flavor:'recraft',output:'vector',inputType:'image+prompt',resolutions:['svg'],maxRefs:0,ratios:['auto'], audio:false, refSummary:'Image + prompt → SVG', qualityRank:4 },
    { id:'recraft-vectorize',      label:'Vectorize',              flavor:'recraft',output:'vector',inputType:'image', resolutions:['svg'],maxRefs:0,ratios:['auto'], audio:false, refSummary:'Image → SVG', qualityRank:1 },
  ];
  const imgSel = IMAGE_MODELS.find(m => m.id === imgModel) || IMAGE_MODELS[0];

  // Aspect ratios available for the currently selected image model.
  const imgRatios = sortRatios(imgSel.ratios);

  // imgResLabel is module-scoped so card renderers in RecentJobs can use it too.

  // Estimated USD cost per image-gen submission. Kling: per-call unit count
  // from the Resource Packs doc × klingImagePointValue (default $0.0035).
  // Grok: flat per-image USD × billable count (refs + outputs); for Pro the
  // backend composites multiple refs into one image so input count caps at 1.
  const KLING_IMAGE_UNITS = {
    'kling-3.0-omni-img': { '1k': 8, '2k': 8, '4k': 16 },
    'kling-image-o1-img': { '1k': 8, '2k': 8 },
    'kling-3.0-img':      { '1k': 8, '2k': 8 },
    'kling-2.1-img':      { '1k_text': 4, '1k_ref': 8 },
    'kling-2.1-multi-img':{ '1k': 16 },
  };
  // Grok image pricing (verified against the audit endpoint over 100+ jobs):
  //   standard: $0.02/output + $0.002/ref  -  matches every observed actual
  //   2.0:      per quality tier and resolution (xAI model listing,
  //             2026-10), keyed by resolution code; refs estimated at the
  //             standard model's $0.002 until real jobs confirm.
  const GROK_IMAGE_PER_OUTPUT_USD = {
    'grok-imagine-image':      0.02,
    'grok-imagine-image-2':    { '1k': 0.04, '2k': 0.06 },
    'grok-imagine-image-2-hq': { '1k': 0.06, '2k': 0.08 },
  };
  const GROK_IMAGE_PER_REF_USD = {
    'grok-imagine-image':      0.002,
    'grok-imagine-image-2':    0.002,
    'grok-imagine-image-2-hq': 0.002,
  };
  // Models that add a single per-call surcharge when ANY refs are
  // present, instead of charging per individual ref.
  const GROK_IMAGE_REF_SURCHARGE_USD = {};
  // Google: Nano Banana original and 2 Lite are flat per image (1K only);
  // 2.1 and Pro tier their pricing by output resolution per Google's
  // published rates. byRes wins when present. Mirrors GOOGLE_IMAGE_COST in
  // katana-backend/src/poller.js.
  const GOOGLE_IMAGE_COST = {
    'google-nano-banana':        { flat: 0.039 },
    'google-nano-banana-2-lite': { flat: 0.0336 },
    'google-nano-banana-2.1':    { byRes: { '1k': 0.0336, '2k': 0.0504, '4k': 0.113 } },
    'google-nano-banana-pro':    { byRes: { '1k': 0.134,  '2k': 0.134,  '4k': 0.24  } },
  };
  // OpenAI GPT Image bills per token; the poller charges the exact usage
  // cost. Pre-submit estimate: ~1024x1024 output tokens x $30/1M (medium
  // ~439, high ~1756 tokens, third-party measurements), scaled by output
  // pixels, plus ~$0.012 per reference image (image input tokens x $8/1M).
  // Mirrors OPENAI_IMAGE_COST_1K in katana-backend/src/poller.js.
  const OPENAI_IMAGE_COST = {
    'openai-image-2.5-flare':    0.0132,
    'openai-image-2.5-flare-hq': 0.0527,
    'openai-image-2.5-sunburst': 0.0527,
  };
  const OPENAI_RES_SCALE = { '1k': 1, '2k': 4, '4k': 7.91 };
  const OPENAI_PER_REF_USD = 0.012;
  // Recraft is flat per-image. Verified against
  // recraft.ai/docs/api-reference/pricing (1000 API units = $1, so the
  // unit count IS the USD cost in tenths-of-cents). Vector-only catalog;
  // mirrors RECRAFT_IMAGE_COST in katana-backend/src/poller.js.
  const RECRAFT_IMAGE_COST = {
    'recraft-v4-pro-vector':  0.30,
    'recraft-v4-vector':      0.08,
    'recraft-v3-vector':      0.08,
    'recraft-v3-vector-edit': 0.08,
    'recraft-vectorize':      0.01,
  };
  // BFL FLUX. Empirically-derived linear pricing (intercept + slope × MP).
  //   - Authoritative billing now comes from `bflReportedCostUsd` (captured
  //     from the BFL submit response) and is used by the poller for the
  //     actual charge. This table is for the *pre-submission estimate*
  //     shown next to the Generate button.
  //   - BFL bills in mebipixels (width × height ÷ 2^20), not decimal MP.
  //   - Pro has different rates for text vs edit (presence of any ref).
  //     Max + Flex use one rate regardless of mode.
  //   - Coefficients fit to observed data points (1MP and 2.25MP cases).
  //     Flex coefficients are extrapolated from docs and will need a
  //     refinement pass once real data lands.
  // Keep in sync with BFL_IMAGE_COST in katana-backend/src/poller.js.
  const BFL_IMAGE_COST = {
    'bfl-flux-2-pro':  { textIntercept: 0.015, textSlope: 0.015, editIntercept: 0.009, editSlope: 0.036 },
    'bfl-flux-2-max':  { intercept: -0.026, slope: 0.096 },
    'bfl-flux-2-flex': { intercept: -0.11,  slope: 0.16  },
  };
  function googleImagePrice(model, resolution) {
    const c = GOOGLE_IMAGE_COST[model];
    if (!c) return 0;
    if (c.flat !== undefined) return c.flat;
    return c.byRes[resolution] ?? c.byRes['1k'] ?? 0;
  }
  // Mirror of backend bfl.js `_dimensionsFor` so the estimate matches the
  // exact width × height we'll send. Returns mebipixels (BFL's billing unit).
  function bflEstimatedMp(ratio, resolution) {
    const targetMP = resolution === '2k' ? 4 : 1;
    const [rwStr, rhStr] = String(ratio || '1:1').split(':');
    const rw = Number(rwStr) || 1;
    const rh = Number(rhStr) || 1;
    const idealScale = Math.sqrt((targetMP * 1_000_000) / (rw * rh));
    let w = rw * idealScale;
    let h = rh * idealScale;
    const cap = 2048;
    if (w > cap || h > cap) {
      const over = Math.min(cap / w, cap / h);
      w *= over; h *= over;
    }
    const snap = (v) => Math.max(256, Math.min(cap, Math.round(v / 16) * 16));
    w = snap(w); h = snap(h);
    return (w * h) / 1_048_576; // mebipixels, not decimal MP
  }
  function bflImagePrice(model, resolution, refsCount = 0, ratio = '1:1') {
    const c = BFL_IMAGE_COST[model];
    if (!c) return 0;
    const mp = bflEstimatedMp(ratio, resolution);
    const hasRefs = refsCount > 0;
    if (c.textIntercept !== undefined) {
      const intercept = hasRefs ? c.editIntercept : c.textIntercept;
      const slope     = hasRefs ? c.editSlope     : c.textSlope;
      return Math.max(0, intercept + slope * mp);
    }
    return Math.max(0, c.intercept + c.slope * mp);
  }
  function estimateImageCostUsd(model, n, refsCount, resolution, ratio, klingImagePpv = 0.0035) {
    const meta = IMAGE_MODELS.find(m => m.id === model);
    if (!meta) return 0;
    if (meta.flavor === 'grok') {
      const rate = GROK_IMAGE_PER_OUTPUT_USD[model] ?? 0.02;
      const perOutput = typeof rate === 'object' ? (rate[resolution] ?? rate['1k']) : rate;
      const perRef    = GROK_IMAGE_PER_REF_USD[model]    ?? 0.002;
      const refSurcharge = (refsCount > 0) ? (GROK_IMAGE_REF_SURCHARGE_USD[model] || 0) : 0;
      return perOutput * n + perRef * (refsCount || 0) + refSurcharge;
    }
    if (meta.flavor === 'google') {
      const per = googleImagePrice(model, resolution);
      return per * n;
    }
    if (meta.flavor === 'openai') {
      const per = (OPENAI_IMAGE_COST[model] ?? 0.0527) * (OPENAI_RES_SCALE[resolution] ?? 1);
      return per * n + OPENAI_PER_REF_USD * (refsCount || 0);
    }
    if (meta.flavor === 'recraft') {
      const per = RECRAFT_IMAGE_COST[model] ?? 0;
      return per * n;
    }
    if (meta.flavor === 'bfl') {
      const per = bflImagePrice(model, resolution, refsCount, ratio);
      return per * n;
    }
    // Kling unit-based.
    const table = KLING_IMAGE_UNITS[model];
    if (!table) return 0;
    let units = 0;
    if (model === 'kling-2.1-img') {
      units = refsCount > 0 ? table['1k_ref'] : table['1k_text'];
    } else {
      units = table[resolution] ?? table['1k'] ?? 0;
    }
    return units * n * klingImagePpv;
  }

  // Re-clamp resolution and ratio when the model changes. We never use
  // 'auto' for image gen, so any stale 'auto' value snaps to 1:1 (or the
  // model's first listed ratio).
  useEffect(() => {
    if (!imgSel.resolutions.includes(imgResolution)) setImgResolution(imgSel.resolutions[0]);
    if (!imgRatios.includes(imgRatio)) setImgRatio(imgRatios.includes('1:1') ? '1:1' : imgRatios[0]);
    // Trim refs over the model's max
    if (imgRefs.length > imgSel.maxRefs) setImgRefs(r => r.slice(0, imgSel.maxRefs));
  }, [imgModel]); // eslint-disable-line

  // Add a reference image: either via picker (existing image) or upload (new file)
  const addImgRefFromBlob = useCallback(async (blobUrl, existingGcsPath) => {
    setImgRefPickerOpen(false);
    // The picker shows the user's own uploads + generated outputs - the
    // existingGcsPath is always a path the backend can read directly.
    const dims = await loadImageDims(blobUrl);
    setImgRefs(r => [...r, { blobUrl, gcsPath: existingGcsPath || null, file: null, dims }]);
  }, []);
  const addImgRefFromFile = useCallback(async (f) => {
    setImgRefPickerOpen(false);
    if (!f || !isImageFile(f)) return;
    const localUrl = URL.createObjectURL(f);
    const dims = await loadImageDims(localUrl);
    try {
      const uploaded = await api.uploadImage(f);
      setImgRefs(r => [...r, { blobUrl: localUrl, gcsPath: uploaded.gcsPath, file: null, dims }]);
    } catch (e) {
      notify('error', `Reference upload failed: ${e.message}`, 5000);
      setImgRefs(r => [...r, { blobUrl: localUrl, gcsPath: null, file: f, dims }]);
    }
  }, [notify]);
  const removeImgRef = (idx) => setImgRefs(r => r.filter((_, i) => i !== idx));
  // Multi-image add for the image-gen references grid (capped by the
  // selected model's maxRefs). Mirrors addVidRefFiles.
  const addImgRefFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(isImageFile);
    const room = (imgSel?.maxRefs || 0) - imgRefs.length;
    for (const f of files.slice(0, Math.max(0, room))) {
      await addImgRefFromFile(f);
    }
  };

  // Reference-image rule check: only Kling models enforce min-side/aspect-ratio
  // constraints on inputs. Recomputed whenever refs or model change so the
  // submit button + per-tile warning stay in sync.
  const imgRefRules = refRulesFor(imgSel);
  const imgRefErrors = imgRefs.map(r => validateRefDims(r.dims, imgRefRules));
  const imgRefsHaveErrors = imgRefErrors.some(Boolean);

  // Backfill dims for refs that arrived without them (e.g. restored from a
  // previous job before this validation existed).
  useEffect(() => {
    const missing = imgRefs.findIndex(r => r.blobUrl && !r.dims);
    if (missing < 0) return;
    let cancelled = false;
    (async () => {
      const dims = await loadImageDims(imgRefs[missing].blobUrl);
      if (cancelled || !dims) return;
      setImgRefs(rs => rs.map((r, i) => i === missing ? { ...r, dims } : r));
    })();
    return () => { cancelled = true; };
  }, [imgRefs]);

  // Submit an image-generation job
  const goImage = async () => {
    const isVectorize = imgModel === 'recraft-vectorize';
    const isVectorEdit = imgModel === 'recraft-v3-vector-edit';
    const usesSourceImage = isVectorize || isVectorEdit;
    if (usesSourceImage) {
      if (!vectorizeGcsPath && !vectorizeFile) {
        setErrMsg(isVectorize
          ? 'Please upload a source image to vectorize.'
          : 'Please upload a source image to edit.');
        return;
      }
      if (isVectorEdit && !imgPrompt.trim()) {
        setErrMsg('Please add a prompt describing the changes for the vector edit.');
        return;
      }
    } else {
      if (!imgPrompt.trim()) {
        setErrMsg('Please add a prompt describing the image you want to generate.');
        return;
      }
    }
    // The prompt box's maxLength stops typing past the cap but doesn't trim a
    // longer prompt carried over from a model with a higher limit.
    const imgLim = imagePromptLimit(imgSel).prompt;
    if (!isVectorize && imgPrompt.length > imgLim) {
      setErrMsg(`This model accepts prompts up to ${imgLim.toLocaleString()} characters; yours is ${imgPrompt.length.toLocaleString()}. Shorten it before submitting.`);
      return;
    }
    if (creditBalance <= 0) {
      setErrMsg('You need credits to generate images. Buy credits in the Credits page.');
      return;
    }
    if (!usesSourceImage && imgRefsHaveErrors) {
      const firstBad = imgRefErrors.findIndex(Boolean);
      setErrMsg(`Reference ${firstBad + 1}: ${imgRefErrors[firstBad]} Replace or remove it before submitting.`);
      return;
    }
    setErrMsg(null);
    setImgStage('sub');
    try {
      let result;
      if (isVectorize) {
        // Upload the source if it's a fresh File (not yet on GCS).
        let srcPath = vectorizeGcsPath;
        if (!srcPath && vectorizeFile) {
          const up = await api.uploadImage(vectorizeFile);
          srcPath = up.gcsPath;
          setVectorizeGcsPath(srcPath);
          setVectorizeFile(null);
        }
        result = await api.submitJob({
          outputType: 'image',
          inputMode: 'image2vector',
          model: imgModel,
          imageGcsPath: srcPath,
          recraftSvgCompression: !!recraftSvgCompression,
          recraftMaxNumShapes: (Number(recraftMaxNumShapes) >= 100 || !recraftMaxNumShapes) ? null : Number(recraftMaxNumShapes),
          });
      } else if (isVectorEdit) {
        // V3 Vector Edit: source image + prompt + strength → SVG.
        let srcPath = vectorizeGcsPath;
        if (!srcPath && vectorizeFile) {
          const up = await api.uploadImage(vectorizeFile);
          srcPath = up.gcsPath;
          setVectorizeGcsPath(srcPath);
          setVectorizeFile(null);
        }
        result = await api.submitJob({
          outputType: 'image',
          inputMode: 'image2vector',
          model: imgModel,
          imageGcsPath: srcPath,
          prompt: imgPrompt,
          negativePrompt: imgNegPrompt,
          n: imgN,
          recraftStrength: Math.max(0, Math.min(1, Number(recraftStrength))),
          });
      } else {
        // Upload any refs that haven't been uploaded yet.
        const refPaths = [];
        for (const r of imgRefs) {
          if (r.gcsPath) { refPaths.push(r.gcsPath); continue; }
          if (r.file) {
            const up = await api.uploadImage(r.file);
            refPaths.push(up.gcsPath);
          }
        }
        const inputMode = refPaths.length > 0 ? 'ref2image' : 'text2image';
        // Optional knobs - sent unconditionally; the backend persists them
        // only when set, and provider submit functions ignore unknown fields.
        const extras = {};
        if (imgSel.supportsSeed && imgSeed) extras.seed = imgSeed;
        if (imgSel.supportsRaw) extras.raw = imgRaw;
        if (imgSel.supportsImagePromptStrength && refPaths.length > 0) extras.imagePromptStrength = imgImagePromptStrength;
        if (imgSel.supportsLatestVariant) extras.useLatestEndpoint = imgUseLatest;
        result = await api.submitJob({
          outputType: 'image',
          inputMode,
          model: imgModel,
          prompt: imgPrompt,
          negativePrompt: imgNegPrompt,
          aspectRatio: imgRatio,
          imageResolution: imgResolution,
          n: imgN,
          referenceImageGcsPaths: refPaths,
          ...extras,
          });
      }
      setImgNewestJobId(result.jobId);
      setImgStage('done');
      setImgRefreshKey(k => k + 1);
      // Smooth-scroll to the new job card once it lands in the DOM.
      // The image-job list refresh is async, so poll briefly until the
      // element appears (matches the video-side scroll in goVideo).
      const scrollToJob = (attempts = 0) => {
        const el = document.querySelector(`[data-job-id="${result.jobId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (attempts < 50) {
          setTimeout(() => scrollToJob(attempts + 1), 100);
        }
      };
      scrollToJob();
      setTimeout(() => setImgStage('idle'), 1500);
    } catch (e) {
      setImgStage('idle');
      setErrMsg(e.message || 'Image generation failed');
    }
  };
  const goImageRef = useRef();
  goImageRef.current = goImage;

  // Default-load the most recent image-gen job's settings into the image
  // panel state on first mount. Mirror of handleJobsReady but for image.
  // Holds imgSettingsLoaded false until refs finish hydrating so the
  // PageLoader keeps the panel hidden through the full restore.
  const handleImageJobsReady = useCallback((jobs) => {
    if (imgSettingsLoaded) return;
    // A reuse has already populated the form - skip the most-recent-job
    // auto-restore so the reused settings aren't overwritten.
    if (reuseConsumedRef.current) { setImgSettingsLoaded(true); return; }
    // Respect the "always preload last used settings" pref (see handleJobsReady).
    if (!prefsFetched) return;
    if (!alwaysPreload) { setImgSettingsLoaded(true); return; }
    if (!jobs || jobs.length === 0) { setImgSettingsLoaded(true); return; }
    const job = jobs[0]; // most recent image-gen job
    if (job.model) {
      const meta = IMAGE_MODELS.find(m => m.id === job.model);
      if (meta) setImgModel(job.model);
    }
    if (job.prompt !== undefined) setImgPrompt(job.prompt || '');
    if (job.negativePrompt !== undefined) setImgNegPrompt(job.negativePrompt || '');
    if (job.aspectRatio) setImgRatio(job.aspectRatio);
    if (job.imageResolution) setImgResolution(job.imageResolution);
    if (job.n) setImgN(Math.min(4, Math.max(1, Number(job.n))));
    const hydrators = [];
    if (Array.isArray(job.referenceImageGcsPaths) && job.referenceImageGcsPaths.length > 0) {
      setImgRefs([]);
      setImgRefsRestoring(job.referenceImageGcsPaths.length);
      hydrators.push((async () => {
        try {
          const items = await restoreRefsFromGcsPaths(job.referenceImageGcsPaths);
          setImgRefs(items);
        } catch {}
        setImgRefsRestoring(0);
      })());
    }
    // Restore Vectorize / V3 Vector Edit source image so the preview
    // zone isn't empty when the user lands on a reused-recently-rasterized
    // pick. Mirrors the start-frame restore the video panel does.
    const usesSourceImage = job.model === 'recraft-vectorize' || job.model === 'recraft-v3-vector-edit';
    if (usesSourceImage && job.imageGcsPath) {
      setVectorizeGcsPath(job.imageGcsPath);
      setVectorizeFile(null);
      setVectorizeError(null);
      const imgUrl = api.getImageUrl(job.id);
      hydrators.push((async () => {
        try {
          const user = auth.currentUser;
          if (!user) return;
          const token = await user.getIdToken();
          const res = await fetch(imgUrl, { headers: { Authorization: `Bearer ${token}` } });
          if (!res.ok) return;
          const blob = await res.blob();
          setVectorizeSrc(prev => { try { if (prev) URL.revokeObjectURL(prev); } catch {} return URL.createObjectURL(blob); });
        } catch {}
      })());
    }
    if (hydrators.length === 0) { setImgSettingsLoaded(true); return; }
    Promise.all(hydrators).finally(() => setImgSettingsLoaded(true));
  }, [imgSettingsLoaded, prefsFetched, alwaysPreload]); // eslint-disable-line

  // Insert a token (e.g. "<<<image_2>>>") at the current cursor position of
  // the given textarea, falling back to appending. Updates the controlled
  // prompt state via setValue, then restores selection after the React
  // re-render.
  const insertAtCaret = (taRef, value, setValue, token) => {
    const ta = taRef.current;
    const start = ta?.selectionStart ?? value.length;
    const end   = ta?.selectionEnd   ?? value.length;
    const before = value.slice(0, start);
    const after  = value.slice(end);
    const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
    const needsSpaceAfter  = after.length > 0  && !/^\s/.test(after);
    const insertion = `${needsSpaceBefore ? ' ' : ''}${token}${needsSpaceAfter ? ' ' : ''}`;
    const next = `${before}${insertion}${after}`;
    setValue(next);
    // Refocus and place caret right after the inserted token on next tick.
    setTimeout(() => {
      if (!ta) return;
      const caret = before.length + insertion.length;
      ta.focus();
      try { ta.setSelectionRange(caret, caret); } catch {}
    }, 0);
  };

  const BtnIcon = () => {
    if (stage==="up")   return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>;
    if (stage==="sub")  return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>;
    if (stage==="done") return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
    return <svg width="28" height="28" viewBox="0 0 1200 1200" fill="currentColor" stroke="currentColor" strokeWidth="30" strokeLinejoin="round"><path d="m737.34 59.625c0.98438-5.7656 9.3281-5.7656 10.312 0 7.5938 43.547 41.672 77.625 85.219 85.219 5.7656 0.98438 5.7656 9.3281 0 10.312-43.547 7.5938-77.625 41.672-85.219 85.219-0.98438 5.7656-9.3281 5.7656-10.312 0-7.5938-43.547-41.672-77.625-85.219-85.219-5.7656-0.98438-5.7656-9.3281 0-10.312 43.547-7.5938 77.625-41.672 85.219-85.219zm239.21 112.22c-1.7812-10.125-16.312-10.125-18.094 0-13.266 76.172-72.938 135.84-149.11 149.11-10.125 1.7812-10.125 16.312 0 18.094 76.172 13.266 135.84 72.938 149.11 149.11 1.7812 10.125 16.312 10.125 18.094 0 13.266-76.172 72.938-135.84 149.11-149.11 10.125-1.7812 10.125-16.312 0-18.094-76.172-13.266-135.84-72.938-149.11-149.11zm-384.05 135.66c12.422 0 22.5 10.078 22.5 22.5s-10.078 22.5-22.5 22.5h-390c-37.266 0-67.5 30.234-67.5 67.5v480c0 37.266 30.234 67.5 67.5 67.5h780c37.266 0 67.5-30.234 67.5-67.5v-240c0-12.422 10.078-22.5 22.5-22.5s22.5 10.078 22.5 22.5v240c0 62.109-50.391 112.5-112.5 112.5h-780c-62.109 0-112.5-50.391-112.5-112.5v-480c0-62.109 50.391-112.5 112.5-112.5zm-64.688 192.24c-35.016-19.219-77.812 6.0938-77.812 45.984v228.56c0 39.891 42.797 65.203 77.812 45.984l207.74-114.28c36.234-19.922 36.234-72.047 0-91.969zm-32.812 45.984c0-5.6719 6.0938-9.2812 11.109-6.5625l207.74 114.28c5.2031 2.8594 5.2031 10.266 0 13.125l-207.74 114.28c-5.0156 2.7188-11.109-0.89062-11.109-6.5625z" fillRule="evenodd"/></svg>;
  };

  const handleDropZoneClick = () => {
    setPickerTarget('start');
    if (img) {
      // Already has an image — open file picker to replace
      ref.current?.click();
    } else {
      // No image — open the image picker drawer
      setPickerOpen(true);
    }
  };
  const handleEndDropZoneClick = () => {
    setPickerTarget('end');
    if (endImg) {
      endRef.current?.click();
    } else {
      setPickerOpen(true);
    }
  };

  // Helper: turn a stored GCS path (uploaded image OR generated output) into
  // a blob URL we can show in <img>. The path determines which streaming
  // endpoint we use.
  const fetchRefBlobFromGcsPath = async (gcsPath) => {
    if (!gcsPath) return null;
    let url = null;
    let m = gcsPath.match(/^outputs\/[^/]+\/([^/]+)\/(\d+)\./);
    if (m) {
      url = api.getOutputImageUrl(m[1], parseInt(m[2], 10));
    } else {
      m = gcsPath.match(/^images\/[^/]+\/([^/]+)\./);
      if (m) url = api.getImageByIdUrl(m[1]);
    }
    if (!url) return null;
    try {
      const user = auth.currentUser;
      if (!user) return null;
      const token = await user.getIdToken();
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch { return null; }
  };
  const restoreRefsFromGcsPaths = async (paths) => {
    const items = [];
    for (const p of paths || []) {
      const blobUrl = await fetchRefBlobFromGcsPath(p);
      const dims = await loadImageDims(blobUrl);
      items.push({ blobUrl, gcsPath: p, file: null, dims });
    }
    return items;
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) return;
    try {
      await api.saveGeneratePreset({
        name: presetName.trim(),
        mode: 'video',
        prompt, negativePrompt: negPrompt,
        model, duration: dur, aspectRatio: ratio, audio, cfgScale,
        inputMode: vidInputMode,
        referenceImageGcsPaths: vidRefs.map(r => r.gcsPath).filter(Boolean),
        startFrameGcsPath: gcsPath || null,
        endFrameGcsPath: endGcsPath || null,
      });
      notify("success", `Saved "${presetName.trim()}"`);
      setPresetPanel(null);
    } catch (e) { notify("error", e.message); }
  };

  const handleLoadPreset = async (p) => {
    if (p.prompt !== undefined) setPrompt(p.prompt || "");
    if (p.negativePrompt !== undefined) setNegPrompt(p.negativePrompt || "");
    if (p.model && MODELS.find(m => m.id === p.model)) setModel(p.model);
    if (p.duration) {
      const targetModel = (p.model && MODELS.find(m => m.id === p.model)) || sel;
      const cap = targetModel?.maxDur ?? 15;
      const lo  = targetModel?.minDur ?? 1;
      setDur(Math.max(lo, Math.min(cap, Number(p.duration))));
    }
    if (p.aspectRatio) {
      const isGrok = p.model && p.model.startsWith('grok-');
      if (isGrok && p.aspectRatio === 'auto') {
        setRatio(closestRatio(imgNatW, imgNatH));
      } else {
        setRatio(p.aspectRatio);
      }
    }
    if (p.audio !== undefined) setAudio(!!p.audio);
    if (p.cfgScale !== undefined) setCfgScale(p.cfgScale);
    if (p.inputMode) setVidInputMode(p.inputMode === 'text2video' ? 'ref2video' : p.inputMode);
    if (Array.isArray(p.referenceImageGcsPaths) && p.referenceImageGcsPaths.length > 0) {
      setVidRefs([]);
      setVidRefsRestoring(p.referenceImageGcsPaths.length);
      try {
        const items = await restoreRefsFromGcsPaths(p.referenceImageGcsPaths);
        setVidRefs(items);
      } finally {
        setVidRefsRestoring(0);
      }
    }
    // Restore the start / end frame source images the preset captured.
    // imgNatW/H (and the end-frame equivalents) derive from a useEffect on
    // img / endImg, so we only set the gcsPath + blob here.
    if (p.startFrameGcsPath) {
      setGcsPath(p.startFrameGcsPath);
      const blobUrl = await fetchRefBlobFromGcsPath(p.startFrameGcsPath);
      if (blobUrl) { setImg(blobUrl); setFile(null); }
    }
    if (p.endFrameGcsPath) {
      setEndGcsPath(p.endFrameGcsPath);
      const blobUrl = await fetchRefBlobFromGcsPath(p.endFrameGcsPath);
      if (blobUrl) { setEndImg(blobUrl); setEndFile(null); }
    }
    notify("success", `Loaded "${p.name}"`);
    setPresetPanel(null);
  };

  const handleDeletePreset = async (id, name) => {
    try {
      await api.deleteGeneratePreset(id);
      setPresets(prev => prev.filter(p => p.id !== id));
      notify("success", `Deleted "${name}"`);
    } catch (e) { notify("error", e.message); }
  };

  // ─── Image-mode preset handlers (mirror the video ones) ─────────────────────
  const handleSaveImagePreset = async () => {
    if (!imgPresetName.trim()) return;
    try {
      await api.saveGeneratePreset({
        name: imgPresetName.trim(),
        mode: 'image',
        prompt: imgPrompt, negativePrompt: imgNegPrompt,
        model: imgModel, aspectRatio: imgRatio,
        imageResolution: imgResolution, n: imgN,
        inputMode: imgRefs.length > 0 ? 'ref2image' : 'text2image',
        referenceImageGcsPaths: imgRefs.map(r => r.gcsPath).filter(Boolean),
      });
      notify("success", `Saved "${imgPresetName.trim()}"`);
      setImgPresetPanel(null);
    } catch (e) { notify("error", e.message); }
  };

  const handleLoadImagePreset = async (p) => {
    if (p.prompt !== undefined) setImgPrompt(p.prompt || "");
    if (p.negativePrompt !== undefined) setImgNegPrompt(p.negativePrompt || "");
    if (p.model) {
      const meta = IMAGE_MODELS.find(m => m.id === p.model);
      if (meta) setImgModel(p.model);
    }
    if (p.aspectRatio) setImgRatio(p.aspectRatio);
    if (p.imageResolution) setImgResolution(p.imageResolution);
    if (p.n) setImgN(Math.min(4, Math.max(1, Number(p.n))));
    if (Array.isArray(p.referenceImageGcsPaths) && p.referenceImageGcsPaths.length > 0) {
      setImgRefs([]);
      setImgRefsRestoring(p.referenceImageGcsPaths.length);
      try {
        const items = await restoreRefsFromGcsPaths(p.referenceImageGcsPaths);
        setImgRefs(items);
      } finally {
        setImgRefsRestoring(0);
      }
    } else {
      setImgRefs([]);
    }
    notify("success", `Loaded "${p.name}"`);
    setImgPresetPanel(null);
  };

  const handleDeleteImagePreset = async (id, name) => {
    try {
      await api.deleteGeneratePreset(id);
      setImgPresets(prev => prev.filter(p => p.id !== id));
      notify("success", `Deleted "${name}"`);
    } catch (e) { notify("error", e.message); }
  };

  const presetPanelJsx = presetPanel ? (
    <>
      <div style={{position:"fixed",inset:0,zIndex:30}} onClick={()=>setPresetPanel(null)}/>
      <div style={{position:"absolute",top:"100%",right:0,zIndex:51,width:320,marginTop:6,background:T.popoverBg,border:`1px solid ${T.border}`,borderRadius:14,padding:16,boxShadow:themeMode==='light'?"0 8px 24px rgba(20,40,40,0.10)":"0 12px 40px rgba(0,0,0,0.5)"}}>
        {presetPanel === "save" ? (
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.text2,marginBottom:12}}>Save Current Settings</div>
            <input value={presetName} onChange={e=>setPresetName(e.target.value)}
              placeholder="Preset name"
              onKeyDown={e=>e.key==="Enter"&&handleSavePreset()}
              style={{width:"100%",padding:"10px 12px",borderRadius:10,background:T.media,border:`1px solid ${T.overlay12}`,color:T.text,fontSize:13,outline:"none",fontFamily:"inherit",marginBottom:12,boxSizing:"border-box"}}/>
            <button onClick={handleSavePreset} disabled={!presetName.trim()}
              style={{padding:"8px 20px",borderRadius:10,background:presetName.trim()?Y:T.overlay12,border:"none",color:presetName.trim()?T.dark:T.text3,fontWeight:700,fontSize:12,cursor:presetName.trim()?"pointer":"not-allowed",display:"flex",alignItems:"center",gap:6}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Save
            </button>
          </div>
        ) : (
          <div>
            <div style={{fontSize:12,fontWeight:700,color:T.text2,marginBottom:12}}>Load Settings</div>
            {presets.length === 0 ? (
              <div style={{color:T.text3,fontSize:12,padding:"12px 0",textAlign:"center"}}>No saved presets yet</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:300,overflowY:"auto"}}>
                {presets.map(p => (
                  <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,cursor:"pointer",transition:"background 0.15s",background:T.media}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}
                    onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.02)"}>
                    <div onClick={()=>handleLoadPreset(p)} style={{flex:1,minWidth:0}}>
                      <div style={{color:T.text,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                      <div style={{color:T.text3,fontSize:10}}>{p.model} · {p.duration}s · {p.aspectRatio}</div>
                    </div>
                    <button onClick={(e)=>{e.stopPropagation();handleDeletePreset(p.id,p.name);}} title="Delete preset"
                      style={{background:"transparent",border:"none",color:T.text3,cursor:"pointer",padding:2,display:"flex",flexShrink:0,transition:"color 0.15s"}}
                      onMouseEnter={e=>e.currentTarget.style.color=T.danger} onMouseLeave={e=>e.currentTarget.style.color=T.text3}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  ) : null;

  // Audio + Duration row — extracted so it can render either below the image
  // drops (default) or as part of a column to the right of the start frame
  // when the model has no end frame slot.
  const audioDurationBlock = (
    <div data-tutorial="duration" style={{display:"flex",gap:24,flexWrap:"wrap",alignItems:"flex-start"}}>
      {/* Audio */}
      {/* Grok and Google Veo (Standard / Fast) bundle audio with no
          per-call toggle, so we render a static checkmark instead of
          the on/off switch. Veo Lite has no audio and falls through to
          the !sel.audio branch entirely. */}
      {sel?.grok || (sel?.google && sel?.audio) || sel?.audioFixed ? (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{color:iconC,display:"flex"}} title="Audio">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
            </span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",flexShrink:0,position:"relative",top:1}}>Audio</span>
          </div>
          <div style={{height:30,display:"flex",alignItems:"center"}}>
            <span title="Audio included" style={{display:"flex"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>
          </div>
        </div>
      ) : supportsAudio(sel) ? (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{color:iconC,display:"flex"}} title="Audio">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
            </span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",flexShrink:0,position:"relative",top:1}}>Audio</span>
          </div>
          <div style={{height:30,display:"flex",alignItems:"center"}}>
            <button onClick={()=>setAudio(a=>!a)} title={audio?"Disable audio":"Enable audio"}
              style={{width:36,height:20,borderRadius:10,background:audio?Y:T.text3,border:"none",cursor:"pointer",position:"relative",transition:"all 0.2s",flexShrink:0}}>
              <div style={{position:"absolute",top:2,left:audio?18:2,width:16,height:16,borderRadius:"50%",background:audio?"#0C0E0F":T.dark,transition:"all 0.2s"}}/>
            </button>
          </div>
        </div>
      ) : null}

      {/* Duration */}
      <div style={{flex:1,minWidth:120}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <span style={{color:iconC,display:"flex"}} title="Duration">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="13" r="8"/><polyline points="12 9 12 13 14.5 15.5"/><line x1="9" y1="2" x2="15" y2="2"/></svg>
          </span>
          <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",flexShrink:0,position:"relative",top:1}}>Duration</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,height:30}}>
          {(v3sel || sel?.grok || sel?.geminiOmni) ? (
            <>
              <RangeSlider min={sel.minDur} max={effectiveMaxDuration(sel.id, vidInputMode)} step={sel.durStep || 1} value={dur}
                onChange={e=>setDur(parseInt(e.target.value))}
                title={`${dur}s`}/>
              <span style={{color:T.text2,fontSize:12,fontFamily:"monospace",minWidth:28,textAlign:"right"}}>{dur}s</span>
            </>
          ) : (
            // Discrete-duration models (fixed-dur, Veo's 4/6/8) render
            // as pills. Derived from the model so adding a new
            // discrete-duration model only needs the right fields on
            // the catalog entry, not changes here.
            (() => {
              const veoLocked = veoLocksDur8(
                sel?.id,
                vidInputMode,
                vidRefs.length > 0,
                !!(endGcsPath || endImg),
              );
              let durs;
              if (veoLocked) {
                // Veo refs / last-frame: server forces 8s, collapse to a single pill.
                durs = [8];
              } else if (sel?.fixedDur) {
                durs = [sel.fixedDur];
              } else if (sel?.durStep) {
                const lo = sel.minDur ?? 4, hi = sel.maxDur ?? 8, step = sel.durStep;
                durs = [];
                for (let d = lo; d <= hi; d += step) durs.push(d);
              } else {
                durs = [];
                if (sel?.p5  != null) durs.push(5);
                if (sel?.p10 != null) durs.push(10);
                if (durs.length === 0) durs = [5, 10];
              }
              return durs.map(d => (
                <Pill key={d} active={dur===d} onClick={()=>setDur(d)} title={`${d} seconds`} w={52}>{d}s</Pill>
              ));
            })()
          )}
        </div>
      </div>
    </div>
  );

  // Category -> color for the placeholder tile shown behind/instead of the
  // real cover. Kling's official cover thumbnails are scraped into the
  // registry (effect.coverUrl); when one is missing or fails to load we
  // fall back to this category-tinted initial tile.
  const EFFECT_CATEGORY_COLORS = {
    holiday:         '#E74C3C',
    halloween:       '#E67E22',
    lunar_new_year:  '#F1C40F',
    dance:           '#E91E63',
    pet:             '#1ABC9C',
    fantasy:         '#9B59B6',
    transform:       '#5C6BC0',
    duo_social:      '#FF7AB6',
    sports:          '#3498DB',
    photo_style:     '#A1887F',
    celebration:     '#F39C12',
    other:           '#7F8C8D',
  };
  const effectCategoryColor = (cat) => EFFECT_CATEGORY_COLORS[cat] || EFFECT_CATEGORY_COLORS.other;
  // Effect tile - tiny self-hosted webp thumbnail (generated by
  // katana-backend/scripts/build-effect-thumbs.js, served same-origin
  // from /effect-thumbs/, immutable-cached). Hot-linking Kling's CDN
  // made the grid crawl; these load instantly. A category-tinted
  // initial tile is the base layer and shows through if the thumb is
  // missing (e.g. pet_vlogger). Used by the chip + picker rows.
  const EffectTile = ({ eff, size = 36 }) => {
    if (!eff) return null;
    const c = effectCategoryColor(eff.category);
    const letter = (eff.label || eff.scene || '?').trim()[0].toUpperCase();
    return (
      <div style={{
        width: size, height: size, flexShrink: 0,
        borderRadius: 6, background: c,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontWeight: 800, fontSize: Math.round(size * 0.45),
        fontFamily: LABEL_FONT, lineHeight: 1,
        position: 'relative', overflow: 'hidden',
      }}>{letter}<img src={`/effect-thumbs/${eff.scene}.webp`} alt="" loading="lazy"
          style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}
          onError={e=>{ e.currentTarget.style.display='none'; }}/>{eff.type === 'dual' && (
        <span style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          fontSize: Math.max(7, Math.round(size * 0.16)),
          fontWeight: 700, letterSpacing: 0.5,
          padding: '1px 2px', textAlign: 'center', zIndex: 1,
        }}>DUO</span>
      )}</div>
    );
  };

  // Aspect-ratio picker. Extracted to a const so it can render in its
  // usual slot for normal video, OR be repositioned between the effect
  // control and the image box for Kling Effects (where we crop the
  // input on our end before submitting).
  const aspectRatioBlock = (
    <div data-tutorial="aspect-ratio">
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <span style={{color:iconC,display:"flex",flexShrink:0}} title="Aspect ratio">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        </span>
        <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",flexShrink:0,position:"relative",top:1}}>Aspect Ratio</span>
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {videoSupportsAutoRatio(model, vidInputMode) && (
          <Pill active={ratio==="auto"} onClick={()=>setRatio("auto")} title="Auto aspect ratio (from image)">
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,position:"relative",top:1}}>Auto</span>
          </Pill>
        )}
        {getVideoRatios(model, vidInputMode).map(r => (
          <Pill key={r.val} active={ratio===r.val} onClick={()=>setRatio(r.val)} title={r.title}>
            <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                {RATIO_SHAPES[r.val]}
              </svg>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,position:"relative",top:1}}>{r.val}</span>
            </span>
          </Pill>
        ))}
      </div>
      {isEffectsModel && (
        <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
          We crop your image to this ratio before applying the effect. Auto keeps the original framing.
        </div>
      )}
    </div>
  );

  // Inline "Kling Effects" panel - description, current selection chip,
  // and "Browse Effects" button that opens the picker drawer. Sits in
  // the same slot as audio/duration for non-effects flows.
  const effectsBlock = (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <span style={{color:iconC,display:'flex',flexShrink:0}} title="Kling Effect">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
        </span>
        <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',flexShrink:0,position:'relative',top:1}}>Kling Effect</span>
      </div>
      <div style={{color:T.text3,fontSize:11,marginBottom:10,lineHeight:1.55}}>
        Kling Effects apply one of {KLING_EFFECTS.length} pre-designed visual scenes to your image(s) and emit a 720p video with audio. The effect scene fixes duration / CFG / prompt (no references); pick an aspect ratio below and we crop your image to it before applying the effect. Each effect has its own price (<CreditIcon size={10} color={CREDIT}/>{' '}{ceilCredits(KLING_EFFECT_PRICE_MIN_USD).toLocaleString()} - {ceilCredits(KLING_EFFECT_PRICE_MAX_USD).toLocaleString()} credits) which replaces the normal model cost. Dual-image effects need two portrait inputs; single-image effects need one.
      </div>
      {klingEffectObj ? (
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:10,border:`1px solid ${T.optionBorderSelected}`,background:T.optionBgSelected}}>
          <EffectTile eff={klingEffectObj} size={40}/>
          <div style={{display:'flex',flexDirection:'column',gap:2,flex:1,minWidth:0}}>
            <span style={{color:Y,fontSize:13,fontWeight:700,lineHeight:1.25,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{klingEffectObj.label}</span>
            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:T.text2}}>
              <span style={{fontFamily:'monospace'}}>{klingEffectObj.scene}</span>
              <span style={{color:T.text3}}>·</span>
              <span style={{display:'inline-flex',alignItems:'center',gap:3,color:CREDIT,fontWeight:700}}>
                <CreditIcon size={11} color={CREDIT}/>{ceilCredits(klingEffectObj.priceUsd).toLocaleString()}
              </span>
              <span style={{color:T.text3}}>·</span>
              <span style={{fontFamily:LABEL_FONT,fontSize:9,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:T.text3}}>{klingEffectObj.type === 'dual' ? 'Dual Image' : 'Single Image'}</span>
            </div>
          </div>
          <button onClick={()=>setEffectsPickerOpen(true)} title="Change effect"
            style={{padding:'6px 12px',borderRadius:999,background:'transparent',border:`1px solid ${T.text3}`,color:T.text2,cursor:'pointer',fontFamily:LABEL_FONT,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}>
            Change
          </button>
        </div>
      ) : (
        <button onClick={()=>setEffectsPickerOpen(true)} title="Browse Kling Effects"
          style={{width:'100%',padding:'12px 14px',borderRadius:10,border:`1.5px dashed ${T.mediaBorder}`,background:T.media,color:T.text2,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,transition:'all 0.15s'}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor=Y;e.currentTarget.style.color=Y;}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor=T.mediaBorder;e.currentTarget.style.color=T.text2;}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}>Browse {KLING_EFFECTS.length} Effects</span>
        </button>
      )}
    </div>
  );

  // Effects always emit audio (locked on, like Grok Imagine). Static
  // "Included" indicator that sits on the same row as Aspect Ratio (to its
  // right); its header mirrors the aspect-ratio header exactly so the AUDIO
  // label lines up with the ASPECT RATIO label, top-aligned.
  const effectsAudioIndicator = (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
        <span style={{color:iconC,display:'flex',flexShrink:0}} title="Audio">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
        </span>
        <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',flexShrink:0,position:'relative',top:1}}>Audio</span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <span style={{color:T.text2,fontSize:11,fontWeight:600}}>Included</span>
      </div>
    </div>
  );

  // Start + End frame block — always renders the two boxes side-by-side in
  // a 2-column grid (1-column when the model has no end-frame slot). Each
  // box is square via aspect-ratio:1/1 and shares width equally with its
  // neighbor, so they scale down together on narrow viewports rather than
  // stacking. Sits just before the prompt in image2video mode, mirroring
  // how the references grid sits before the prompt in ref2video mode.
  //
  // Kling Effects retargets the same two slots: single-image effects use
  // just the first slot, dual-image effects (kiss_pro etc.) use both. The
  // labels swap to "Image 1" / "Image 2" since "Start/End Frame" doesn't
  // describe what the effect actually consumes.
  const isDualEffect = isEffectsModel && klingEffectObj?.type === 'dual';
  const hasEndFrame = isDualEffect || supportsEndFrame(model);
  const startFrameLabel = isEffectsModel ? 'Image 1' : `Start Frame${!requiresStartFrame(model) ? ' · Optional' : ''}`;
  const endFrameLabel   = isDualEffect ? 'Image 2' : 'End Frame · Optional';
  const cropContainerStyle = {
    width: '100%', aspectRatio: '1 / 1', maxWidth: 310,
    border: undefined, borderRadius: 4,
    background: T.media,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'border-color 0.2s, background-color 0.2s',
    overflow: 'hidden', position: 'relative',
    // Inherited color for the placeholder picture-frame icons inside.
    // Dark = faint white; light = visible dark.
    color: T.iconMuted,
  };
  const framesBlock = (
    <div data-tutorial="drop-zone">
      {/* Flex (not grid 1fr 1fr) so the start + end frame boxes stay
          left-aligned and butt up against each other on wide layouts
          instead of being pushed apart by the grid columns each taking
          half the container. flex:1 1 0 + maxWidth:310 lets the pair
          shrink together on narrow viewports while capping their width
          on the desktop (matches the box's own maxWidth). */}
      <div style={{display:'flex',gap:12,alignItems:'flex-start'}}>
        {/* Start frame */}
        <div style={{flex:'1 1 0',maxWidth:310,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{color:T.text3,display:"flex"}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",position:"relative",top:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0}}>{startFrameLabel}</span>
          </div>
          <div ref={startFrameRef}
            onDragOver={e=>{e.preventDefault();setDrag(true);}}
            onDragLeave={()=>setDrag(false)}
            onDrop={e=>{e.preventDefault();setDrag(false);handleFramesFiles(e.dataTransfer.files,'start');}}
            onClick={handleDropZoneClick}
            title={startFrameError ? `Start frame: ${startFrameError}` : (img ? "Click to change starting image" : "Upload or select source image")}
            style={{...cropContainerStyle,border:`1.5px ${startFrameError?'solid':'dashed'} ${startFrameError?T.danger:(drag?Y:T.mediaBorder)}`,background:drag?Y+"08":T.media}}>
            {img ? (<>
              <img src={img} alt="Source" style={{width:"100%",height:"100%",objectFit:"contain",position:"absolute",inset:0}} onError={e=>{e.target.style.display="none";e.target.nextSibling.style.display="flex";}}/><div style={{display:"none",position:"absolute",inset:0,alignItems:"center",justifyContent:"center",flexDirection:"column",gap:6}}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.iconMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span style={{color:T.text3,fontSize:10}}>Image uploaded</span></div>
              {startFrameSize > 0 && <CropOverlay containerSize={startFrameSize} imgNatW={imgNatW} imgNatH={imgNatH} ratio={supportsRatio(model) ? ratio : "auto"} cropPos={cropPos} onCropPosChange={setCropPos}/>}
              <div style={{position:"absolute",inset:0,background:T.imageOverlay,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:10,gap:6,zIndex:3,pointerEvents:"none"}}>
                <button onClick={e=>{e.stopPropagation();setImg(null);setFile(null);setGcsPath(null);setImgNatW(0);setImgNatH(0);setCropPos(0.5);}} title="Clear image"
                  style={{background:T.dark,border:"none",color:Y,borderRadius:20,width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"auto"}}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
                </button>
                <button onClick={e=>{e.stopPropagation();setPickerTarget('start');setPickerOpen(true);}} title="Browse images"
                  style={{background:T.dark,border:"none",color:Y,borderRadius:20,width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"auto"}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                </button>
              </div>
            </>) : requiresStartFrame(model) ? (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,color:T.danger}}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",fontWeight:700}}>Start Frame Required</span>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",opacity:0.5}}>Select</span>
              </div>
            )}
            {startFrameError && (
              <div title={startFrameError}
                style={{position:'absolute',top:6,left:6,display:'flex',zIndex:4}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
            )}
            <input ref={ref} type="file" multiple accept="image/*,.heic,.heif,.avif,.webp,.tiff,.tif,.bmp" style={{display:"none"}} onChange={e=>{handleFramesFiles(e.target.files,'start');e.target.value="";}}/>
          </div>
        </div>

        {/* End frame — only when the model supports it */}
        {hasEndFrame && (
          <div style={{flex:'1 1 0',maxWidth:310,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <span style={{color:T.text3,display:"flex"}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="9 14 12 11 15 14"/><circle cx="8.5" cy="8.5" r="1.5"/></svg></span>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",position:"relative",top:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0}}>{endFrameLabel}</span>
            </div>
            <div ref={endFrameRef}
              onDragOver={e=>{e.preventDefault();setEndDrag(true);}}
              onDragLeave={()=>setEndDrag(false)}
              onDrop={e=>{e.preventDefault();setEndDrag(false);handleFramesFiles(e.dataTransfer.files,'end');}}
              onClick={handleEndDropZoneClick}
              title={endFrameError ? `End frame: ${endFrameError}` : (endImg ? "Click to change end frame" : "Upload or select end frame")}
              style={{...cropContainerStyle,border:`1.5px ${endFrameError?'solid':'dashed'} ${endFrameError?T.danger:(endDrag?Y:T.mediaBorder)}`,background:endDrag?Y+"08":T.media}}>
              {endImg ? (<>
                <img src={endImg} alt="End frame" style={{width:"100%",height:"100%",objectFit:"contain",position:"absolute",inset:0}}/>
                {endFrameSize > 0 && <CropOverlay containerSize={endFrameSize} imgNatW={endImgNatW} imgNatH={endImgNatH} ratio={supportsRatio(model) ? ratio : "auto"} cropPos={endCropPos} onCropPosChange={setEndCropPos}/>}
                <div style={{position:"absolute",inset:0,background:T.imageOverlayHeavy,display:"flex",alignItems:"flex-end",justifyContent:"center",padding:10,gap:6,zIndex:3,pointerEvents:"none"}}>
                  <button onClick={e=>{e.stopPropagation();clearEndFrame();}} title="Clear end frame"
                    style={{background:T.dark,border:"none",color:Y,borderRadius:20,width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"auto"}}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
                  </button>
                  <button onClick={e=>{e.stopPropagation();setPickerTarget('end');setPickerOpen(true);}} title="Browse images"
                    style={{background:T.dark,border:"none",color:Y,borderRadius:20,width:28,height:28,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"auto"}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  </button>
                </div>
              </>) : (
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="9 14 12 11 15 14"/><circle cx="8.5" cy="8.5" r="1.5"/></svg>
                  <span style={{fontSize:9,letterSpacing:1,textTransform:"uppercase",opacity:0.5}}>Select</span>
                </div>
              )}
              {endFrameError && (
                <div title={endFrameError}
                  style={{position:'absolute',top:6,left:6,display:'flex',zIndex:4}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
              )}
              <input ref={endRef} type="file" multiple accept="image/*,.heic,.heif,.avif,.webp,.tiff,.tif,.bmp" style={{display:"none"}} onChange={e=>{handleFramesFiles(e.target.files,'end');e.target.value="";}}/>
            </div>
          </div>
        )}
      </div>
      {framesHaveError && klingFrameRules && (
        <div style={{marginTop:8,padding:'8px 10px',borderRadius:6,border:`1px solid ${T.danger}55`,background:`${T.danger}15`,color:T.danger,fontSize:11,lineHeight:1.5}}>
          {startFrameError && <div><b>Start frame:</b> {startFrameError}</div>}
          {endFrameError   && <div><b>End frame:</b> {endFrameError}</div>}
          <div style={{marginTop:4,opacity:0.85}}>
            Kling requires each side to be at least {klingFrameRules.minSide}px and the aspect ratio to be within 1:{klingFrameRules.maxAspectMag} - {klingFrameRules.maxAspectMag}:1.
          </div>
        </div>
      )}
      {klingFrameRules && !framesHaveError && (
        <div style={{marginTop:6,color:T.text3,fontSize:11,lineHeight:1.5}}>
          Kling requires each frame to be at least {klingFrameRules.minSide}px on every side and within a 1:{klingFrameRules.maxAspectMag} - {klingFrameRules.maxAspectMag}:1 aspect ratio.
        </div>
      )}
    </div>
  );

  // Image Input Mode toggle - References vs Start / End Frame. Defined as
  // a const here so the same block can render directly above either drop
  // section (frames or refs grid) without duplicating the JSX. Styled to
  // match the other generation-settings rows (icon + label + control).
  // The actual inputMode (image2video / ref2video / text2video) is
  // inferred at submit from filled inputs - see goVideo + backend. The
  // References position is disabled for models that don't support
  // reference images; the auto-correct effect coerces vidInputMode off
  // ref2video for those models so the visual state stays consistent.
  const refsAllowed = modelSupportsInputMode(model, 'ref2video');
  const onRefsToggle = refsAllowed && vidInputMode !== 'image2video';
  // Pill style mirrors the aspect-ratio pills (./shared Pill): height 30,
  // borderRadius 20, content-width (no flex:1). Active = Y bg + dark
  // glyph; inactive = transparent with a soft border. The disabled state
  // (References locked because the model can't take refs) keeps the
  // pill shape but dims it via opacity + not-allowed cursor.
  const inputModePillStyle = (selected, disabled) => ({
    paddingLeft:10, paddingRight:10, height:30, borderRadius:20,
    background: selected ? Y : 'transparent',
    border:`1px solid ${selected ? Y : T.overlay12}`,
    color: selected ? T.dark : T.text3,
    fontWeight: selected ? 800 : 400,
    fontSize:12,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition:'all 0.15s',
    display:'flex', alignItems:'center', justifyContent:'center',
    flexShrink:0,
  });
  const inputModeToggle = (
    <div data-tutorial="input-mode">
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <span style={{color:iconC,display:"flex",flexShrink:0}} title="Image input mode">
          {/* "Images" glyph - a framed photo with a second frame behind
              it - so the input-mode label reads as being about images. */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="3" width="14" height="14" rx="2"/><circle cx="11.5" cy="7.5" r="1.5"/><path d="m21 13-3.5-3.5L12 15"/><path d="M14 17v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h2"/></svg>
        </span>
        <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",flexShrink:0,position:"relative",top:1}}>Image Input Mode</span>
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        <button type="button" disabled={!refsAllowed}
          onClick={()=>setVidInputMode('ref2video')}
          title={refsAllowed ? 'Drive the video from reference images' : "This model doesn't support reference images"}
          style={inputModePillStyle(onRefsToggle, !refsAllowed)}>
          <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="7" width="14" height="14" rx="2"/><path d="M3 17V5a2 2 0 0 1 2-2h12"/></svg>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,position:"relative",top:1}}>References</span>
          </span>
        </button>
        <button type="button"
          onClick={()=>setVidInputMode('image2video')}
          title="Drive the video from a start frame (and optional end frame)"
          style={inputModePillStyle(!onRefsToggle, false)}>
          <span style={{display:"inline-flex",alignItems:"center",gap:5}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 3v18"/><path d="M3 7.5h4"/><path d="M3 12h18"/><path d="M3 16.5h4"/><path d="M17 3v18"/><path d="M17 7.5h4"/><path d="M17 16.5h4"/></svg>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,position:"relative",top:1}}>Start / End Frame</span>
          </span>
        </button>
      </div>
      {!refsAllowed && (
        <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
          {sel ? sel.label : 'This model'} doesn't support reference images - locked to Start / End Frame.
        </div>
      )}
    </div>
  );

  // Prompt block — extracted so it can render either below the image drops
  // (default) or as part of a column to the right of the start frame.
  // Quick-link pills above the textarea insert <<<image_N>>> tokens at the
  // cursor for Omni models that accept that reference syntax.
  const showVidRefPills = supportsRefToken(model) && vidInputMode === 'ref2video' && vidRefs.length > 0;
  const promptBlock = (
    <div data-tutorial="prompt">
      <div style={{marginBottom:5,color:T.text3,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span title="Prompt" style={{display:"flex"}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></span>
          <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",position:"relative",top:1}}>Prompt</span>
        </div>
        {prompt && <CopyBtn text={prompt}/>}
      </div>
      {showVidRefPills && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:6}}>
          {vidRefs.map((_, idx) => {
            const c = refColor(idx);
            const tok = refTokenFor(model, idx);
            const lbl = refLabelFor(model, idx);
            if (!tok) return null;
            return (
              <button key={idx} type="button"
                onClick={()=>insertAtCaret(promptTaRef, prompt, setPrompt, tok)}
                title={`Insert ${tok}`}
                style={{padding:'3px 8px',borderRadius:6,border:`1px solid ${c}`,background:`${c}20`,color:c,cursor:'pointer',fontSize:11,fontWeight:700,fontFamily:'monospace',display:'inline-flex',alignItems:'center',gap:4}}>
                <span style={{width:8,height:8,borderRadius:2,background:c}}/>
                {lbl}
              </button>
            );
          })}
        </div>
      )}
      <FocusTextarea ref={promptTaRef} value={prompt} maxLength={videoPromptLimit(model).prompt} onChange={e=>setPrompt(e.target.value)} title="Prompt"
        style={{...inp,resize:"none",lineHeight:1.7,fontSize:14,width:"100%",height:38}}/>
      <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
        Describe the scene including subjects, environment, and any action or camera motion you want.
      </div>
    </div>
  );

  // Always render audio/duration and prompt as their own rows below the
  // image area. The previous "fold next to start frame" behavior put aspect
  // ratio AFTER the prompt for end-frame-less models (incl. Grok), which
  // broke the canonical Kling-Pro layout: start image -> aspect ratio ->
  // audio + duration -> prompt. Keeping it false unifies the flow.
  const foldNextToStart = false;

  const ImgBtnIcon = () => {
    if (imgStage==="sub")  return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>;
    if (imgStage==="done") return <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
    return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
  };

  // ─── Render: image mode panel ──────────────────────────────────────────────
  function renderImageMode() {
    const imgBusy = imgStage === 'sub';
    const isVectorize  = imgModel === 'recraft-vectorize';
    const isVectorEdit = imgModel === 'recraft-v3-vector-edit';
    // Both vectorize and vector-edit use the same source-image zone.
    // Vector edit ALSO requires a prompt; vectorize doesn't take one.
    const usesSourceImage = isVectorize || isVectorEdit;
    const noPrompt = !imgPrompt || !imgPrompt.trim();
    const noVectorizeSrc = usesSourceImage && !vectorizeGcsPath && !vectorizeFile;
    const cantGen  = imgBusy
      || (isVectorize  ? (noVectorizeSrc || !!vectorizeError) : false)
      || (isVectorEdit ? (noVectorizeSrc || !!vectorizeError || noPrompt) : false)
      || (!usesSourceImage ? noPrompt : false)
      || creditBalance <= 0
      || (!usesSourceImage && imgRefsHaveErrors)
      || atMaxJobs;
    // Image-specific affordability for the generate row. When the user
    // can't cover THIS image's cost, the row's right slot becomes a Buy
    // Credits button instead of Generate (keeps the narrow row from
    // crowding). Uses the image cost, not the shared video `price`.
    const imgCostCredits = ceilCredits(estimateImageCostUsd(imgModel, imgN, imgRefs.length, imgResolution, imgRatio, studioKlingImagePpv));
    const imgCannotAfford = creditBalance <= 0 || imgCostCredits > creditBalance;
    const refsAtMax = imgRefs.length >= imgSel.maxRefs;

    const clearVectorizeSrc = () => {
      try { if (vectorizeSrc) URL.revokeObjectURL(vectorizeSrc); } catch {}
      setVectorizeSrc(null);
      setVectorizeFile(null);
      setVectorizeGcsPath(null);
      setVectorizeError(null);
    };

    return (
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <PageLoader ready={imgSettingsLoaded}/>
        {tutorialOpen && (
          <TutorialOverlay steps={IMAGE_STEPS} onClose={()=>setTutorialOpen(false)}/>
        )}
        {/* Mode tabs + preset Load/Save live in the page header now. */}

        {/* Model selector */}
        <div data-tutorial="image-model" style={{position:'relative'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
            <span style={{color:T.text3,display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg></span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1,flex:1}}>Image Model</span>
          </div>
          <div onClick={()=>setImgModelOpen(o=>!o)}
            style={{padding:'6px 0',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${imgModelOpen?Y:T.overlay12}`,transition:'border-color 0.15s'}}>
            <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',minWidth:0}}>
              <ModelIcon model={imgModel} s={32}/>
              <span style={{color:T.title,fontSize:narrow?18:22,fontWeight:800,whiteSpace:'nowrap'}}>{imgSel.label}</span>
              <ResPill res={imgResLabel(imgResolution)}/>
            </div>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round" style={{transform:imgModelOpen?'rotate(180deg)':'none',transition:'transform 0.2s'}}><polyline points="6 9 12 15 18 9"/></svg>
          </div>
          {imgModelOpen && <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:T.media,zIndex:30}} onClick={()=>setImgModelOpen(false)}/>}
          {imgModelOpen && (() => {
            // Admin Model Control: filter out disabled IDs and let the saved
            // recommended list (if any) drive the Recommended section.
            // Recraft vector models live alongside raster models in this
            // single list; the form below adapts to whichever model is
            // selected (vectorize hides the prompt + shows a source-image
            // zone, vector models hide the resolution selector, etc.).
            const adminDisabled = new Set(studioImageDisabled);
            const visibleModels = IMAGE_MODELS.filter(m => !adminDisabled.has(m.id));
            const defaultRecommendedIds = ['grok-imagine-image', 'kling-3.0-omni-img', 'recraft-v4-vector'];
            const recommendedIds = studioImageRecommended.length > 0
              ? studioImageRecommended.filter(id => !adminDisabled.has(id) && visibleModels.some(m => m.id === id))
              : defaultRecommendedIds.filter(id => visibleModels.some(m => m.id === id));
            // Sort key (applied to both Recommended and All Models):
            //   1. Reference-image support: 2+ refs > 1 ref > text-only
            //   2. Max available resolution descending (4k > 2k > 1k > svg/0)
            //   3. Provider: Gemini > FLUX > Kling > Grok > Recraft
            //   4. qualityRank descending (Max/Ultra > Pro > standard > Fast)
            const RES_RANK = { '4k': 3, '2k': 2, '1k': 1 };
            const maxRes = (m) => Math.max(...m.resolutions.map(r => RES_RANK[r] || 0));
            const flavorRank = (m) =>
              m.id.startsWith('google-')  ? 0 :
              m.id.startsWith('openai-')  ? 0.5 :
              m.id.startsWith('bfl-')     ? 1 :
              m.id.startsWith('grok-')    ? 3 :
              m.id.startsWith('recraft-') ? 4 :
              2; // kling + its sub-flavors (omni / gen / multi)
            const refTier = (m) => {
              const r = m.maxRefs || 0;
              if (r >= 2) return 2;
              if (r >= 1) return 1;
              // No refs slot, but the model still consumes a source image
              // via a separate input field (e.g. Recraft Vectorize: raster
              // → SVG; Recraft Vector Edit: raster + prompt → SVG). Treat
              // as single-image tier so they don't get buried with pure
              // text-to-image models.
              const it = m.inputType || '';
              if (it.includes('image')) return 1;
              return 0;
            };
            const sortModels = (list) => [...list].sort((a, b) => {
              const rt = refTier(b) - refTier(a);
              if (rt !== 0) return rt;
              const ra = maxRes(a), rb = maxRes(b);
              if (ra !== rb) return rb - ra;
              const fa = flavorRank(a), fb = flavorRank(b);
              if (fa !== fb) return fa - fb;
              return (b.qualityRank || 0) - (a.qualityRank || 0);
            });
            const recommended = sortModels(recommendedIds.map(id => visibleModels.find(m => m.id === id)).filter(Boolean));
            const others = sortModels(visibleModels.filter(m => !recommendedIds.includes(m.id)));
            const renderCard = (m) => (
              <div key={m.id} onClick={()=>{setImgModel(m.id);setImgModelOpen(false);}}
                title={m.label}
                style={{padding:'8px 10px',cursor:'pointer',borderRadius:10,border:imgModel===m.id?`3px solid ${T.optionBorderSelected}`:`1.5px solid ${T.optionBorder}`,background:imgModel===m.id?T.optionBgSelected:T.optionBg,display:'flex',gap:8,alignItems:'flex-start'}}>
                <ModelIcon model={m.id} s={28}/>
                <div style={{display:'flex',flexDirection:'column',gap:4,overflow:'hidden',minWidth:0,flex:1,alignItems:'flex-start'}}>
                  {/* Three-line layout, consistent across every tile:
                      1. model name (wraps to 2 lines, ellipsis after)
                      2. resolution pills
                      3. ref-count summary (or override via m.refSummary) */}
                  <span style={{color:imgModel===m.id?Y:T.title,fontSize:11,fontWeight:700,lineHeight:1.25,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',wordBreak:'break-word'}}>{m.label}</span>
                  <div style={{display:'flex',gap:3,alignItems:'center',flexWrap:'wrap'}}>
                    {m.resolutions.map(r => <ResPill key={r} res={imgResLabel(r)} small/>)}
                  </div>
                  <span style={{color:T.text3,fontSize:9,lineHeight:1.3,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'100%'}}>{m.refSummary || (m.maxRefs === 0 ? 'Txt to img only' : m.maxRefs === 1 ? '1 ref' : `Up to ${m.maxRefs} refs`)}</span>
                </div>
              </div>
            );
            const sectionHeader = (label) => (
              <div style={{gridColumn:'1 / -1',fontFamily:LABEL_FONT,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:T.text3,padding:'4px 2px 2px'}}>{label}</div>
            );
            return (
              <div style={{position:'absolute',top:'calc(100% + 6px)',left:0,right:0,background:T.popoverBg,border:`1px solid ${T.border}`,borderRadius:14,padding:12,zIndex:50,maxHeight:'70vh',overflowY:'auto',display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(min(200px, calc(50% - 4px)), 1fr))',gap:8,alignContent:'start'}}>
                {sectionHeader('Recommended')}
                {recommended.map(renderCard)}
                {sectionHeader('All Models')}
                {others.map(renderCard)}
              </div>
            );
          })()}
        </div>

        {/* Aspect ratio - hidden for Vectorize and Vector Edit (output preserves source) */}
        {!usesSourceImage && (
        <div data-tutorial="image-output">
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
            <span style={{color:iconC,display:'flex',flexShrink:0}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg></span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',flexShrink:0,position:'relative',top:1}}>Aspect Ratio</span>
          </div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
            {imgRatios.map(r => (
              <Pill key={r} active={imgRatio===r} onClick={()=>setImgRatio(r)} title={r}>
                <span style={{display:'inline-flex',alignItems:'center',gap:5}}>
                  {RATIO_SHAPES[r] && <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">{RATIO_SHAPES[r]}</svg>}
                  <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,position:'relative',top:1}}>{r}</span>
                </span>
              </Pill>
            ))}
          </div>
        </div>
        )}

        {/* Resolution + N - hidden for Vectorize (single SVG out, no batch) */}
        {!isVectorize && (
        <div style={{display:'flex',gap:24,flexWrap:'wrap'}}>
          {imgSel.resolutions.length > 1 && (
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <span style={{color:iconC,display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg></span>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Resolution</span>
              </div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                {imgSel.resolutions.map(r => {
                  const lbl = imgResLabel(r);
                  const active = imgResolution === r;
                  return (
                    <button key={r} onClick={()=>setImgResolution(r)}
                      style={{
                        display:'inline-flex', alignItems:'center', gap:6,
                        height:30,
                        background:'transparent', border:'none', padding:'0',
                        cursor:'pointer', transition:'opacity 0.15s',
                        opacity: active ? 1 : 0.85,
                      }}
                      onMouseEnter={e=>{ e.currentTarget.style.opacity='1'; }}
                      onMouseLeave={e=>{ e.currentTarget.style.opacity = active ? '1' : '0.85'; }}>
                      {/* Yellow radio dot — selection indicator, independent
                          of the resolution color so it reads consistently. */}
                      <span style={{
                        width:14, height:14, borderRadius:'50%',
                        border:`1.5px solid ${Y}`,
                        background: active ? Y : 'transparent',
                        flexShrink:0,
                        transition:'background 0.15s',
                      }}/>
                      <ResPill res={lbl} large/>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div style={{flex:1,minWidth:160}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <span style={{color:iconC,display:'flex'}}><svg width="20" height="20" viewBox="0 -960 960 960" fill="currentColor"><path d="M508-200h224q-7 26-24 42t-44 20L228-85q-33 5-59.5-15.5T138-154L85-591q-4-33 16-59t53-30l46-6v80l-36 5 54 437 290-36Zm-148-80q-33 0-56.5-23.5T280-360v-440q0-33 23.5-56.5T360-880h440q33 0 56.5 23.5T880-800v440q0 33-23.5 56.5T800-280H360Zm0-80h440v-440H360v440Zm220-220ZM218-164Z"/></svg></span>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Outputs</span>
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {[1,2,3,4].map(n => (
                <Pill key={n} active={imgN===n} onClick={()=>setImgN(n)} title={`${n} image${n===1?'':'s'}`}>
                  <span style={{fontFamily:LABEL_FONT,fontSize:14,fontWeight:800,letterSpacing:0.5,position:'relative',top:1}}>×{n}</span>
                </Pill>
              ))}
            </div>
          </div>
        </div>
        )}

        {/* Source-image-driven flows: Vectorize (source → SVG, no
            prompt) and V3 Vector Edit (source + prompt + strength →
            SVG). Both share the same source-image drop zone; the
            controls row above swaps in the appropriate knobs. */}
        {usesSourceImage && (
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {/* Vectorize controls: Max Shapes + SVG Compression. Hidden
              for vector edit (which uses Strength instead). */}
          {isVectorize && (
          <div style={{display:'flex',gap:24,flexWrap:'wrap',alignItems:'flex-start'}}>
            <div style={{flex:1,minWidth:180}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase'}}>Max Shapes</span>
                <span style={{color:Y,fontSize:12,fontWeight:700,fontFamily:'monospace',marginLeft:'auto'}}>
                  {Number(recraftMaxNumShapes) >= 100 ? 'No limit' : recraftMaxNumShapes}
                </span>
              </div>
              <RangeSlider min={1} max={100} step={1} value={Number(recraftMaxNumShapes) || 100}
                onChange={e=>setRecraftMaxNumShapes(parseInt(e.target.value,10))}
                title={Number(recraftMaxNumShapes) >= 100 ? 'No limit' : `Max ${recraftMaxNumShapes} shapes`}/>
              <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
                Cap on the number of vector shapes. Lower = simpler / smaller file. Slide to max for no limit.
              </div>
            </div>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase'}}>SVG Compression</span>
              </div>
              <button onClick={()=>setRecraftSvgCompression(v=>!v)}
                title="Collapse redundant geometry to keep file size small"
                style={{
                  width:40,height:22,borderRadius:11,padding:2,
                  border:`1px solid ${recraftSvgCompression?Y:T.text3}`,
                  background:recraftSvgCompression?Y:'transparent',
                  cursor:'pointer',display:'flex',alignItems:'center',
                  justifyContent:recraftSvgCompression?'flex-end':'flex-start',
                }}>
                <div style={{width:16,height:16,borderRadius:'50%',background:recraftSvgCompression?T.dark:T.text3,transition:'background 0.2s'}}/>
              </button>
            </div>
          </div>
          )}

          {/* Vector Edit controls: Strength slider. 0 = nearly identical
              to source, 1 = minimal similarity. Default 0.4. */}
          {isVectorEdit && (
          <div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase'}}>Edit Strength</span>
              <span style={{color:Y,fontSize:12,fontWeight:700,fontFamily:'monospace',marginLeft:'auto'}}>
                {Number(recraftStrength).toFixed(2)}
              </span>
            </div>
            <RangeSlider min={0} max={1} step={0.05} value={Number(recraftStrength)}
              onChange={e=>setRecraftStrength(parseFloat(e.target.value))}
              title={`Strength ${Number(recraftStrength).toFixed(2)}`}/>
            <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
              How much the output diverges from the source. 0 = nearly identical, 1 = minimal similarity. Lower values stay closer to the original; higher values follow the prompt more aggressively.
            </div>
          </div>
          )}

          <div>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <span style={{color:iconC,display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Source Image</span>
            </div>
            <div
              onDragOver={e=>{e.preventDefault();setVectorizeDrag(true);}}
              onDragLeave={()=>setVectorizeDrag(false)}
              onDrop={e=>{e.preventDefault();setVectorizeDrag(false);handleVectorizeFile(e.dataTransfer.files[0]);}}
              onClick={()=>{
                // Click empty zone -> open the shared ImagePickerDrawer
                // (matching the Start Frame behavior in video mode).
                // Once an image is loaded, the overlay buttons handle
                // clear / re-pick instead of falling through to a
                // hidden-input swap (matches start-frame UX).
                if (!vectorizeSrc) {
                  setPickerTarget('vectorize');
                  setPickerOpen(true);
                }
              }}
              title={vectorizeError ? `Source image: ${vectorizeError}` : (vectorizeSrc ? 'Source image' : 'Click to browse images or drop a PNG / JPG / WEBP')}
              style={{
                width:'100%',aspectRatio:'1 / 1',maxWidth:310,
                borderRadius:4,
                border:`1.5px ${(vectorizeSrc||vectorizeError)?'solid':'dashed'} ${vectorizeError?T.danger:(vectorizeDrag?Y:T.mediaBorder)}`,
                background:vectorizeDrag?Y+'08':T.media,
                display:'flex',alignItems:'center',justifyContent:'center',
                cursor:vectorizeSrc?'default':'pointer',
                position:'relative',overflow:'hidden',
                color:T.iconMuted,
                transition:'border-color 0.2s, background-color 0.2s',
              }}>
              {vectorizeSrc ? (
                <>
                  <img src={vectorizeSrc} alt="Source" style={{width:'100%',height:'100%',objectFit:'contain'}}/>
                  {/* Same overlay button row as the Start Frame zone:
                      clear-image (X) + browse-picker (4-square grid). */}
                  <div style={{position:'absolute',inset:0,background:T.imageOverlay,display:'flex',alignItems:'flex-end',justifyContent:'center',padding:10,gap:6,zIndex:3,pointerEvents:'none'}}>
                    <button onClick={e=>{e.stopPropagation();clearVectorizeSrc();}} title="Clear image"
                      style={{background:T.dark,border:'none',color:Y,borderRadius:20,width:28,height:28,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'auto'}}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>
                    </button>
                    <button onClick={e=>{e.stopPropagation();setPickerTarget('vectorize');setPickerOpen(true);}} title="Browse images"
                      style={{background:T.dark,border:'none',color:Y,borderRadius:20,width:28,height:28,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'auto'}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                    </button>
                  </div>
                </>
              ) : (
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <span style={{fontSize:9,letterSpacing:1,textTransform:'uppercase',opacity:0.6}}>Drop or click</span>
                </div>
              )}
              {/* Warning triangle in the top-left, mirroring the Kling
                  start-frame error indicator. Tooltip carries the full
                  message for hover, the pill below carries the readable
                  copy. */}
              {vectorizeError && (
                <div title={vectorizeError}
                  style={{position:'absolute',top:6,left:6,display:'flex',zIndex:4}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
              )}
              <input id="vectorize-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg" style={{display:'none'}}
                onChange={e=>{ if(e.target.files[0]) handleVectorizeFile(e.target.files[0]); e.target.value=''; }}/>
            </div>
            {/* Inline error pill - same red-tinted box style the Kling
                start/end-frame validator uses. Replaces the prior toast-
                only feedback so the message stays put until fixed. */}
            {vectorizeError ? (
              <div style={{marginTop:8,padding:'8px 10px',borderRadius:6,border:`1px solid ${T.danger}55`,background:`${T.danger}15`,color:T.danger,fontSize:11,lineHeight:1.5}}>
                <div><b>Source image:</b> {vectorizeError}</div>
                <div style={{marginTop:4,opacity:0.85}}>
                  Source images accept PNG / JPG / WEBP / SVG up to 5MB, 256-4096px on each side, and 16MP total. SVGs are auto-rasterized to PNG before upload.
                </div>
              </div>
            ) : (
              <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
                PNG, JPG, WEBP, or SVG (auto-rasterized to PNG). Max 5MB; 256-4096px on each side. Output preserves the source aspect ratio.
              </div>
            )}
          </div>
        </div>
        )}

        {/* References (sits directly above the prompt so quick-links are
            adjacent to where they get inserted). Hidden entirely for
            text-to-image-only models (e.g. Imagen) where maxRefs is 0. */}
        {imgSel.maxRefs > 0 && (
        <div data-tutorial="image-refs">
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
            <span style={{color:iconC,display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>
              References{imgRefs.length > 0 ? ` · ${imgRefs.length}/${imgSel.maxRefs}` : ' · Optional'}
            </span>
          </div>
          <div
            onDragOver={e=>{e.preventDefault(); if (imgRefs.length < imgSel.maxRefs) setImgRefDrag(true);}}
            onDragLeave={()=>setImgRefDrag(false)}
            onDrop={e=>{e.preventDefault();setImgRefDrag(false);addImgRefFiles(e.dataTransfer.files);}}
            style={{display:'flex',gap:8,flexWrap:'wrap',borderRadius:8,outline:imgRefDrag?`2px dashed ${Y}`:'none',outlineOffset:4,background:imgRefDrag?`${Y}08`:'transparent',transition:'background-color 0.15s'}}>
            {imgRefs.map((r, idx) => {
              const c = refColor(idx);
              const refErr = imgRefErrors[idx];
              const borderColor = refErr ? T.danger : c;
              const labelBg    = refErr ? T.danger : c;
              return (
              <div key={idx} title={refErr || undefined}
                style={{position:'relative',width:96,height:96,borderRadius:6,overflow:'hidden',background:T.media,border:`2px solid ${borderColor}`}}>
                <img src={r.blobUrl} alt={`ref ${idx+1}`} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                <button onClick={()=>removeImgRef(idx)} title="Remove reference"
                  style={{position:'absolute',top:4,right:4,background:T.dark,border:'none',color:Y,borderRadius:14,width:22,height:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                {refErr && (
                  <div title={refErr}
                    style={{position:'absolute',top:4,left:4,display:'flex'}}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                )}
                <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'3px 4px',background:labelBg,color:'#0C0E0F',fontSize:10,fontWeight:800,textAlign:'center',fontFamily:'monospace',letterSpacing:0.5}}>{refLabelFor(imgModel, idx)}</div>
              </div>
              );
            })}
            {imgRefsRestoring > 0 && Array.from({length: imgRefsRestoring}).map((_, idx) => (
              <div key={`restoring-${idx}`} title="Loading reference image"
                style={{width:96,height:96,borderRadius:6,border:`1.5px dashed ${Y}55`,background:`${Y}08`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <MiniCyberBars/>
              </div>
            ))}
            {!refsAtMax && imgRefsRestoring === 0 && (
              <button onClick={()=>setImgRefPickerOpen(true)} title="Add reference"
                style={{width:96,height:96,borderRadius:6,border:`1.5px dashed ${T.mediaBorder}`,background:T.media,color:T.text3,cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4,transition:'all 0.15s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=Y;e.currentTarget.style.color=Y;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.mediaBorder;e.currentTarget.style.color=T.text3;}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <span style={{fontSize:9,letterSpacing:1,textTransform:'uppercase',fontWeight:700}}>Add</span>
              </button>
            )}
          </div>
          <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
            Leave blank for text-to-image. Up to {imgSel.maxRefs} reference image{imgSel.maxRefs===1?'':'s'} for {imgSel.label}.
            {imgRefRules && <> Each reference must be at least {imgRefRules.minSide}px on every side and within a 1:{imgRefRules.maxAspectMag} - {imgRefRules.maxAspectMag}:1 aspect ratio.</>}
            {imgSel.note && <span style={{display:'block',color:T.text3,fontStyle:'italic',marginTop:2}}>{imgSel.note}</span>}
          </div>
          {imgRefsHaveErrors && (
            <div style={{marginTop:8,padding:'8px 10px',borderRadius:6,border:`1px solid ${T.danger}55`,background:`${T.danger}15`,color:T.danger,fontSize:11,lineHeight:1.5}}>
              {imgRefErrors.map((err, idx) => err ? (
                <div key={idx}><b>Reference {idx+1}:</b> {err}</div>
              ) : null)}
            </div>
          )}
        </div>
        )}

        {/* Prompt - hidden for Vectorize (image-to-vector takes no prompt) */}
        {!isVectorize && (
        <div data-tutorial="prompt">
          <div style={{marginBottom:5,color:T.text3,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span title="Prompt" style={{display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></span>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Prompt</span>
            </div>
            {imgPrompt && <CopyBtn text={imgPrompt}/>}
          </div>
          {/* Reference quick-link pills. Token format depends on the model:
              Kling Omni uses <<<image_N>>> (1-indexed); Grok image edits
              use <IMAGE_N> (0-indexed). Other endpoints take refs
              positionally and don't get pills. */}
          {supportsRefToken(imgModel) && imgRefs.length > 0 && (
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:6}}>
              {imgRefs.map((_, idx) => {
                const c = refColor(idx);
                const tok = refTokenFor(imgModel, idx);
                const lbl = refLabelFor(imgModel, idx);
                if (!tok) return null;
                return (
                  <button key={idx} type="button"
                    onClick={()=>insertAtCaret(imgPromptTaRef, imgPrompt, setImgPrompt, tok)}
                    title={`Insert ${tok}`}
                    style={{padding:'3px 8px',borderRadius:6,border:`1px solid ${c}`,background:`${c}20`,color:c,cursor:'pointer',fontSize:11,fontWeight:700,fontFamily:'monospace',display:'inline-flex',alignItems:'center',gap:4}}>
                    <span style={{width:8,height:8,borderRadius:2,background:c}}/>
                    {lbl}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{position:'relative'}}>
            <FocusTextarea ref={imgPromptTaRef} value={imgPrompt} maxLength={imagePromptLimit(imgSel).prompt} onChange={e=>setImgPrompt(e.target.value)}
              style={{...inp,resize:'vertical',lineHeight:1.6,fontSize:14,width:'100%',minHeight:90}}/>
          </div>
          <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
            Describe what you want generated. Specific details about subject, style, lighting, and composition produce stronger results.
          </div>
        </div>
        )}

        {/* Negative prompt - hidden for Grok, Vectorize, and BFL (none of
            those forward it upstream, so showing the input is misleading). */}
        {!isVectorize && imgSel.flavor !== 'grok' && imgSel.flavor !== 'bfl' && imgSel.flavor !== 'recraft' && (
          <div>
            <div style={{marginBottom:5,color:T.text3,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg></span>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Negative Prompt</span>
              </div>
              {imgNegPrompt && <CopyBtn text={imgNegPrompt}/>}
            </div>
            <FocusTextarea value={imgNegPrompt} maxLength={imagePromptLimit(imgSel).negative} onChange={e=>setImgNegPrompt(e.target.value)}
              style={{...inp,resize:'none',lineHeight:1.7,fontSize:14,width:'100%',height:38}}/>
            <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
              Optional. List things to exclude from the result (e.g. blurry, distorted, watermark, text).
            </div>
          </div>
        )}

        {/* Model Version - segmented control between the fixed snapshot
            endpoint and BFL's continually-updated preview endpoint. */}
        {!isVectorize && imgSel.supportsLatestVariant && (
          <div>
            <div style={{marginBottom:5,color:T.text3,display:'flex',alignItems:'center',gap:8}}>
              <span style={{display:'flex'}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </span>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Model Version</span>
            </div>
            <div style={{display:'inline-flex',gap:2,padding:2,borderRadius:6,background:T.overlay06||'rgba(255,255,255,0.04)',border:`1px solid ${T.overlay12||'rgba(255,255,255,0.08)'}`}}>
              <button type="button" onClick={()=>setImgUseLatest(false)}
                style={{
                  background: !imgUseLatest ? Y : 'transparent',
                  color: !imgUseLatest ? T.dark : T.text3,
                  border:'none', borderRadius:4, padding:'4px 14px', cursor:'pointer',
                  fontSize:11, fontWeight:700, letterSpacing:0.5, textTransform:'uppercase', fontFamily:'monospace',
                }}>Stable</button>
              <button type="button" onClick={()=>setImgUseLatest(true)}
                style={{
                  background: imgUseLatest ? Y : 'transparent',
                  color: imgUseLatest ? T.dark : T.text3,
                  border:'none', borderRadius:4, padding:'4px 14px', cursor:'pointer',
                  fontSize:11, fontWeight:700, letterSpacing:0.5, textTransform:'uppercase', fontFamily:'monospace',
                }}>Latest</button>
            </div>
            <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
              "Stable" pins to a fixed model snapshot for reproducible results across time. "Latest" uses BFL's continually-updated preview weights — newer quality, but outputs may shift as BFL refines the model. Same price either way.
            </div>
          </div>
        )}

        {/* Raw mode (FLUX 1.1 Ultra only) - skips the model's default
            stylization pass for more natural-looking, less processed output. */}
        {!isVectorize && imgSel.supportsRaw && (
          <div>
            <div style={{marginBottom:5,color:T.text3,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{display:'flex'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                </span>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Raw Mode</span>
              </div>
              <button type="button" onClick={() => setImgRaw(v => !v)}
                style={{
                  background: imgRaw ? Y : 'transparent',
                  border: `1px solid ${imgRaw ? Y : T.text3+'55'}`,
                  color: imgRaw ? T.dark : T.text3,
                  borderRadius:4, padding:'2px 10px', cursor:'pointer',
                  fontSize:10, fontWeight:700, letterSpacing:0.5,
                  textTransform:'uppercase', fontFamily:'monospace',
                }}>
                {imgRaw ? 'On' : 'Off'}
              </button>
            </div>
            <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
              Optional. Disables the default styling pass for a less processed, more natural-looking result — better for documentary or photographic aesthetic.
            </div>
          </div>
        )}

        {/* Image prompt strength (FLUX 1.1 Ultra remix only - requires refs). */}
        {!isVectorize && imgSel.supportsImagePromptStrength && imgRefs.length > 0 && (
          <div>
            <div style={{marginBottom:5,color:T.text3,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{display:'flex'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                </span>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Reference Strength</span>
              </div>
              <span style={{color:Y,fontSize:12,fontFamily:'monospace',fontWeight:700}}>{Number(imgImagePromptStrength).toFixed(2)}</span>
            </div>
            <input type="range" min="0" max="1" step="0.05" value={imgImagePromptStrength}
              onChange={e=>setImgImagePromptStrength(parseFloat(e.target.value))}
              style={{width:'100%', accentColor:Y, cursor:'pointer'}}/>
            <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
              How closely the output should follow the reference image. Lower values give the model more creative freedom; higher values lock onto the source. Defaults to 0.5.
            </div>
          </div>
        )}

        {/* Seed (BFL + Imagen) - integer for reproducible generations. */}
        {!isVectorize && imgSel.supportsSeed && (
          <div>
            <div style={{marginBottom:5,color:T.text3,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{display:'flex'}}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><circle cx="15.5" cy="8.5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="8.5" cy="15.5" r="1.5"/><circle cx="15.5" cy="15.5" r="1.5"/></svg>
                </span>
                <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>Seed</span>
              </div>
              {imgSeed && (
                <button type="button" onClick={()=>setImgSeed('')}
                  title="Clear seed — next generation will use a random one"
                  style={{background:'transparent',border:'none',color:T.text3,cursor:'pointer',fontSize:11,letterSpacing:0.5,padding:'2px 6px'}}>
                  Clear
                </button>
              )}
            </div>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={imgSeed}
              onChange={e=>{
                const v = String(e.target.value || '').replace(/[^\d]/g, '');
                setImgSeed(v);
              }}
              placeholder="Random"
              style={{...inp,fontSize:14,width:220,fontFamily:'monospace'}}/>
            <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
              Optional. Set a specific positive integer to reproduce the same generation. Leave blank for a random seed each run.
            </div>
          </div>
        )}

        {/* Generate button row (sticky). Right-aligned cluster reads as
            an equation: [Buy Credits if low] [Balance] − [Cost] [Generate].
            Balance is the "how much do I have", cost is "how much does this
            take", minus sign makes the relationship literal. */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:12,position:'sticky',bottom:0,zIndex:40,background:T.genRowBg,backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',...(narrow?{margin:'16px -16px 12px',padding:'12px 16px'}:{margin:'16px 0 12px',padding:'12px 12px'})}}>
          {renderClearBtn()}
          <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:1,minWidth:0}}>
            <a href="/bank" style={{textDecoration:'none',color:'inherit',marginRight:8}} title={`Credits available: ${creditBalance.toLocaleString()}${creditOnHold > 0 ? ` (${creditOnHold.toLocaleString()} on hold for generations in progress)` : ''}`}>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'4px 12px',borderRadius:12,background:`${CREDIT}10`,border:`1px solid ${CREDIT}30`,color:CREDIT,cursor:'pointer'}}>
                <span style={{fontFamily:LABEL_FONT,fontSize:9,fontWeight:600,letterSpacing:1,textTransform:'uppercase',color:CREDIT,lineHeight:1}}>Balance</span>
                <div style={{display:'flex',alignItems:'center',gap:4,marginTop:2}}>
                  <CreditIcon size={14} color={CREDIT}/>
                  <span style={{fontFamily:LABEL_FONT,fontSize:14,fontWeight:700,lineHeight:1}}>{creditBalance.toLocaleString()}</span>
                </div>
              </div>
            </a>
            <span aria-hidden="true" style={{color:CREDIT,fontSize:16,fontWeight:900,letterSpacing:-0.5,lineHeight:1,padding:'0 2px'}}>−</span>
            {(() => {
              const usd = estimateImageCostUsd(imgModel, imgN, imgRefs.length, imgResolution, imgRatio, studioKlingImagePpv);
              const credits = ceilCredits(usd);
              return (
                <div style={{display:'flex',alignItems:'center',gap:2}} title={`Estimated cost: ${credits.toLocaleString()} credits ($${usd.toFixed(4)})`}>
                  <CreditIcon size={14} color={CREDIT}/>
                  <span style={{color:CREDIT,fontSize:16,fontWeight:900,letterSpacing:-0.5}}>{credits.toLocaleString()}</span>
                </div>
              );
            })()}
          </div>
          {imgCannotAfford ? (
            <a data-tutorial="generate" href="/bank" title="Buy credits"
              style={{padding:'8px 20px',boxSizing:'border-box',height:40,borderRadius:999,background:CREDIT,border:'none',color:T.dark,textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'center',gap:8,minWidth:narrow?0:160,transition:'all 0.2s',flexShrink:0}}>
              <CreditIcon size={16} color={T.dark}/>
              <span style={{fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:2,textTransform:'uppercase',position:'relative',top:1}}>Buy Credits</span>
            </a>
          ) : (
            <button data-tutorial="generate" onClick={()=>goImageRef.current?.()} disabled={cantGen}
              title={noPrompt?'Add a prompt first':imgRefsHaveErrors?'One or more references violate the model\'s size/aspect rules':'Generate image'}
              style={{padding:'8px 20px',borderRadius:999,background:cantGen?T.overlay12:(imgStage==='done'?T.success:Y),border:'none',color:cantGen?T.text3:T.dark,cursor:cantGen?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,minWidth:narrow?0:160,transition:'all 0.2s',flexShrink:0}}>
              <ImgBtnIcon/>
              <span style={{fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:2,textTransform:'uppercase',position:'relative',top:1}}>Generate</span>
            </button>
          )}
        </div>

        <RecentJobs outputType="image" refreshKey={imgRefreshKey} newestJobId={imgNewestJobId} onReady={handleImageJobsReady} onReuseJob={handleReuseImageJob} onJobComplete={refreshBalances}/>

        {/* Need Help? — scrolls to the top and launches the tutorial,
            same as the header Tutorial button. */}
        <div style={{display:"flex",justifyContent:"center",marginTop:24,marginBottom:4}}>
          <button onClick={()=>{scrollToTop();setTutorialOpen(true);}} title="Open the tutorial"
            style={{display:"inline-flex",alignItems:"center",gap:6,background:"transparent",border:"none",color:'#FF6FB5',cursor:"pointer",fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,padding:"8px 12px",transition:"color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.color='#FF9BD0'} onMouseLeave={e=>e.currentTarget.style.color='#FF6FB5'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            Need Help?
          </button>
        </div>

        {/* Image picker drawer (refs) */}
        <ImagePickerDrawer
          open={imgRefPickerOpen}
          onClose={()=>setImgRefPickerOpen(false)}
          multiFile
          onSelect={addImgRefFromBlob}
          onFileSelected={addImgRefFromFile}
          onFilesSelected={(files)=>{addImgRefFiles(files);setImgRefPickerOpen(false);}}
          confirm={confirm}
        />

        {/* Image picker drawer (vectorize source). Image mode only mounts
            the refs drawer above; the start/end/etc drawer in render-
            VideoMode isn't reachable from here, so we need our own
            instance that responds to pickerOpen + pickerTarget when the
            user clicks the Vectorize source-image zone. */}
        <ImagePickerDrawer
          open={pickerOpen && pickerTarget === 'vectorize'}
          onClose={()=>setPickerOpen(false)}
          onSelect={handleSelectVectorizeExisting}
          onFileSelected={(f)=>{ handleVectorizeFile(f); setPickerOpen(false); }}
          confirm={confirm}
        />
      </div>
    );
  }

  // ─── Preset Load/Save buttons (live in the top tab row) ────────────────────
  // Extracted so they can sit on the right side of the Generate Video /
  // Generate Image tab row instead of cluttering the model selector header.
  // Two flavors because video vs image use different state + filters.
  const presetBtnLoadStyle = (active) => ({
    background: active ? "rgba(255,255,255,0.08)" : "transparent",
    border: "none", color: T.title, cursor: "pointer",
    padding: "4px 8px", borderRadius: 8, display: "flex", alignItems: "center", gap: 4,
    transition: "color 0.15s",
  });
  const presetBtnSaveStyle = (active) => ({
    background: active ? "rgba(255,255,255,0.08)" : "transparent",
    border: "none", color: T.title, cursor: "pointer",
    padding: "4px 8px", borderRadius: 8, display: "flex", alignItems: "center", gap: 4,
    transition: "color 0.15s",
  });
  const videoPresetButtons = (
    <>
      <button onClick={()=>{setPresetPanel(presetPanel==="load"?null:"load");if(presetPanel!=="load"){api.getGeneratePresets().then(d=>setPresets((d.presets||[]).filter(p=>!p.mode||p.mode==='video'))).catch(()=>{});}}} title="Load preset"
        style={presetBtnLoadStyle(presetPanel==="load")}
        onMouseEnter={e=>{if(presetPanel!=="load")e.currentTarget.style.color=Y}} onMouseLeave={e=>{e.currentTarget.style.color=T.title}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
        {!compactTopBar && <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Load</span>}
      </button>
      <button onClick={()=>{if(presetPanel==="save"){setPresetPanel(null);}else{api.getGeneratePresets().then(d=>{const filtered=(d.presets||[]).filter(p=>!p.mode||p.mode==='video');const n=`Gen Settings ${filtered.length+1}`;setPresetName(n);setPresets(filtered);setPresetPanel("save");}).catch(()=>{setPresetName("Gen Settings 1");setPresetPanel("save");});}}} title="Save preset"
        style={presetBtnSaveStyle(presetPanel==="save")}
        onMouseEnter={e=>{if(presetPanel!=="save")e.currentTarget.style.color=Y}} onMouseLeave={e=>{e.currentTarget.style.color=T.title}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
        {!compactTopBar && <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Save</span>}
      </button>
      {presetPanelJsx}
    </>
  );
  const imagePresetButtons = (
    <>
      <button onClick={()=>{setImgPresetPanel(imgPresetPanel==="load"?null:"load");if(imgPresetPanel!=="load"){api.getGeneratePresets().then(d=>setImgPresets((d.presets||[]).filter(p=>p.mode==='image'))).catch(()=>{});}}} title="Load preset"
        style={presetBtnLoadStyle(imgPresetPanel==="load")}
        onMouseEnter={e=>{if(imgPresetPanel!=="load")e.currentTarget.style.color=Y}} onMouseLeave={e=>{e.currentTarget.style.color=T.title}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
        {!compactTopBar && <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Load</span>}
      </button>
      <button onClick={()=>{if(imgPresetPanel==="save"){setImgPresetPanel(null);}else{api.getGeneratePresets().then(d=>{const filtered=(d.presets||[]).filter(p=>p.mode==='image');const n=`Image Settings ${filtered.length+1}`;setImgPresetName(n);setImgPresets(filtered);setImgPresetPanel("save");}).catch(()=>{setImgPresetName("Image Settings 1");setImgPresetPanel("save");});}}} title="Save preset"
        style={presetBtnSaveStyle(imgPresetPanel==="save")}
        onMouseEnter={e=>{if(imgPresetPanel!=="save")e.currentTarget.style.color=Y}} onMouseLeave={e=>{e.currentTarget.style.color=T.title}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
        {!compactTopBar && <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Save</span>}
      </button>
      {imgPresetPanel && (
        <>
          {/* Click-outside backdrop. Mirrors the video preset panel so any
              click off the panel closes it (instead of forcing the user
              back to the Load/Save buttons to dismiss). */}
          <div style={{position:"fixed",inset:0,zIndex:30}} onClick={()=>setImgPresetPanel(null)}/>
        <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,minWidth:280,background:T.popoverBg,border:`1px solid ${T.border}`,borderRadius:14,padding:14,zIndex:50,boxShadow:themeMode==='light'?"0 8px 24px rgba(20,40,40,0.10)":"0 8px 32px rgba(0,0,0,0.6)"}}>
          {imgPresetPanel === "save" ? (
            <div>
              <div style={{fontSize:12,fontWeight:700,color:T.text2,marginBottom:8}}>Save Image Settings</div>
              <input type="text" value={imgPresetName} onChange={e=>setImgPresetName(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&handleSaveImagePreset()}
                autoFocus placeholder="Preset name" style={{...inp,width:"100%",fontSize:13,padding:"8px 10px"}}/>
              <button onClick={handleSaveImagePreset} disabled={!imgPresetName.trim()}
                style={{marginTop:10,padding:"8px 16px",borderRadius:10,background:Y,border:"none",color:T.dark,fontWeight:700,fontSize:12,cursor:imgPresetName.trim()?"pointer":"not-allowed",opacity:imgPresetName.trim()?1:0.5,display:"flex",alignItems:"center",gap:6}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                Save
              </button>
            </div>
          ) : (
            <div>
              <div style={{fontSize:12,fontWeight:700,color:T.text2,marginBottom:12}}>Load Image Settings</div>
              {imgPresets.length === 0 ? (
                <div style={{color:T.text3,fontSize:12,padding:"12px 0",textAlign:"center"}}>No saved image presets yet</div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:300,overflowY:"auto"}}>
                  {imgPresets.map(p => (
                    <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,cursor:"pointer",transition:"background 0.15s",background:T.media}}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}
                      onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.02)"}>
                      <div onClick={()=>handleLoadImagePreset(p)} style={{flex:1,minWidth:0}}>
                        <div style={{color:T.text,fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                        <div style={{color:T.text3,fontSize:10}}>{p.model} · ×{p.n||1} · {p.imageResolution||'1k'}{(p.referenceImageGcsPaths||[]).length>0?` · ${(p.referenceImageGcsPaths||[]).length} ref${(p.referenceImageGcsPaths||[]).length===1?'':'s'}`:''}</div>
                      </div>
                      <button onClick={(e)=>{e.stopPropagation();handleDeleteImagePreset(p.id,p.name);}} title="Delete preset"
                        style={{background:"transparent",border:"none",color:T.text3,cursor:"pointer",padding:2,display:"flex",flexShrink:0,transition:"color 0.15s"}}
                        onMouseEnter={e=>e.currentTarget.style.color=T.danger} onMouseLeave={e=>e.currentTarget.style.color=T.text3}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        </>
      )}
    </>
  );

  // ─── Render: mode tabs ─────────────────────────────────────────────────────
  // The video icon (brand IcoGenerate) has a ~1200-unit viewBox with extra
  // padding around the actual glyph, so at the same display width it reads
  // "Generate video" glyph: the brand IcoGenerate (sparkle + screen-
  // with-play) reused as the icon family for the video tab and every
  // sub-tab destination. Sized via a wrapper so we can render it
  // bigger on the top tab than in the pills without redefining the
  // path.
  const VideoIcon = ({ s = 20 }) => (
    <span style={{display:'inline-flex',alignItems:'center'}}><IcoGenerate s={s} c="currentColor" sw={40}/></span>
  );
  const ModeTabIcon = ({ id }) => id === 'image' ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
  ) : (
    <VideoIcon s={22}/>
  );
  // Header-mounted: the Video / Image top-level tabs live in the page
  // header now (via setHeaderRight) rather than as a row at the top of
  // the body. The page header already provides its own bottom border,
  // so we drop the ModeTabs border + margin that existed when this sat
  // inline above the form.
  const ModeTabs = () => (
    <div data-tutorial="mode-tabs" style={{display:"flex",alignItems:"center",gap:0}}>
      {[
        { id:'video', label:'Generate Video', shortLabel:'Video' },
        { id:'image', label:'Generate Image', shortLabel:'Image' },
      ].map(t => (
        <button key={t.id} onClick={()=>setMode(t.id)}
          title={t.label}
          style={{
            background:'transparent',border:'none',padding: compactTopBar ? '8px 12px' : '8px 16px',cursor:'pointer',
            // Tab labels and icons take on the page-title turquoise
            // (T.title) so they harmonize with the title icon and the
            // selected model text. Inactive uses a dim shade of the same
            // turquoise so it stays legible while still receding. The
            // active underline is brand yellow for stronger visual
            // anchoring.
            borderBottom:`2px solid ${mode===t.id?Y:'transparent'}`,
            color: mode===t.id ? T.title : `${T.title}99`,
            fontSize:13,fontWeight:mode===t.id?700:500,
            fontFamily:LABEL_FONT,letterSpacing:0.5,textTransform:'uppercase',
            transition:'color 0.15s, border-color 0.15s',
            display:'inline-flex',alignItems:'center',gap:8,whiteSpace:'nowrap',
          }}
          onMouseEnter={e=>{ if(mode!==t.id) e.currentTarget.style.color = T.title; }}
          onMouseLeave={e=>{ if(mode!==t.id) e.currentTarget.style.color = `${T.title}99`; }}>
          <ModeTabIcon id={t.id}/>
          {compactTopBar ? t.shortLabel : t.label}
        </button>
      ))}
    </div>
  );

  // Open / close the Generate-page tutorial overlay. Lives at the
  // GenerateView level so it survives mode switches and is rendered
  // inside both return paths (video JSX + renderImageMode) using the
  // same flag - which mode's STEPS array is shown follows the mode.
  const [tutorialOpen, setTutorialOpen] = useState(false);

  // Mount Mode tabs (Video / Image) on the LEFT of the page header and
  // the active mode's preset Load/Save buttons (plus their inline panel
  // popover) on the RIGHT. The header context only stores pre-rendered
  // JSX, so we have to enumerate every state piece the JSX reads in
  // the dep array; that way the effect re-fires and re-pushes a fresh
  // tree whenever any visible bit could change.
  useEffect(() => {
    if (!settingsLoaded) { setHeaderRight(null); return; }
    setHeaderRight(
      <div style={{display:'flex',flex:1,alignItems:'center',justifyContent:'space-between',width:'100%',gap:8}}>
        <ModeTabs />
        <div style={{display:'flex',alignItems:'center',gap:2,flexShrink:0,position:'relative',paddingRight:4}}>
          {/* Tutorial trigger - sits to the LEFT of the preset Load /
              Save controls so the per-mode buttons stay anchored on
              the right edge. Reuses the preset button style for visual
              consistency; label collapses on compactTopBar. */}
          <button onClick={()=>{scrollToTop();setTutorialOpen(true);}} title="Tutorial"
            style={{...presetBtnLoadStyle(false), color:'#FF6FB5'}}
            onMouseEnter={e=>{e.currentTarget.style.color='#FF9BD0';}}
            onMouseLeave={e=>{e.currentTarget.style.color='#FF6FB5';}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            {!compactTopBar && <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Tutorial</span>}
          </button>
          {mode === 'image' ? imagePresetButtons : videoPresetButtons}
        </div>
      </div>
    );
    return () => setHeaderRight(null);
  }, [mode, settingsLoaded, compactTopBar, themeMode, presetPanel, presetName, presets, imgPresetPanel, imgPresetName, imgPresets]); // eslint-disable-line

  if (mode === 'image') return renderImageMode();

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <PageLoader ready={settingsLoaded && jobsReady}/>
      {tutorialOpen && (
        <TutorialOverlay steps={VIDEO_STEPS} onClose={()=>setTutorialOpen(false)}/>
      )}
      {/* Mode tabs + preset Load/Save live in the page header now. The
          Image Input Mode toggle (References vs Start / End Frame) lives
          further down, immediately above the image drop section, so it
          reads as a property of the drops it controls. */}

      {/* ── Settings: Model + Load/Save / Aspect ratio ── */}
      <div style={{display:"flex",flexDirection:"column",gap:22}}>
        <div data-tutorial="video-model" style={{position:"relative"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
            <span style={{color:T.text3,display:"flex"}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg></span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",position:"relative",top:1,flex:1}}>Video Model</span>
          </div>
          <div ref={modelSelRef} style={{display:"flex",alignItems:"center",gap:0,borderBottom:`1px solid ${modelOpen?Y:T.overlay12}`,transition:"border-color 0.15s"}}>
            <div onClick={()=>{if(!modelOpen&&modelSelRef.current){setModelDropTop(modelSelRef.current.getBoundingClientRect().bottom+6);}setModelOpen(!modelOpen);}} title="Select model"
              style={{padding:"6px 0",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",minWidth:0}}>
                {sel && modelAvailable ? (<>
                  <ModelIcon model={model} s={32}/>
                  <span style={{color:T.title,fontSize:narrow?18:22,fontWeight:800,whiteSpace:"nowrap"}}>{sel.label}</span>
                  <ResPill res={sel.res}/>
                </>) : (<>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,color:T.danger,position:"relative",top:1}}>0 credits remaining</span>
                  <a href="/bank" onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 10px",borderRadius:999,background:CREDIT,border:"none",color:T.dark,textDecoration:"none",whiteSpace:"nowrap",marginLeft:4}}>
                    <CreditIcon size={12} color={T.dark}/>
                    <span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Buy Credits</span>
                  </a>
                </>)}
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round" style={{transform:modelOpen?"rotate(180deg)":"none",transition:"transform 0.2s"}}><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
          {modelOpen && (
            <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:T.media,zIndex:30}} onClick={()=>setModelOpen(false)}/>
          )}
          {modelOpen && (() => {
            // All admin-allowed models are listed; mode support is no longer
            // a filter (so the picker never hides a model behind a "Not
            // Supported" section). If the user picks a model that doesn't
            // support the current Image Input Mode, the model -> mode
            // auto-correct effect swaps the toggle to one the model
            // supports (the toggle's References position also locks itself
            // for models that don't take reference images).
            const adminDisabled = new Set(studioVideoDisabled);
            const recommendedIds = studioVideoRecommended.length > 0
              ? studioVideoRecommended.filter(id => !adminDisabled.has(id))
              : ['kling-3.0-pro', 'kling-3.0-omni-pro', 'grok-imagine-720p', 'kling-3.0', 'kling-3.0-omni'];
            const visibleModels = MODELS.filter(m => !adminDisabled.has(m.id));
            const recommended  = recommendedIds.map(id => visibleModels.find(m => m.id === id)).filter(Boolean);
            const others       = visibleModels.filter(m => !recommendedIds.includes(m.id));
            // Sort key for the Recommended + All Models sections:
            //   1. Resolution descending (4K > 1080p > 720p > 480p)
            //   2. Provider: Grok > Kling > Veo
            //   3. Version number descending (e.g. 3.0 > 2.6 > 2.1)
            // Kling's "O" series (O1, O2, ...) has no major.minor in the
            // id; we slot it at 2.7+ since Kling positions O between the
            // 2.x and 3.x lines.
            const VID_RES_RANK = { '4K': 5, '2160p': 5, '1440p': 4, '2K': 4, '1080p': 3, '720p': 2, '480p': 1, '360p': 0 };
            const vidMaxRes = (m) => VID_RES_RANK[m.res] || 0;
            const vidFlavorRank = (m) =>
              m.id.startsWith('grok-')   ? 0 :
              m.id.startsWith('google-') ? 2 :
              1; // kling + its sub-flavors (omni / gen / multi / etc)
            const vidVersion = (m) => {
              const verMatch = m.id.match(/(\d+)\.(\d+)/);
              if (verMatch) return parseFloat(`${verMatch[1]}.${verMatch[2]}`);
              const oMatch = m.id.match(/-o(\d+)/);
              if (oMatch) return 2.6 + parseInt(oMatch[1], 10) * 0.1;
              return 0;
            };
            const vidSort = (a, b) => {
              const ra = vidMaxRes(a), rb = vidMaxRes(b);
              if (ra !== rb) return rb - ra;
              const fa = vidFlavorRank(a), fb = vidFlavorRank(b);
              if (fa !== fb) return fa - fb;
              return vidVersion(b) - vidVersion(a);
            };
            recommended.sort(vidSort);
            others.sort(vidSort);
            const renderCard = (m) => (
              <div key={m.id}
                onClick={()=>{ if (hasCredits) handleModelChange(m.id); else setModelOpen(false); }}
                title={m.label}
                style={{padding:"8px 10px",cursor:hasCredits?"pointer":"default",borderRadius:10,border:(model===m.id&&hasCredits)?`3px solid ${T.optionBorderSelected}`:`1.5px solid ${T.optionBorder}`,background:model===m.id&&hasCredits?T.optionBgSelected:T.optionBg,transition:"all 0.15s",display:"flex",gap:8,alignItems:"flex-start",position:"relative",overflow:"hidden"}}>
                <ModelIcon model={m.id} s={28}/>
                <div style={{display:"flex",flexDirection:"column",gap:3,overflow:"hidden",minWidth:0,flex:1,alignItems:"flex-start"}}>
                  <span style={{color:model===m.id&&hasCredits?Y:T.title,fontSize:11,fontWeight:700,lineHeight:1.25,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',wordBreak:'break-word'}}>{m.label}</span>
                  <ResPill res={m.res} small/>
                  <ModelPriceHint m={m} klingPpv={studioKlingPpv} ctx={{mode:vidInputMode,hasVidRefs:vidRefs.length>0,hasEndFrame:!!(endGcsPath||endImg)}}/>
                </div>
              </div>
            );
            const sectionHeader = (label) => (
              <div style={{gridColumn:'1 / -1',fontFamily:LABEL_FONT,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:T.text3,padding:'4px 2px 2px'}}>{label}</div>
            );
            return (
              <div style={{position:"absolute",top:"calc(100% + 6px)",left:0,right:0,background:T.popoverBg,border:`1px solid ${T.border}`,borderRadius:14,padding:12,zIndex:50,maxHeight:"70vh",overflowY:"auto",display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(min(200px, calc(50% - 4px)), 1fr))",gap:8,alignContent:"start"}}>
                {recommended.length > 0 && sectionHeader('Recommended')}
                {recommended.map(m => renderCard(m))}
                {others.length > 0 && sectionHeader('All Models')}
                {others.map(m => renderCard(m))}
              {!hasCredits && (
                <div style={{position:"absolute",inset:0,borderRadius:14,background:"rgba(0,0,0,0.7)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,zIndex:1}}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <span style={{fontFamily:LABEL_FONT,fontSize:16,fontWeight:600,letterSpacing:1,textTransform:"uppercase",color:T.danger}}>0 Credits Remaining</span>
                  <a href="/bank" style={{display:"flex",alignItems:"center",gap:5,padding:"8px 20px",borderRadius:999,background:CREDIT,border:"none",color:T.dark,textDecoration:"none"}}>
                    <CreditIcon size={16} color={T.dark}/>
                    <span style={{fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Buy Credits</span>
                  </a>
                </div>
              )}
              </div>
            );
          })()}
        </div>

      </div>

      {/* The start/end frame block (image2video) is rendered further down,
          right before the prompt — mirroring how the references grid sits
          before the prompt in ref2video mode. See framesBlock placement. */}

      {/* The References → Video grid and the Text → Video hint are
          rendered further down, just above the prompt textarea, so all
          three sub-modes have their context-specific block in the same
          spot relative to the prompt. */}

      {/* Aspect ratio (sits below the input-mode section so the image
          area is closer to the model selector at the top). For Kling
          Effects it's moved further down - between the effect control
          and the image box - so it renders there instead. */}
      {!isEffectsModel && aspectRatioBlock}

      {/* When sub-mode hides the image drops, audio+duration + (refs) +
          prompt render as their own rows. The references grid sits right
          above the prompt so the colored <<<image_N>>> quick-link pills
          attached to the prompt are next to the colored ref tiles.
          Effects mode hides everything except the image slot(s) - the
          effect scene picks duration / audio / prompt / cfg internally. */}
      {!isEffectsModel && vidInputMode === 'image2video' && !foldNextToStart && audioDurationBlock}
      {/* Effects: effect picker + description, then a row with the aspect
          ratio (we crop on our end) + the always-on audio indicator to its
          right, then the image box(es). */}
      {isEffectsModel && effectsBlock}
      {isEffectsModel && (
        <div style={{display:'flex',alignItems:'flex-start',gap:16,flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:240}}>{aspectRatioBlock}</div>
          <div style={{flexShrink:0}}>{effectsAudioIndicator}</div>
        </div>
      )}
      {/* Image Input Mode toggle - sits directly above the active drop
          section in both modes so it reads as a property of the drops. */}
      {!isEffectsModel && vidInputMode === 'image2video' && inputModeToggle}
      {(vidInputMode === 'image2video' || isEffectsModel) && framesBlock}
      {!isEffectsModel && vidInputMode === 'image2video' && !foldNextToStart && promptBlock}
      {!isEffectsModel && vidInputMode !== 'image2video' && audioDurationBlock}
      {/* Text → Video: hint sits in the same slot as frames / refs so the
          panel layout is consistent across sub-modes. When the current
          model supports ref2video, offer a one-click switch to add a
          reference image rather than making the user hunt for the tab. */}
      {!isEffectsModel && vidInputMode === 'text2video' && (
        <div style={{padding:'14px 16px',borderRadius:10,background:`${Y}08`,border:`1px solid ${Y}20`,color:T.text2,fontSize:12,lineHeight:1.5,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={Y} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <span style={{flex:'1 1 220px',minWidth:0}}><strong style={{color:T.title}}>Text-to-Video mode.</strong> No images needed - just write a prompt below and generate.</span>
          {supportsRef2Video(model) && (
            <button onClick={()=>setVidInputMode('ref2video')}
              title="Switch to References → Video to add a reference image"
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:16,background:'transparent',border:`1px solid ${Y}55`,color:`${Y}99`,cursor:'pointer',fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:'uppercase',transition:'all 0.15s',flexShrink:0}}
              onMouseEnter={e=>{e.currentTarget.style.background=`${Y}10`;e.currentTarget.style.color=Y;e.currentTarget.style.borderColor=Y;}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color=`${Y}99`;e.currentTarget.style.borderColor=`${Y}55`;}}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Reference Image
            </button>
          )}
        </div>
      )}
      {!isEffectsModel && onRefsToggle && (
        <>
          {inputModeToggle}
          <div data-tutorial="drop-zone">
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
            <span style={{color:iconC,display:'flex'}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:'uppercase',position:'relative',top:1}}>
              References{vidRefs.length > 0 ? ` · ${vidRefs.length}/${vidRefMax}` : ` · Up to ${vidRefMax}`}
            </span>
          </div>
          <div
            onDragOver={e=>{e.preventDefault(); if (vidRefs.length < vidRefMax) setVidRefDrag(true);}}
            onDragLeave={()=>setVidRefDrag(false)}
            onDrop={e=>{e.preventDefault();setVidRefDrag(false);addVidRefFiles(e.dataTransfer.files);}}
            style={{display:'flex',gap:8,flexWrap:'wrap',borderRadius:8,outline:vidRefDrag?`2px dashed ${Y}`:'none',outlineOffset:4,background:vidRefDrag?`${Y}08`:'transparent',transition:'background-color 0.15s'}}>
            {vidRefs.map((r, idx) => {
              const c = refColor(idx);
              const err = vidRefErrors[idx];
              return (
              <div key={idx} title={err ? `Ref ${idx+1}: ${err}` : undefined}
                style={{position:'relative',width:120,height:120,borderRadius:6,overflow:'hidden',background:T.media,border:`2px solid ${err ? T.danger : c}`}}>
                <img src={r.blobUrl} alt={`ref ${idx+1}`} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                <button onClick={()=>removeVidRef(idx)} title="Remove reference"
                  style={{position:'absolute',top:4,right:4,background:T.dark,border:'none',color:Y,borderRadius:14,width:22,height:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                {err && (
                  <div style={{position:'absolute',top:4,left:4,display:'flex',zIndex:1}}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  </div>
                )}
                <div style={{position:'absolute',bottom:0,left:0,right:0,padding:'3px 4px',background:c,color:'#0C0E0F',fontSize:10,fontWeight:800,textAlign:'center',fontFamily:'monospace',letterSpacing:0.5}}>{refLabelFor(model, idx)}</div>
              </div>
              );
            })}
            {vidRefsRestoring > 0 && Array.from({length: vidRefsRestoring}).map((_, idx) => (
              <div key={`restoring-${idx}`} title="Loading reference image"
                style={{width:120,height:120,borderRadius:6,border:`1.5px dashed ${Y}55`,background:`${Y}08`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <MiniCyberBars/>
              </div>
            ))}
            {vidRefs.length < vidRefMax && vidRefsRestoring === 0 && (
              <button onClick={()=>setVidRefPickerOpen(true)} title="Add reference"
                style={{width:120,height:120,borderRadius:6,border:`1.5px dashed ${T.mediaBorder}`,background:T.media,color:T.text3,cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4,transition:'all 0.15s'}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=Y;e.currentTarget.style.color=Y;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=T.mediaBorder;e.currentTarget.style.color=T.text3;}}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                <span style={{fontSize:9,letterSpacing:1,textTransform:'uppercase',fontWeight:700}}>Add</span>
              </button>
            )}
          </div>
          {/* Per-ref error breakdown + the Kling min-side / aspect rules.
              Mirrors the error/hint block at the bottom of framesBlock so
              the user is told the constraints up-front (and on a bad
              upload) for reference images, not just start / end frames. */}
          {vidRefsHaveError && klingFrameRules && (
            <div style={{marginTop:8,padding:'8px 10px',borderRadius:6,border:`1px solid ${T.danger}55`,background:`${T.danger}15`,color:T.danger,fontSize:11,lineHeight:1.5}}>
              {vidRefErrors.map((e, idx) => e ? <div key={idx}><b>Ref {idx+1}:</b> {e}</div> : null)}
              <div style={{marginTop:4,opacity:0.85}}>
                Kling requires each reference image to be at least {klingFrameRules.minSide}px on every side and within a 1:{klingFrameRules.maxAspectMag} - {klingFrameRules.maxAspectMag}:1 aspect ratio.
              </div>
            </div>
          )}
          {klingFrameRules && !vidRefsHaveError && (
            <div style={{marginTop:6,color:T.text3,fontSize:11,lineHeight:1.5}}>
              Kling requires each reference image to be at least {klingFrameRules.minSide}px on every side and within a 1:{klingFrameRules.maxAspectMag} - {klingFrameRules.maxAspectMag}:1 aspect ratio.
            </div>
          )}
          </div>
        </>
      )}
      {!isEffectsModel && vidInputMode !== 'image2video' && promptBlock}

      {/* ── Negative prompt (Kling only — Grok doesn't support it. Also
            hidden for Kling Effects, which ignores prompt entirely). ── */}
      {!sel?.grok && !isEffectsModel && (
        <div>
          <div style={{marginBottom:5,color:T.text3,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span title="Negative prompt" style={{display:"flex"}}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg></span>
              <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",position:"relative",top:1}}>Negative Prompt</span>
            </div>
            {negPrompt && <CopyBtn text={negPrompt}/>}
          </div>
          <FocusTextarea value={negPrompt} maxLength={videoPromptLimit(model).negative} onChange={e=>setNegPrompt(e.target.value)} title="Negative prompt"
            style={{...inp,resize:"none",lineHeight:1.7,fontSize:14,width:"100%",height:38}}/>
          <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
            Optional. List things to exclude from the result (e.g. blurry, distorted, watermark, text).
          </div>
        </div>
      )}

      {/* ── CFG Scale (also hidden for Kling Effects). ── */}
      {!isEffectsModel && supportsCfg(model) && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
            <span style={{color:iconC,display:"flex",flexShrink:0}} title="CFG Scale">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
            </span>
            <span style={{fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,color:T.text3,letterSpacing:1,textTransform:"uppercase",flexShrink:0,position:"relative",top:1}}>CFG Scale</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <RangeSlider min={0} max={1} step={0.05} value={cfgScale} onChange={e=>setCfgScale(parseFloat(e.target.value))} title={`CFG Scale: ${cfgScale}`}/>
            <span style={{color:T.text2,fontSize:12,fontFamily:"monospace",minWidth:32,textAlign:"right"}}>{cfgScale.toFixed(2)}</span>
          </div>
          <div style={{color:T.text3,fontSize:11,marginTop:6,lineHeight:1.5}}>
            CFG Scale (Classifier-Free Guidance) determines how strictly a model adheres to your text prompt, with higher values producing results more faithful to the prompt at the potential cost of visual quality.
          </div>
        </div>
      )}

      {/* Generate button row (sticky). Right-aligned cluster reads as
          an equation: [Buy Credits if low] [Balance] − [Cost] [Generate].
          Balance is the "how much do I have", cost is "how much does this
          take", minus sign makes the relationship literal. */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:12,position:"sticky",bottom:0,zIndex:40,background:T.genRowBg,backdropFilter:'blur(8px)',WebkitBackdropFilter:'blur(8px)',...(narrow?{margin:"16px -16px 12px",padding:"12px 16px"}:{margin:"16px 0 12px",padding:"12px 12px"})}}>
        {renderClearBtn()}
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:1,minWidth:0}}>
          <a href="/bank" style={{textDecoration:"none",color:"inherit",marginRight:8}} title={`Credits available: ${creditBalance.toLocaleString()}${creditOnHold > 0 ? ` (${creditOnHold.toLocaleString()} on hold for generations in progress)` : ''}`}>
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"4px 12px",borderRadius:12,background:`${CREDIT}10`,border:`1px solid ${CREDIT}30`,color:CREDIT,cursor:"pointer"}}>
              <span style={{fontFamily:LABEL_FONT,fontSize:9,fontWeight:600,letterSpacing:1,textTransform:"uppercase",color:CREDIT,lineHeight:1}}>Balance</span>
              <div style={{display:"flex",alignItems:"center",gap:4,marginTop:2}}>
                <CreditIcon size={14} color={CREDIT}/>
                <span style={{fontFamily:LABEL_FONT,fontSize:14,fontWeight:700,lineHeight:1}}>{creditBalance.toLocaleString()}</span>
              </div>
            </div>
          </a>
          <span aria-hidden="true" style={{color:CREDIT,fontSize:16,fontWeight:900,letterSpacing:-0.5,lineHeight:1,padding:"0 2px"}}>−</span>
          <div style={{display:"flex",alignItems:"center",gap:2}} title={`Estimated cost: ${price} credits`}>
            <CreditIcon size={14} color={CREDIT}/>
            <span style={{color:CREDIT,fontSize:16,fontWeight:900,letterSpacing:-0.5}}>{price}</span>
          </div>
        </div>
        {(() => {
          // When the user can't afford the generation, the row's right slot
          // becomes a Buy Credits button instead of Generate so the narrow
          // row doesn't crowd with both.
          if (cannotAfford) {
            return (
              <a data-tutorial="generate" href="/bank" title="Buy credits"
                style={{padding:"8px 20px",boxSizing:"border-box",height:44,borderRadius:999,background:CREDIT,border:"none",color:T.dark,textDecoration:"none",transition:"all 0.2s",display:"flex",alignItems:"center",justifyContent:"center",gap:8,minWidth:narrow?0:160,flexShrink:0}}>
                <CreditIcon size={16} color={T.dark}/>
                <span style={{fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:2,textTransform:"uppercase",position:"relative",top:1}}>Buy Credits</span>
              </a>
            );
          }
          // Kling Effects ignore prompt + use their own input rules: single-
          // image scenes need img, dual-image scenes need img + endImg. Cost
          // gating still applies (creditBalance vs effect price).
          // Text-capable models (Kling 3.0 family, Omni, Grok, etc.) can
          // submit with the active drop empty - goVideo coerces to
          // text2video and runs the confirm dialog. So an empty image /
          // ref slot only blocks Generate when the model can't do text.
          const textOnlyOk = !isEffectsModel && supportsText2Video(model);
          const hasInputForMode = isEffectsModel
            ? effectInputsReady
            : vidInputMode === 'image2video' ? (hasImage || textOnlyOk)
            : vidInputMode === 'ref2video'  ? (vidRefs.length > 0 || textOnlyOk)
            : true;
          const frameErrorBlocking = !isEffectsModel && vidInputMode === 'image2video' && framesHaveError;
          const refErrorBlocking   = !isEffectsModel && vidInputMode === 'ref2video' && vidRefsHaveError;
          const noEffectChosen = isEffectsModel && !klingEffectObj;
          const promptRequired = !isEffectsModel && noPrompt;
          const disabled = !hasInputForMode || busy || noCredits || promptRequired || frameErrorBlocking || refErrorBlocking || noEffectChosen || atMaxJobs;
          const title = noCredits ? 'Buy credits first'
            : noEffectChosen ? 'Pick an effect first'
            : !hasInputForMode ? (
                isEffectsModel
                  ? (klingEffectObj?.type === 'dual' ? 'Upload both images first' : 'Upload an image first')
                  : vidInputMode === 'image2video'
                    ? (supportsEndFrameOnly(model) ? 'Upload a starting or end frame first' : 'Upload a starting image first')
                    : 'Add at least one reference image first'
              )
            : promptRequired ? 'Add a prompt first'
            : frameErrorBlocking ? 'One or more frames violate Kling\'s size/aspect rules'
            : refErrorBlocking ? 'One or more references violate Kling\'s size/aspect rules'
            : isEffectsModel ? `Generate ${klingEffectObj?.label || 'effect'}`
            : 'Generate video';
          return (
            <button data-tutorial="generate" onClick={()=>goRef.current?.()} disabled={disabled} title={title}
              style={{padding:"8px 20px",borderRadius:999,background:disabled?T.overlay12:(stage==="done"?T.success:Y),border:"none",color:disabled?T.text3:T.dark,cursor:disabled?"not-allowed":"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",justifyContent:"center",gap:8,minWidth:narrow?0:160,flexShrink:0}}>
              <BtnIcon/>
              <span style={{fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:2,textTransform:"uppercase",position:"relative",top:1}}>Generate</span>
            </button>
          );
        })()}
      </div>

      <RecentJobs outputType="video" refreshKey={refreshKey} newestJobId={newestJobId} onSelectImage={handleSelectExisting} onReuseJob={handleReuseJob} onReady={handleJobsReady} onJobComplete={refreshBalances} onActiveCountChange={setActiveJobCount}/>

      {/* Need Help? — scrolls to the top and launches the tutorial,
          same as the header Tutorial button. */}
      <div style={{display:"flex",justifyContent:"center",marginTop:24,marginBottom:4}}>
        <button onClick={()=>{scrollToTop();setTutorialOpen(true);}} title="Open the tutorial"
          style={{display:"inline-flex",alignItems:"center",gap:6,background:"transparent",border:"none",color:'#FF6FB5',cursor:"pointer",fontFamily:LABEL_FONT,fontSize:12,fontWeight:600,letterSpacing:0.5,padding:"8px 12px",transition:"color 0.15s"}}
          onMouseEnter={e=>e.currentTarget.style.color='#FF9BD0'} onMouseLeave={e=>e.currentTarget.style.color='#FF6FB5'}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          Need Help?
        </button>
      </div>

      {/* ── Image Picker Drawer (start/end frames + vectorize source) ── */}
      <ImagePickerDrawer
        open={pickerOpen}
        onClose={()=>setPickerOpen(false)}
        multiFile={pickerTarget !== 'vectorize'}
        onSelect={
          pickerTarget === 'end'       ? handleSelectEndExisting :
          pickerTarget === 'vectorize' ? handleSelectVectorizeExisting :
          handleSelectExisting
        }
        onFileSelected={(f)=>{
          if (pickerTarget === 'end')            handleEndFile(f);
          else if (pickerTarget === 'vectorize') handleVectorizeFile(f);
          else                                   handleFile(f);
          setPickerOpen(false);
        }}
        onFilesSelected={(files)=>{ handleFramesFiles(files, pickerTarget==='end'?'end':'start'); setPickerOpen(false); }}
        confirm={confirm}
      />

      {/* ── Image Picker Drawer (video references) ── */}
      <ImagePickerDrawer
        open={vidRefPickerOpen}
        onClose={()=>setVidRefPickerOpen(false)}
        multiFile
        onSelect={addVidRefFromBlob}
        onFileSelected={addVidRefFromFile}
        onFilesSelected={(files)=>{addVidRefFiles(files);setVidRefPickerOpen(false);}}
        confirm={confirm}
      />

      {/* ── Kling Effects picker ── */}
      <KlingEffectsPicker
        open={effectsPickerOpen}
        selected={klingEffect}
        onClose={()=>setEffectsPickerOpen(false)}
        onPick={(scene) => {
          setKlingEffect(scene);
          setEffectsPickerOpen(false);
          // If the user picked a single-image effect but a second image is
          // still loaded, clear it. The opposite case (picking a dual after
          // single) leaves the existing image in place; the user adds the
          // second one.
          const eff = getKlingEffect(scene);
          if (eff && eff.type !== 'dual') {
            setEndImg(null); setEndFile(null); setEndGcsPath(null);
            setEndImgNatW(0); setEndImgNatH(0); setEndCropPos(0.5);
          }
        }}
        EffectTile={EffectTile}
      />

    </div>
  );
}

// ── Authenticated image loader (fetches via proxy with auth header) ──────────
// Caches blob URLs in a module-level map to avoid re-fetching the same image
const _blobCache = {};
export function AuthImage({ src, style, alt = "", onClick, loadingIndicator = false }) {
  const [blobUrl, setBlobUrl] = useState(_blobCache[src] || null);
  // Hold the route's PageLoader open while we're fetching this image, so
  // the panel doesn't reveal with placeholder squares that pop into real
  // images a second later.
  useLoadingLock(!blobUrl);
  useEffect(() => {
    if (_blobCache[src]) { setBlobUrl(_blobCache[src]); return; }
    let cancelled = false;
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          _blobCache[src] = url;
          setBlobUrl(url);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [src]);
  if (!blobUrl) {
    if (loadingIndicator) {
      // Visible loader for large previews (e.g. ImagePreviewModal). The min
      // dimensions guarantee the placeholder is large enough to read on a
      // phone even before the image's intrinsic size is known.
      return (
        <div style={{...style, background:T.media, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:14, minWidth:240, minHeight:200}} onClick={onClick}>
          <MiniCyberBars/>
          <span style={{color:"rgba(255,255,255,0.7)", fontSize:11, fontFamily:'ui-monospace,Menlo,Consolas,monospace', letterSpacing:1.2, textTransform:'uppercase'}}>Loading image…</span>
        </div>
      );
    }
    return <div style={{...style, background:T.media, display:"flex", alignItems:"center", justifyContent:"center"}} onClick={onClick}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
    </div>;
  }
  return <img src={blobUrl} alt={alt} style={{...style, cursor: onClick ? "pointer" : undefined}} onClick={onClick}/>;
}

// ── Authenticated video player ───────────────────────────────────────────────
export function AuthVideo({ src, style }) {
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(false);
  // Hold the route's PageLoader open until the video URL resolves (or fails).
  useLoadingLock(!videoUrl && !error);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const cacheBust = src + (src.includes('?') ? '&' : '?') + '_t=' + Date.now();
        const res = await fetch(cacheBust, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
        if (!res.ok || cancelled) { if (!cancelled) setError(true); return; }
        const data = await res.json();
        if (!cancelled) setVideoUrl(data.url);
      } catch { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; };
  }, [src]);

  if (error) return (
    <div style={{...style, background:"rgba(255,45,111,0.06)", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:4}}>
      <span style={{color:T.dangerText,fontSize:11}}>Failed to load video</span>
    </div>
  );
  if (!videoUrl) return (
    <div style={{...style, background:T.media, display:"flex", alignItems:"center", justifyContent:"center"}}>
      <MiniCyberBars/>
    </div>
  );
  return <video controls playsInline loop style={style} src={videoUrl}/>;
}

// Authenticated download helper (delegates to shared downloadVideo,
// which uses the Web Share API on iOS so "Save Video" lands in Photos).
async function authDownload(url, filename) {
  return downloadVideo(url, filename);
}

// Pick the right file extension for an image-job download. The actual
// extension is whatever _saveBytesToGcs picked at save time (svg / png /
// jpg) so we read it off the stored GCS path. Falls back to a model-id
// heuristic for jobs whose paths haven't materialised yet (still
// processing) so the timer card label doesn't flicker.
function imageJobExt(job) {
  const path = (job?.imageOutputGcsPaths || [])[0] || '';
  const ext = (path.match(/\.([a-z0-9]+)$/i) || [])[1];
  if (ext) return ext.toLowerCase();
  const m = String(job?.model || '').toLowerCase();
  if (m === 'recraft-v4-vector' || m === 'recraft-v4-pro-vector' || m === 'recraft-v3-vector' || m === 'recraft-v3-vector-edit' || m === 'recraft-vectorize') return 'svg';
  return 'jpg';
}

// Returns true when an image-job output is worth offering a "Download
// as JPG" button for - i.e. it's stored as something other than JPG
// already (SVG vector outputs, PNG raster outputs with transparency).
function jobOffersJpgConvert(job) {
  const ext = imageJobExt(job);
  return ext === 'svg' || ext === 'png';
}

// Image download — the output-images endpoint streams the image directly
// (not a signed-URL response), so we fetch it and trigger a browser save.
async function authDownloadImage(url, filename) {
  try {
    const user = auth.currentUser;
    if (!user) return;
    const token = await user.getIdToken();
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('Image download failed');
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.error('Image download error:', err);
  }
}

// Helper: get a blob URL for an image by its image ID (for picker / preview).
// When `opts.thumb` is set, fetches the 320px JPEG thumbnail variant - used
// by the picker grid so opening a drawer with N images pulls N tiny tiles
// instead of N full-resolution decodes.
async function fetchImageBlob(imageId, opts) {
  const wantThumb = !!(opts && opts.thumb);
  const cacheKey = `img:${imageId}${wantThumb ? ':thumb' : ''}`;
  if (_blobCache[cacheKey]) return _blobCache[cacheKey];
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken();
  // imageId can be either a plain image-collection id or an "output:JOBID:IDX"
  // synthetic id produced by /api/jobs/images for generated output images.
  let url;
  if (typeof imageId === 'string' && imageId.startsWith('output:')) {
    const [, jobId, idx] = imageId.split(':');
    url = api.getOutputImageUrl(jobId, idx, wantThumb ? { thumb: true } : undefined);
  } else {
    url = api.getImageByIdUrl(imageId, wantThumb ? { thumb: true } : undefined);
  }
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  _blobCache[cacheKey] = blobUrl;
  return blobUrl;
}

// ── Image Picker Drawer ─────────────────────────────────────────────────────
// One tile in the picker grid. Defers the thumbnail fetch until the tile
// scrolls into view (IntersectionObserver), so opening a drawer with N
// images doesn't immediately fire N parallel blob requests. Renders a
// spinner placeholder while the thumb is in flight, the thumb once ready,
// and a fallback icon if the fetch fails.
function PickerTile({ img, thumb, onThumbReady, onSelect, onDelete }) {
  const elRef = useRef(null);
  const startedRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed]   = useState(false);

  useEffect(() => {
    if (thumb || startedRef.current) return;
    const node = elRef.current;
    if (!node) return;
    const fetchThumb = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      setPending(true);
      // Picker grid only ever displays the tile - request the 320px
      // thumbnail variant so 50+ tiles don't pull 50+ full-resolution
      // decodes. The click handler below upgrades to full-res before
      // handing the blob off to the consumer.
      fetchImageBlob(img.id, { thumb: true })
        .then(blob => {
          if (blob) onThumbReady(img.id, blob);
          else setFailed(true);
        })
        .catch(() => setFailed(true))
        .finally(() => setPending(false));
    };
    if (typeof IntersectionObserver === 'undefined') { fetchThumb(); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        fetchThumb();
        io.disconnect();
      }
    }, { rootMargin: '200px 0px' }); // start fetching just before scroll-in
    io.observe(node);
    return () => io.disconnect();
  }, [img.id, thumb, onThumbReady]);

  // Click handler: fetch full-resolution bytes before handing off so the
  // start/end frame crop overlay and Kling pixel/aspect validation operate
  // on the real dims, not the 320px thumbnail dims.
  const handlePick = async () => {
    if (!thumb) return; // wait for the thumb to load before allowing click
    const full = await fetchImageBlob(img.id);
    onSelect(full || thumb, img.gcsPath);
  };

  return (
    <div ref={elRef}
      onClick={handlePick}
      style={{
        aspectRatio:"1",borderRadius:4,overflow:"hidden",cursor:thumb?"pointer":"default",
        border:"1px solid rgba(255,255,255,0.08)",
        background:T.media,
        transition:"border-color 0.15s, transform 0.15s",
        position:"relative",
      }}
      onMouseEnter={e=>{if(thumb){e.currentTarget.style.borderColor=Y;e.currentTarget.style.transform="scale(1.03)";}}}
      onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";e.currentTarget.style.transform="scale(1)";}}>
      {thumb ? (
        <img src={thumb} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
      ) : pending ? (
        <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <MiniCyberBars/>
        </div>
      ) : failed ? (
        <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </div>
      ) : (
        <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
        </div>
      )}
      {img.kind === 'output' && (
        <div title={`Generated image${img.model ? ` (${img.model})` : ''}`}
          style={{position:"absolute",bottom:4,left:4,width:18,height:18,borderRadius:4,background:T.modelBadgeBg,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)"}}>
          <svg width="12" height="12" viewBox="0 0 1200 1200" fill={Y} stroke="none"><path d="m737.34 59.625c0.98438-5.7656 9.3281-5.7656 10.312 0 7.5938 43.547 41.672 77.625 85.219 85.219 5.7656 0.98438 5.7656 9.3281 0 10.312-43.547 7.5938-77.625 41.672-85.219 85.219-0.98438 5.7656-9.3281 5.7656-10.312 0-7.5938-43.547-41.672-77.625-85.219-85.219-5.7656-0.98438-5.7656-9.3281 0-10.312 43.547-7.5938 77.625-41.672 85.219-85.219zm239.21 112.22c-1.7812-10.125-16.312-10.125-18.094 0-13.266 76.172-72.938 135.84-149.11 149.11-10.125 1.7812-10.125 16.312 0 18.094 76.172 13.266 135.84 72.938 149.11 149.11 1.7812 10.125 16.312 10.125 18.094 0 13.266-76.172 72.938-135.84 149.11-149.11 10.125-1.7812 10.125-16.312 0-18.094-76.172-13.266-135.84-72.938-149.11-149.11zm-384.05 135.66c12.422 0 22.5 10.078 22.5 22.5s-10.078 22.5-22.5 22.5h-390c-37.266 0-67.5 30.234-67.5 67.5v480c0 37.266 30.234 67.5 67.5 67.5h780c37.266 0 67.5-30.234 67.5-67.5v-240c0-12.422 10.078-22.5 22.5-22.5s22.5 10.078 22.5 22.5v240c0 62.109-50.391 112.5-112.5 112.5h-780c-62.109 0-112.5-50.391-112.5-112.5v-480c0-62.109 50.391-112.5 112.5-112.5zm-64.688 192.24c-35.016-19.219-77.812 6.0938-77.812 45.984v228.56c0 39.891 42.797 65.203 77.812 45.984l207.74-114.28c36.234-19.922 36.234-72.047 0-91.969zm-32.812 45.984c0-5.6719 6.0938-9.2812 11.109-6.5625l207.74 114.28c5.2031 2.8594 5.2031 10.266 0 13.125l-207.74 114.28c-5.0156 2.7188-11.109-0.89062-11.109-6.5625z" fillRule="evenodd"/></svg>
        </div>
      )}
      {img.kind !== 'output' && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete image"
          style={{position:"absolute",top:4,right:4,width:20,height:20,borderRadius:"50%",background:T.modelBadgeBg,border:"none",color:T.danger,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",opacity:0.7,transition:"opacity 0.15s"}}
          onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity="0.7"}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      )}
    </div>
  );
}

// Recognises a picker entry as SVG so vectorize-mode can hide it. Recraft's
// vectorize endpoint is raster-to-vector and rejects SVG input with a
// generic "unknown format" error - filtering at the picker spares the user
// that round-trip.
function _isSvgPickerEntry(img) {
  if (!img) return false;
  const path = String(img.gcsPath || '').toLowerCase();
  if (path.endsWith('.svg')) return true;
  const model = String(img.model || '').toLowerCase();
  return model === 'recraft-v4-vector' || model === 'recraft-v4-pro-vector' || model === 'recraft-v3-vector' || model === 'recraft-v3-vector-edit' || model === 'recraft-vectorize';
}

function ImagePickerDrawer({ open, onClose, onSelect, onFileSelected, onFilesSelected, confirm, filterRaster = false, multiFile = false }) {
  // Mirror App.js's chrome breakpoint exactly (useNarrow() -> default
  // 800): above 800px the left sidebar is shown and the drawer must clear
  // it (left:4em); at <=800px the navbar moves to the top and the content
  // area goes full-width (left:0), so the drawer must too. Using 600 here
  // left a dead 64px gutter in the 601-800px range where the sidebar is
  // already gone but the drawer hadn't reclaimed the space yet.
  const narrow = useNarrow();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [thumbs, setThumbs]   = useState({});   // imageId -> blobUrl
  const [drag, setDrag]       = useState(false);
  const fileRef = useRef(null);

  // Reset state every time the drawer opens so we never flash stale data
  // from the previous open. The first useEffect below kicks off a fresh
  // listImages request; thumbnails fetch lazily per-tile via
  // IntersectionObserver in PickerTile.
  useEffect(() => {
    if (!open) return;
    setImages([]);
    setThumbs({});
    setLoading(true);
    let cancelled = false;
    api.listImages().then(data => {
      if (cancelled) return;
      setImages(data.images || []);
    }).catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const setThumb = useCallback((id, blob) => {
    setThumbs(prev => prev[id] ? prev : { ...prev, [id]: blob });
  }, []);

  const isPickableFile = (f) =>
    !!f &&
    (f.type.startsWith("image/") || /\.(heic|heif|avif|webp|tiff?|bmp|jpe?g|png|gif|svg)$/i.test(f.name)) &&
    !(filterRaster && (f.type === 'image/svg+xml' || /\.svg$/i.test(f.name))); // raster-only target

  // Accept one OR many dropped / browsed *files*. Multi-file consumers pass
  // onFilesSelected (gets the whole array); single consumers (e.g.
  // vectorize) only get the first file via onFileSelected. Picking an
  // already-stored image from the grid below is always one-at-a-time.
  const handleDropFiles = (list) => {
    let files = Array.from(list || []).filter(isPickableFile);
    if (!multiFile) files = files.slice(0, 1);
    if (files.length === 0) return;
    if (onFilesSelected) onFilesSelected(files);
    else if (onFileSelected) files.forEach(f => onFileSelected(f));
  };

  const visibleImages = filterRaster ? images.filter(i => !_isSvgPickerEntry(i)) : images;

  if (!open) return null;

  // On desktop, offset left by sidebar width (4em = 64px)
  const drawerLeft = narrow ? 0 : "4em";

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{position:"fixed",inset:0,left:drawerLeft,background:"rgba(0,0,0,0.6)",zIndex:998,backdropFilter:"blur(4px)"}}/>
      {/* Drawer */}
      <div style={{
        position:"fixed",bottom:0,left:drawerLeft,right:0,
        height:"75vh",
        background:T.popoverBg,
        borderTop:`1px solid ${T.overlay12}`,
        borderRadius:"20px 20px 0 0",
        zIndex:999,
        display:"flex",flexDirection:"column",
        animation:"slideUp 0.25s ease-out",
      }}>
        {/* Handle + header */}
        <div style={{padding:"12px 20px 8px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <span style={{color:T.text2,fontSize:13,fontWeight:700}}>Source Images</span>
            <span style={{color:T.text3,fontSize:11}}>{visibleImages.length}</span>
          </div>
          <button onClick={onClose} title="Close"
            style={{background:"none",border:"none",color:T.text3,cursor:"pointer",padding:4}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Grid - the drop / browse control is the first tile and shares
            the exact footprint of the image tiles. */}
        <div style={{flex:1,overflowY:"auto",padding:"8px 16px 20px",WebkitOverflowScrolling:"touch"}}>
          {loading ? (
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,padding:48}}>
              <MiniCyberBars/>
              <span style={{color:T.text3,fontSize:11,letterSpacing:0.5,textTransform:"uppercase"}}>Loading images</span>
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(min(180px, 100%), 1fr))",gap:10}}>
              {/* Drop / browse tile (always first) */}
              <div
                onDragOver={e=>{e.preventDefault();setDrag(true);}}
                onDragLeave={()=>setDrag(false)}
                onDrop={e=>{e.preventDefault();setDrag(false);handleDropFiles(e.dataTransfer.files);}}
                onClick={()=>fileRef.current?.click()}
                title={multiFile ? "Drop image(s) here or click to browse" : "Drop image here or click to browse"}
                style={{
                  aspectRatio:"1",borderRadius:4,overflow:"hidden",cursor:"pointer",
                  border:`1.5px dashed ${drag?Y:T.mediaBorder}`,
                  background:drag?Y+"0A":T.media,
                  color:drag?Y:T.text2,
                  display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,
                  textAlign:"center",padding:12,transition:"all 0.2s",
                }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=Y;e.currentTarget.style.color=Y;}}
                onMouseLeave={e=>{if(!drag){e.currentTarget.style.borderColor=T.mediaBorder;e.currentTarget.style.color=T.text2;}}}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
                <span style={{fontSize:10,fontWeight:700,letterSpacing:1,textTransform:"uppercase",lineHeight:1.35}}>Drop / Browse</span>
                <input ref={fileRef} type="file" multiple={multiFile} accept={filterRaster ? "image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" : "image/*,.heic,.heif,.avif,.webp,.tiff,.tif,.bmp"} style={{display:"none"}}
                  onChange={e=>{handleDropFiles(e.target.files); e.target.value="";}}/>
              </div>
              {visibleImages.length === 0 ? (
                <div style={{gridColumn:"1 / -1",color:T.text3,fontSize:12,padding:"14px 4px",lineHeight:1.5}}>{filterRaster ? "No raster images yet. Vectorize needs PNG / JPG input - drop one in the first tile." : "No images yet. Drop one in the first tile to get started."}</div>
              ) : visibleImages.map(img => (
                <PickerTile
                  key={img.id}
                  img={img}
                  thumb={thumbs[img.id]}
                  onThumbReady={setThumb}
                  onSelect={onSelect}
                  onDelete={async () => {
                    const ok = await confirm({ title: "Remove image?", imageUrl: thumbs[img.id] || null, confirmLabel: "Remove", danger: true });
                    if (!ok) return;
                    api.deleteImage(img.id).then(() => setImages(prev => prev.filter(i => i.id !== img.id))).catch(() => {});
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </>
  );
}

// Full-screen preview overlay - opened by clicking a row's tile. Plays
// the effect's clip large + looping (video for mp4/mov/webm, img for
// animated webp/gif) without committing a selection. "Use This Effect"
// commits (onPick); backdrop / X / Esc just closes the preview. Sits
// above the picker (zIndex 1100). onError falls back to the cover.
function KlingEffectPreviewModal({ eff, onClose, onPick }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const [vidFailed, setVidFailed] = useState(false);
  // The modal instance is reused (not remounted) when the user previews a
  // different effect, so clear the video-failed flag on scene change.
  useEffect(() => { setVidFailed(false); }, [eff && eff.scene]);
  if (!eff) return null;
  const preview = eff.previewUrl || null;
  const isVid = preview && /\.(mp4|mov|webm)(\?|$)/i.test(preview);
  const credits = ceilCredits(eff.priceUsd);
  const resizedCover = eff.coverUrl
    ? `${eff.coverUrl}?x-oss-process=image%2Fresize%2Cw_640%2Ch_640%2Cm_mfit%2Fformat%2Cwebp`
    : null;
  // Cover fallback chain when the preview video/image fails: Kling CDN
  // resized cover -> self-hosted 144px thumb (same-origin, reliable) ->
  // raw Kling cover. Walked via the img's data-ci index on each onError.
  const localThumb = `/effect-thumbs/${eff.scene}.webp`;
  const coverChain = [resizedCover, localThumb, eff.coverUrl || null]
    .filter(Boolean).filter((v, i, a) => a.indexOf(v) === i);
  return ReactDOM.createPortal(
    <div onClick={onClose}
      style={{position:'fixed',inset:0,zIndex:1100,background:'rgba(0,0,0,0.82)',display:'flex',alignItems:'center',justifyContent:'center',padding:'4vh 16px'}}>
      <div onClick={e=>e.stopPropagation()}
        style={{width:'100%',maxWidth:440,background:T.popoverBg,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',display:'flex',flexDirection:'column'}}>
        <button onClick={onClose} title="Back to effects (Esc)"
          style={{display:'flex',alignItems:'center',gap:8,width:'100%',padding:'11px 14px',background:T.optionBg,border:'none',borderBottom:`1px solid ${T.border}`,color:T.title,cursor:'pointer',textAlign:'left',fontFamily:LABEL_FONT,fontSize:12,fontWeight:700,letterSpacing:1,textTransform:'uppercase'}}
          onMouseEnter={e=>{ e.currentTarget.style.background=T.optionBgSelected; }}
          onMouseLeave={e=>{ e.currentTarget.style.background=T.optionBg; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Effects
        </button>
        <div style={{position:'relative',width:'100%',aspectRatio:'1 / 1',background:T.media}}>
          {preview && isVid && !vidFailed ? (
            <video key={`v-${eff.scene}`} src={preview} autoPlay muted loop playsInline
              poster={coverChain[0] || undefined}
              style={{width:'100%',height:'100%',objectFit:'cover'}}
              onError={()=>setVidFailed(true)}/>
          ) : (preview && !isVid) ? (
            <img key={`p-${eff.scene}`} src={preview} alt={eff.label}
              style={{width:'100%',height:'100%',objectFit:'cover'}}
              onError={e=>{ const i=(+e.currentTarget.dataset.ci||0); if(coverChain[i]){ e.currentTarget.dataset.ci=i+1; e.currentTarget.src=coverChain[i]; } }}/>
          ) : coverChain.length ? (
            <img key={`c-${eff.scene}`} src={coverChain[0]} alt={eff.label}
              style={{width:'100%',height:'100%',objectFit:'cover'}}
              onError={e=>{ const i=(+e.currentTarget.dataset.ci||1); if(coverChain[i]){ e.currentTarget.dataset.ci=i+1; e.currentTarget.src=coverChain[i]; } }}/>
          ) : (
            <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:T.text3,fontSize:13}}>No preview available</div>
          )}
          <button onClick={onClose} title="Close preview (Esc)"
            style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,0.55)',border:'none',color:'#fff',borderRadius:999,width:30,height:30,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          {eff.type === 'dual' && (
            <span style={{position:'absolute',bottom:8,left:8,background:'rgba(0,0,0,0.6)',color:'#FF7AB6',fontFamily:LABEL_FONT,fontSize:9,fontWeight:800,letterSpacing:1,textTransform:'uppercase',padding:'3px 7px',borderRadius:999}}>Duo · 2 images</span>
          )}
        </div>
        <div style={{padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
          <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{color:T.title,fontSize:16,fontWeight:800,lineHeight:1.2}}>{eff.label}</div>
              <div style={{color:T.text3,fontSize:11,fontFamily:'monospace',marginTop:3}}>{eff.scene}</div>
            </div>
            <span style={{display:'inline-flex',alignItems:'center',gap:4,color:CREDIT,fontWeight:800,fontSize:15,fontFamily:LABEL_FONT,flexShrink:0,whiteSpace:'nowrap'}}>
              <CreditIcon size={13} color={CREDIT}/>{credits.toLocaleString()}
            </span>
          </div>
          <button onClick={()=>onPick(eff.scene)}
            style={{width:'100%',padding:'11px 16px',borderRadius:999,background:Y,border:'none',color:T.dark,cursor:'pointer',fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase'}}>
            Use This Effect
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// One picker row. Clicking ANYWHERE on the row opens the expanded
// preview overlay - selection happens only there, via "Use This
// Effect". No hover video (it made the grid laggy) and no play-badge
// overlay; the tile is just the tiny self-hosted thumbnail.
// isSelected still highlights a previously-picked effect in the list.
function KlingEffectRow({ eff, isSelected, onPreview, EffectTile }) {
  const credits = ceilCredits(eff.priceUsd);
  const open = () => onPreview(eff);
  const onKey = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } };
  return (
    <div role="button" tabIndex={0} onClick={open} onKeyDown={onKey}
      title={`${eff.label} (${eff.scene}) - ${credits} credits${eff.type === 'dual' ? ' - needs 2 portrait images' : ''} - click to preview`}
      onMouseEnter={e=>{ if (!isSelected) e.currentTarget.style.borderColor=T.title; }}
      onMouseLeave={e=>{ if (!isSelected) e.currentTarget.style.borderColor=T.optionBorder; }}
      style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',background:isSelected?T.optionBgSelected:T.optionBg,border:isSelected?`2px solid ${T.optionBorderSelected}`:`1px solid ${T.optionBorder}`,borderRadius:10,cursor:'pointer',textAlign:'left',transition:'border-color 0.12s',overflow:'hidden'}}>
      <div style={{position:'relative',width:40,height:40,flexShrink:0}}>
        <EffectTile eff={eff} size={40}/>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:2,flex:1,minWidth:0}}>
        <span style={{color:isSelected?Y:T.title,fontSize:12,fontWeight:700,lineHeight:1.25,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{eff.label}</span>
        <span style={{color:T.text3,fontSize:10,fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{eff.scene}</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2,flexShrink:0}}>
        <span style={{display:'inline-flex',alignItems:'center',gap:3,color:CREDIT,fontWeight:800,fontSize:13,fontFamily:LABEL_FONT}}>
          <CreditIcon size={11} color={CREDIT}/>{credits.toLocaleString()}
        </span>
        {eff.type === 'dual' && (
          <span style={{fontFamily:LABEL_FONT,fontSize:8,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:'#FF7AB6'}}>Duo</span>
        )}
      </div>
    </div>
  );
}

// Category display names + scan order (registry `category` keys ->
// human labels). Alphabetical by label, except the "Other" catch-all
// (anything unmapped) which is pinned last.
const KLING_CAT_META = [
  ['celebration',    'Celebration'],
  ['dance',          'Dances'],
  ['duo_social',     'Duo & Social'],
  ['fantasy',        'Fantasy'],
  ['halloween',      'Halloween'],
  ['holiday',        'Holiday'],
  ['lunar_new_year', 'Lunar New Year'],
  ['pet',            'Pets'],
  ['photo_style',    'Photo Styles'],
  ['sports',         'Sports & Action'],
  ['transform',      'Transformations'],
  ['other',          'Other'],
];
const KLING_CAT_LABEL = Object.fromEntries(KLING_CAT_META);
const KLING_CAT_COUNT = KLING_EFFECTS.reduce((m, e) => {
  const c = KLING_CAT_LABEL[e.category] ? e.category : 'other';
  m[c] = (m[c] || 0) + 1; return m;
}, {});

// Kling Effects picker drawer. Search box + multi-select category
// filter chips on top; the body is grouped into category sections
// (alphabetical within each) when not searching, or a flat relevance
// list when a query is active. Each row's thumbnail is a tiny self-
// hosted webp; clicking a row opens the expanded preview where the
// effect is actually chosen via "Use This Effect".
function KlingEffectsPicker({ open, onClose, onPick, selected, EffectTile }) {
  const [query, setQuery] = useState('');
  const [cats, setCats] = useState(() => new Set()); // empty = all
  const [previewEff, setPreviewEff] = useState(null);
  const inputRef = useRef(null);
  // Reset search + category filter and refocus each time the drawer
  // opens. We do NOT reset on close - irrelevant to the next open.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCats(new Set());
    setPreviewEff(null);
    const id = setTimeout(() => { try { inputRef.current?.focus(); } catch {} }, 60);
    return () => clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const catOf = (e) => (KLING_CAT_LABEL[e.category] ? e.category : 'other');
  const q = query.trim().toLowerCase();
  const toggleCat = (c) => setCats(prev => {
    const n = new Set(prev);
    if (n.has(c)) n.delete(c); else n.add(c);
    return n;
  });
  // Search across label + scene key + category; then apply the category
  // multi-select (empty set = all categories).
  const filtered = KLING_EFFECTS.filter(e => {
    if (cats.size && !cats.has(catOf(e))) return false;
    if (!q) return true;
    return e.label.toLowerCase().includes(q)
        || e.scene.toLowerCase().includes(q)
        || (e.category || '').toLowerCase().includes(q);
  });
  // Searching -> flat relevance list (dual first, then cheapest).
  // Browsing -> grouped into category sections, alpha within each.
  const grouped = !q;
  const flatSorted = [...filtered].sort((a, b) => {
    if (a.type !== b.type) return a.type === 'dual' ? -1 : 1;
    return a.priceUsd - b.priceUsd;
  });
  const sections = grouped
    ? KLING_CAT_META
        .map(([key, label]) => [key, label, filtered
          .filter(e => catOf(e) === key)
          .sort((a, b) => a.label.localeCompare(b.label))])
        .filter(([, , list]) => list.length > 0)
    : [];

  const gridStyle = { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(min(280px, calc(50% - 4px)), 1fr))', gap:6 };
  const renderRow = (eff) => (
    <KlingEffectRow key={eff.scene} eff={eff}
      isSelected={selected === eff.scene}
      onPreview={setPreviewEff} EffectTile={EffectTile}/>
  );

  return ReactDOM.createPortal(
    <div onClick={onClose}
      style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'4vh 16px'}}>
      <div onClick={e=>e.stopPropagation()}
        style={{width:'100%',maxWidth:720,maxHeight:'92vh',background:T.popoverBg,border:`1px solid ${T.border}`,borderRadius:14,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'12px 14px',borderBottom:`1px solid ${T.border}`}}>
          <span style={{display:'flex',flexShrink:0,color:T.title}} title="Kling Effect"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></span>
          <span style={{display:'flex',flexShrink:0}}><ModelIcon model="kling-effects" s={20}/></span>
          <span style={{fontFamily:LABEL_FONT,fontSize:13,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:T.title}}>Kling Effects</span>
          <span style={{color:T.text3,fontSize:11}}>{filtered.length} of {KLING_EFFECTS.length}</span>
          <div style={{flex:1}}/>
          <button onClick={onClose} title="Close"
            style={{background:'transparent',border:'none',color:T.text2,cursor:'pointer',display:'flex',padding:4}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div style={{padding:'10px 14px',borderBottom:`1px solid ${T.border}`}}>
          <input ref={inputRef} type="text" value={query} onChange={e=>setQuery(e.target.value)}
            placeholder="Search effects (name, scene, category)..."
            style={{...inp,width:'100%',fontSize:13,padding:'8px 12px'}}/>
          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:10}}>
            {(() => {
              const allActive = cats.size === 0;
              return (
                <>
                  <button onClick={()=>setCats(new Set())}
                    style={{padding:'4px 10px',borderRadius:999,cursor:'pointer',fontFamily:LABEL_FONT,fontSize:11,fontWeight:700,letterSpacing:0.3,
                      border:allActive?`1px solid ${T.optionBorderSelected}`:`1px solid ${T.optionBorder}`,
                      background:allActive?T.optionBgSelected:'transparent',color:allActive?Y:T.text2,transition:'all 0.12s'}}>
                    All ({KLING_EFFECTS.length})
                  </button>
                  {KLING_CAT_META.filter(([key]) => KLING_CAT_COUNT[key]).map(([key, label]) => {
                    const active = cats.has(key);
                    return (
                      <button key={key} onClick={()=>toggleCat(key)}
                        style={{padding:'4px 10px',borderRadius:999,cursor:'pointer',fontFamily:LABEL_FONT,fontSize:11,fontWeight:700,letterSpacing:0.3,
                          border:active?`1px solid ${T.optionBorderSelected}`:`1px solid ${T.optionBorder}`,
                          background:active?T.optionBgSelected:'transparent',color:active?Y:T.text2,transition:'all 0.12s'}}>
                        {label} ({KLING_CAT_COUNT[key]})
                      </button>
                    );
                  })}
                </>
              );
            })()}
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:8}}>
          {filtered.length === 0 ? (
            <div style={{padding:'40px 16px',textAlign:'center',color:T.text3,fontSize:13}}>
              No effects match{q ? ` "${query}"` : ''}{cats.size ? ' in the selected categories' : ''}.
            </div>
          ) : grouped ? (
            sections.map(([key, label, list]) => (
              <div key={key} style={{marginBottom:14}}>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'2px 2px 8px',position:'sticky',top:-8,background:T.popoverBg,zIndex:1}}>
                  <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:800,letterSpacing:1,textTransform:'uppercase',color:T.title}}>{label}</span>
                  <span style={{color:T.text3,fontSize:11}}>{list.length}</span>
                  <div style={{flex:1,height:1,background:T.border}}/>
                </div>
                <div style={gridStyle}>{list.map(renderRow)}</div>
              </div>
            ))
          ) : (
            <div style={gridStyle}>{flatSorted.map(renderRow)}</div>
          )}
        </div>
      </div>
      {previewEff && (
        <KlingEffectPreviewModal eff={previewEff}
          onClose={()=>setPreviewEff(null)}
          onPick={onPick}/>
      )}
    </div>,
    document.body
  );
}

// Processing duration timer. Defined at module scope so React's component
// identity stays stable across parent re-renders - if this lived inside
// RecentJobs every render would create a new component type, remounting
// the timer and re-initialising state on every poll.
//
// Anchor strategy: persist a per-job "first observed locally" timestamp
// in localStorage. The first time this client sees the job in an active
// state we stamp `now`; every subsequent render reads that same value.
// We deliberately do NOT use job.createdAt directly because image
// submissions can spend many seconds in pre-create uploads (one
// uploadImage per reference / source frame) before the server stamps
// createdAt - so anchoring on createdAt makes the timer first appear at
// values like 5-15s, not 0. localStorage gives us "starts at 0 from
// the user's POV" plus "survives reload/tab-switch/remount".
function jobAnchorKey(jobId) { return `katana.t0.${jobId}`; }
function readJobAnchor(jobId) {
  try {
    const v = localStorage.getItem(jobAnchorKey(jobId));
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}
function writeJobAnchor(jobId, t) {
  try { localStorage.setItem(jobAnchorKey(jobId), String(t)); } catch {}
}
function clearJobAnchor(jobId) {
  try { localStorage.removeItem(jobAnchorKey(jobId)); } catch {}
}

function ProcessingTimer({ job }) {
  const jobId = job?.id;
  const status = job?.status;
  const isActive = status === 'processing' || status === 'queued';
  // Anchor: stored client-observed start, falling back to createdAt
  // when the user is viewing a job we've never seen actively (e.g.
  // first time on this device, or the job was already terminal). On
  // first active observation, stamp serverNow() into localStorage so
  // every later read returns the same value.
  let anchorMs = jobId ? readJobAnchor(jobId) : null;
  if (anchorMs == null && jobId && isActive) {
    anchorMs = serverNow();
    writeJobAnchor(jobId, anchorMs);
  }
  if (anchorMs == null && job?.createdAt) {
    const t = new Date(job.createdAt).getTime();
    if (Number.isFinite(t)) anchorMs = t;
  }
  // Drop the persisted anchor once the job is terminal so localStorage
  // doesn't grow forever. The timer no longer renders past this point
  // (callers gate on processing/queued), so the cleared value is fine.
  useEffect(() => {
    if (!jobId) return;
    if (status === 'ready' || status === 'failed') clearJobAnchor(jobId);
  }, [jobId, status]);

  const [elapsed, setElapsed] = useState(() => formatElapsed(anchorMs));
  useEffect(() => {
    if (!isActive || anchorMs == null) return;
    setElapsed(formatElapsed(anchorMs));
    const id = setInterval(() => setElapsed(formatElapsed(anchorMs)), 1000);
    return () => clearInterval(id);
  }, [isActive, anchorMs]);
  return <span style={{color:"#5BBFB8",fontSize:10,fontWeight:700,fontFamily:"monospace"}}>{elapsed}</span>;
}

function formatElapsed(anchorMs) {
  if (anchorMs == null) return '0s';
  const diff = Math.max(0, Math.floor((serverNow() - anchorMs) / 1000));
  const m = Math.floor(diff / 60);
  const s = diff % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ── Recent jobs shown below generate form ─────────────────────────────────────
function RecentJobs({ refreshKey, newestJobId, onSelectImage, onReuseJob, onReady, onJobComplete, onActiveCountChange, outputType = 'video' }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { notify } = useNotify();
  // Stack frames in a column next to the video. Switch to row layout only when the
  // viewport is narrow enough that the video has wrapped to its own line (~480px).
  const framesRow = useNarrow(480);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  // Lightbox state for image-gen output previews. { jobId, index, count }.
  const [imagePreview, setImagePreview] = useState(null);
  const prevRefreshKey = useRef(refreshKey);
  const readyCalled = useRef(false);
  const prevStatuses = useRef({});
  const prevImagePaths = useRef({});

  const load = (showIndicator) => {
    if (showIndicator) setLoading(true);
    const minDelay = showIndicator ? new Promise(r => setTimeout(r, 500)) : Promise.resolve();
    Promise.all([api.listJobs(undefined, undefined, outputType), minDelay]).then(([data]) => {
      const j = data.jobs || [];
      // Detect jobs that just completed (were processing/queued, now
      // ready/failed) - fire onJobComplete and drop any persisted timer
      // anchor for them. The ProcessingTimer's own cleanup effect can't
      // catch this transition because the timer is unmounted (not
      // re-rendered) when status flips terminal, so the cleanup lives
      // here where the prev-status diff is already tracked.
      for (const job of j) {
        const prev = prevStatuses.current[job.id];
        if (prev && (prev === 'processing' || prev === 'queued') && (job.status === 'ready' || job.status === 'failed')) {
          if (onJobComplete) onJobComplete(job);
          clearJobAnchor(job.id);
        }
      }
      // Track current statuses
      const statMap = {};
      for (const job of j) statMap[job.id] = job.status;
      prevStatuses.current = statMap;

      // Clear AuthImage cache for jobs whose imageGcsPath changed (e.g. after cropping)
      for (const job of j) {
        const prevPath = prevImagePaths.current[job.id];
        if (prevPath && prevPath !== job.imageGcsPath) {
          delete _blobCache[api.getImageUrl(job.id)];
        }
        prevImagePaths.current[job.id] = job.imageGcsPath;
      }

      setJobs(j);
      setLoading(false);
      if (onActiveCountChange) onActiveCountChange(j.filter(x => x.status === 'processing' || x.status === 'queued').length);
      if (!readyCalled.current) {
        // On initial load, expand the most recent job plus anything still
        // processing or queued so users land on whatever's most relevant.
        const toExpand = new Set();
        if (j.length > 0) toExpand.add(j[0].id);
        for (const job of j) {
          if (job.status === 'processing' || job.status === 'queued') toExpand.add(job.id);
        }
        if (toExpand.size > 0) setExpanded(prev => new Set([...prev, ...toExpand]));
        if (onReady) onReady(j);
        readyCalled.current = true;
      }
    }).catch(() => {
      setLoading(false);
      if (!readyCalled.current && onReady) { readyCalled.current = true; onReady([]); }
    });
  };

  useEffect(() => {
    load();
    // Auto-expand newest job when refreshKey changes (new submission)
    if (refreshKey > prevRefreshKey.current && newestJobId) {
      setExpanded(prev => new Set([...prev, newestJobId]));
    }
    prevRefreshKey.current = refreshKey;
  }, [refreshKey, newestJobId]);

  // Auto-refresh every 8s if any jobs are processing/queued
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'processing' || j.status === 'queued');
    if (!hasActive) return;
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [jobs]);

  if (loading && jobs.length === 0) return null;

  const statusColor = { ready:Y, processing:"#5BBFB8", queued:"#415459", failed:T.danger };
  const statusIcon  = {
    ready:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    processing: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/></svg>,
    queued:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    failed:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  };

  // Icon labels for card detail grid
  const I = {
    id:       <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>,
    model:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>,
    outputs:  <svg width="12" height="12" viewBox="0 -960 960 960" fill="currentColor"><path d="M508-200h224q-7 26-24 42t-44 20L228-85q-33 5-59.5-15.5T138-154L85-591q-4-33 16-59t53-30l46-6v80l-36 5 54 437 290-36Zm-148-80q-33 0-56.5-23.5T280-360v-440q0-33 23.5-56.5T360-880h440q33 0 56.5 23.5T880-800v440q0 33-23.5 56.5T800-280H360Zm0-80h440v-440H360v440Zm220-220ZM218-164Z"/></svg>,
    dur:      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="13" r="8"/><polyline points="12 9 12 13 14.5 15.5"/></svg>,
    ratio:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
    audio:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
    cfg:      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/></svg>,
    cost:     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
    created:  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    completed:<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    task:     <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  };

  const handleDelete = async (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    if (isJobSentToProvider(job)) { notify('error', JOB_SENT_TO_PROVIDER_HINT, 6000); return; }
    const isImage = job?.outputType === 'image';
    // The confirm dialog's <img> can't send auth headers, so we fetch the
    // representative thumbnail bytes and hand it a blob URL instead. Skip
    // the preview entirely if no thumbnail is available or the fetch fails.
    let previewUrl = null;
    try {
      let endpoint = null;
      if (isImage && job?.imageOutputGcsPaths?.length > 0) endpoint = api.getOutputImageUrl(jobId, 0, { thumb: true });
      else if (job?.imageGcsPath)                          endpoint = api.getImageUrl(jobId, { thumb: true });
      else if (job?.posterGcsPath)                         endpoint = api.getPosterUrl(jobId, { thumb: true });
      if (endpoint) {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const blob = await res.blob();
            previewUrl = URL.createObjectURL(blob);
          }
        }
      }
    } catch {}
    // Still waiting to be sent, so cancelling costs nothing.
    const cancelling = job?.status === 'queued';
    const ok = await confirm({
      title: cancelling
        ? (isImage ? 'Cancel this image job?' : 'Cancel this video job?')
        : (isImage ? 'Delete this image job?' : 'Delete this video job?'),
      message: cancelling
        ? "It hasn't been sent to the provider yet, so you won't be charged. The job is removed from your library."
        : 'This permanently removes the job and any generated outputs from your library. Credits already charged are not refunded.',
      imageUrl: previewUrl,
      confirmLabel: cancelling ? 'Cancel job' : 'Delete',
      danger: true,
    });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (!ok) return;
    // The backend refuses (409) if the job was sent while the dialog was open.
    try { await api.deleteJob(jobId); load(); }
    catch (e) { notify('error', e.message, 6000); load(); }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return `${Math.floor(diff/86400000)}d ago`;
  };

  // Handle clicking the source image thumbnail on a card → use that image in generate
  const handleImageClick = (e, job) => {
    e.stopPropagation();
    const imgSrc = api.getImageUrl(job.id);
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(imgSrc, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        onSelectImage(blobUrl, job.imageGcsPath);
      } catch {}
    })();
  };

  // Handle reuse button — fetch the image blob and pass job + blob to parent
  const handleReuse = (e, job) => {
    e.stopPropagation();
    if (typeof onReuseJob !== 'function') return;
    // Image-output jobs don't have a source frame to fetch - skip the
    // blob fetch (which would 404 on /api/files/images/<id>) and hand the
    // job straight to the parent.
    if (!job.imageGcsPath) { onReuseJob(job, null); return; }
    const imgSrc = api.getImageUrl(job.id);
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(imgSrc, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { onReuseJob(job, null); return; }
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        onReuseJob(job, blobUrl);
      } catch { onReuseJob(job, null); }
    })();
  };

  const MAX_RECENT = 30;
  const displayJobs = jobs.slice(0, MAX_RECENT);
  const hasMore = jobs.length > MAX_RECENT;
  const isImageList = outputType === 'image';

  return (
    <div style={{marginTop:32,borderTop:`1px solid rgba(255,255,255,0.06)`,paddingTop:24}}>
      {/* data-tutorial="jobs" marks the header row + the first card's top
          row (below). The tutorial halo unions those two so it hugs just
          the top of the queue, not the whole (tall) list. */}
      <div data-tutorial="jobs" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title={isImageList ? "Image generation jobs" : "Video generation jobs"}>
          {isImageList ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 1200 1200" fill="currentColor" stroke="none"><path d="m737.34 59.625c0.98438-5.7656 9.3281-5.7656 10.312 0 7.5938 43.547 41.672 77.625 85.219 85.219 5.7656 0.98438 5.7656 9.3281 0 10.312-43.547 7.5938-77.625 41.672-85.219 85.219-0.98438 5.7656-9.3281 5.7656-10.312 0-7.5938-43.547-41.672-77.625-85.219-85.219-5.7656-0.98438-5.7656-9.3281 0-10.312 43.547-7.5938 77.625-41.672 85.219-85.219zm239.21 112.22c-1.7812-10.125-16.312-10.125-18.094 0-13.266 76.172-72.938 135.84-149.11 149.11-10.125 1.7812-10.125 16.312 0 18.094 76.172 13.266 135.84 72.938 149.11 149.11 1.7812 10.125 16.312 10.125 18.094 0 13.266-76.172 72.938-135.84 149.11-149.11 10.125-1.7812 10.125-16.312 0-18.094-76.172-13.266-135.84-72.938-149.11-149.11zm-384.05 135.66c12.422 0 22.5 10.078 22.5 22.5s-10.078 22.5-22.5 22.5h-390c-37.266 0-67.5 30.234-67.5 67.5v480c0 37.266 30.234 67.5 67.5 67.5h780c37.266 0 67.5-30.234 67.5-67.5v-240c0-12.422 10.078-22.5 22.5-22.5s22.5 10.078 22.5 22.5v240c0 62.109-50.391 112.5-112.5 112.5h-780c-62.109 0-112.5-50.391-112.5-112.5v-480c0-62.109 50.391-112.5 112.5-112.5zm-64.688 192.24c-35.016-19.219-77.812 6.0938-77.812 45.984v228.56c0 39.891 42.797 65.203 77.812 45.984l207.74-114.28c36.234-19.922 36.234-72.047 0-91.969zm-32.812 45.984c0-5.6719 6.0938-9.2812 11.109-6.5625l207.74 114.28c5.2031 2.8594 5.2031 10.266 0 13.125l-207.74 114.28c-5.0156 2.7188-11.109-0.89062-11.109-6.5625z" fillRule="evenodd"/></svg>
          )}
          <span style={{fontSize:11,fontWeight:700,fontFamily:"monospace"}}>{displayJobs.length}{hasMore ? ` / ${jobs.length}` : ""}</span>
        </div>
        <button onClick={()=>load(true)} title="Refresh" style={{background:"none",border:"none",color:T.text3,cursor:"pointer",padding:4}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            style={loading ? {animation:"spin 0.8s linear infinite"} : {}}><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
        </button>
      </div>
      <div style={{position:"relative"}}>
        {loading && (
          <div style={{position:"absolute",inset:0,zIndex:2,background:"rgba(12,14,15,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",paddingTop:24,borderRadius:4}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round"
              style={{animation:"spin 0.8s linear infinite"}}><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/></svg>
          </div>
        )}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {displayJobs.map((job, jobIdx) => {
          const isExpanded = expanded.has(job.id);
          const sc = statusColor[job.status] || T.text3;
          return (
            <div key={job.id} data-job-id={job.id} style={{borderRadius:4,background:T.media,overflow:"hidden"}}>
              {/* Card header — always visible. The first card's header is
                  also marked data-tutorial="jobs" so the tutorial highlight
                  includes the queue's top row (not the whole list). */}
              <div data-tutorial={jobIdx === 0 ? "jobs" : undefined} onClick={()=>setExpanded(prev => { const s = new Set(prev); if (s.has(job.id)) s.delete(job.id); else s.add(job.id); return s; })} style={{padding:"12px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
                {/* Model icon */}
                <ModelIcon model={job.model} s={32}/>
                {/* Header thumbnail — generated image (when ready), then
                    start/end frame, then ref placeholder, then text-mode
                    placeholder. Always 36×36 so the header row stays
                    vertically aligned. */}
                {(() => {
                  // Image-gen job that finished successfully: show the first
                  // output image as the thumbnail instead of the ref icon.
                  if (job.outputType === 'image' && job.status === 'ready' && (job.imageOutputGcsPaths || []).length > 0) {
                    return (
                      <div title="Generated image" style={{width:36,height:36,borderRadius:4,overflow:'hidden',flexShrink:0,background:T.media,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <AuthImage src={api.getOutputImageUrl(job.id, 0, { thumb: true })} alt="Output"
                          style={{maxWidth:'100%',maxHeight:'100%',borderRadius:4,objectFit:'contain'}}/>
                      </div>
                    );
                  }
                  // Video job without a source frame (ref2video / text2video):
                  // use the server-extracted poster (first frame of the
                  // generated video) once it's available.
                  if (job.outputType !== 'image' && job.status === 'ready' && job.posterGcsPath && !job.imageGcsPath && !job.endImageGcsPath) {
                    return (
                      <div title="Generated video first frame" style={{width:36,height:36,borderRadius:4,background:T.media,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
                        <AuthImage src={api.getPosterUrl(job.id, { thumb: true })} alt="First frame"
                          style={{maxWidth:'100%',maxHeight:'100%',borderRadius:4,objectFit:'contain'}}/>
                      </div>
                    );
                  }
                  if (job.imageGcsPath) {
                    return (
                      <div title="Start frame" style={{width:36,height:36,borderRadius:4,background:T.media,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden"}}>
                        <AuthImage key={job.imageGcsPath} src={api.getImageUrl(job.id, { thumb: true })} alt="Source"
                          onClick={e => handleImageClick(e, job)}
                          style={{maxWidth:"100%",maxHeight:"100%",borderRadius:4,objectFit:"contain",cursor:"pointer"}}/>
                      </div>
                    );
                  }
                  if (job.endImageGcsPath) {
                    return (
                      <div title="End frame (no start frame used)" style={{width:36,height:36,borderRadius:4,background:T.media,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,overflow:"hidden",border:`1px solid ${Y}30`}}>
                        <AuthImage src={api.getEndImageUrl(job.id, { thumb: true })} alt="End frame"
                          style={{maxWidth:"100%",maxHeight:"100%",borderRadius:4,objectFit:"contain"}}/>
                      </div>
                    );
                  }
                  const refs = job.referenceImageGcsPaths || [];
                  // Reference jobs: show a generic layers/stack icon (no actual
                  // ref thumbnails) on the collapsed row. The expanded view
                  // surfaces the real thumbnails. A small badge with the ref
                  // count overlays the icon when refs are attached.
                  if (refs.length > 0 || job.inputMode === 'ref2video' || job.inputMode === 'ref2image') {
                    return (
                      <div title={refs.length > 0 ? `${refs.length} reference image${refs.length===1?'':'s'}` : "Reference-based generation"}
                        style={{position:"relative",width:36,height:36,borderRadius:4,background:T.media,border:`1px solid ${T.mediaBorder}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:T.text2}}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
                        {refs.length > 0 && (
                          <div style={{position:"absolute",bottom:-3,right:-4,minWidth:14,height:14,padding:"0 3px",borderRadius:7,background:T.dark,border:`1px solid ${T.text3}`,color:T.text2,fontSize:9,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"monospace"}}>
                            {refs.length}
                          </div>
                        )}
                      </div>
                    );
                  }
                  // Text-only mode (text2video / text2image): no images at all.
                  if (job.inputMode === 'text2video' || job.inputMode === 'text2image') {
                    return (
                      <div title="Text-only generation" style={{width:36,height:36,borderRadius:4,background:"rgba(238,255,68,0.06)",border:`1px dashed ${Y}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:Y}}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                      </div>
                    );
                  }
                  return null;
                })()}
                {/* Status icon only */}
                <div style={{color:sc,flexShrink:0,display:"flex"}} title={job.status}>{statusIcon[job.status]}</div>
                {/* Info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2,flexWrap:"wrap"}}>
                    {(job.status === "processing" || job.status === "queued") ? (
                      <ProcessingTimer job={job}/>
                    ) : (
                      job.cost > 0 && <span style={{display:"inline-flex",alignItems:"center",gap:1,color:CREDIT,fontSize:10,fontWeight:700}}><CreditIcon size={9} color={CREDIT}/>{Math.round(job.cost / 0.002).toLocaleString()}</span>
                    )}
                    <ResPill res={(job.outputType === 'image' ? imgResLabel(job.imageResolution || '1k') : getResolution(job.model))} small/>
                    {job.outputType !== 'image' && <span style={{color:T.title,fontSize:10}}>{job.duration}s</span>}
                  </div>
                  {(() => {
                    // Effects jobs don't carry a prompt; surface the effect's
                    // human-readable label in the same slot so the card row
                    // stays informative instead of saying "(no prompt text)".
                    if (job.model === 'kling-effects' && job.klingEffectScene) {
                      const eff = getKlingEffect(job.klingEffectScene);
                      const label = eff?.label || job.klingEffectScene;
                      return <div style={{color:T.text2,fontSize:12,overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>Kling Effect: <span style={{color:T.title,fontWeight:600}}>{label}</span></div>;
                    }
                    return <div style={{color:job.prompt?T.text2:T.text3,fontSize:12,fontStyle:job.prompt?"normal":"italic",overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis"}}>{job.prompt || "(no prompt text)"}</div>;
                  })()}
                </div>
                {/* Time + expand arrow */}
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <span style={{color:T.text3,fontSize:10}}>{timeAgo(job.createdAt)}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" strokeLinecap="round" style={{transform:isExpanded?"rotate(180deg)":"none",transition:"transform 0.2s"}}><polyline points="6 9 12 15 18 9"/></svg>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{padding:"0 16px 14px",borderTop:"1px solid rgba(255,255,255,0.05)"}}>

                  {/* Row 1: Starting image + Prompts */}
                  <div style={{paddingTop:12,display:"flex",gap:16,flexWrap:"wrap"}}>
                    {/* Frames + References: stacked column while video sits on
                        the same row; switch to side-by-side row on very narrow
                        viewports where the video wraps below. References render
                        below the start/end frames at half size with the same
                        color-coded ribbon as the form, so multiple fit. */}
                    {(job.imageGcsPath || job.endImageGcsPath || (job.referenceImageGcsPaths || []).length > 0) && (
                      <div style={{flexShrink:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,color:T.text3,marginBottom:6}}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          <span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>
                            {(job.imageGcsPath || job.endImageGcsPath) ? 'Frames' : 'References'}
                          </span>
                        </div>
                        {(() => {
                          const hasFrames = job.imageGcsPath || job.endImageGcsPath;
                          // Wide viewport: single row, no cap.
                          // Narrow + frames present: stack frames in column.
                          // Narrow + refs only: row that wraps at 5-wide
                          //   (40px ref + 8px gap × 5 = 232px).
                          const containerStyle = framesRow
                            ? {flexDirection:"row",maxWidth:undefined}
                            : hasFrames
                              ? {flexDirection:"column",maxWidth:130}
                              : {flexDirection:"row",maxWidth:40*5 + 8*4};
                          return (
                        <div style={{display:"flex",gap:8,flexWrap:"wrap",...containerStyle}}>
                          {job.imageGcsPath && (
                            <div title="Start frame" style={{width:120,height:120,borderRadius:4,background:T.media,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
                              <AuthImage key={job.imageGcsPath} src={api.getImageUrl(job.id, { thumb: true })} alt="Source image"
                                onClick={e => handleImageClick(e, job)}
                                style={{maxWidth:"100%",maxHeight:"100%",borderRadius:4,objectFit:"contain",cursor:"pointer"}}/>
                            </div>
                          )}
                          {job.endImageGcsPath && (
                            <div title="End frame" style={{width:120,height:120,borderRadius:4,background:T.media,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",border:`1px solid ${Y}30`}}>
                              <AuthImage src={api.getEndImageUrl(job.id, { thumb: true })} alt="End frame"
                                style={{maxWidth:"100%",maxHeight:"100%",borderRadius:4,objectFit:"contain"}}/>
                            </div>
                          )}
                          {(job.referenceImageGcsPaths || []).map((_, idx) => {
                            const c = refColor(idx);
                            return (
                              <div key={idx} title={`Reference image_${idx+1}`} style={{position:"relative",width:40,height:40,borderRadius:4,background:T.media,overflow:"hidden",border:`2px solid ${c}`}}>
                                <AuthImage src={api.getReferenceImageUrl(job.id, idx, { thumb: true })} alt={`Reference ${idx+1}`}
                                  style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                                <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"0",background:c,color:"#0C0E0F",fontSize:7,fontWeight:800,textAlign:"center",fontFamily:"monospace",letterSpacing:0.2,lineHeight:1.4}}>{idx+1}</div>
                              </div>
                            );
                          })}
                        </div>
                          );
                        })()}
                      </div>
                    )}
                    {/* Prompts */}
                    <div style={{flex:1,minWidth:180,display:"flex",flexDirection:"column",gap:8,marginRight:56}}>
                      {job.prompt && (
                        <div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,color:T.text3}} title="Prompt">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                              <span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Prompt</span>
                            </div>
                            <button onClick={()=>{navigator.clipboard.writeText(job.prompt);notify("success","Prompt copied");}} title="Copy prompt"
                              style={{background:"transparent",border:"none",color:T.text3,cursor:"pointer",padding:2,display:"flex",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color=T.title} onMouseLeave={e=>e.currentTarget.style.color=T.text3}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            </button>
                          </div>
                          <div style={{color:T.text2,fontSize:12,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{job.prompt}</div>
                        </div>
                      )}
                      {job.negativePrompt && !isGrokModel(job.model) && (
                        <div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                            <div style={{display:"flex",alignItems:"center",gap:6,color:T.text3}} title="Negative prompt">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                              <span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Negative Prompt</span>
                            </div>
                            <button onClick={()=>{navigator.clipboard.writeText(job.negativePrompt);notify("success","Negative prompt copied");}} title="Copy negative prompt"
                              style={{background:"transparent",border:"none",color:T.text3,cursor:"pointer",padding:2,display:"flex",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color=T.title} onMouseLeave={e=>e.currentTarget.style.color=T.text3}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            </button>
                          </div>
                          <div style={{color:T.text2,fontSize:12,lineHeight:1.5,whiteSpace:"pre-wrap"}}>{job.negativePrompt}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Video + Details grid */}
                  {(() => {
                    // For image-output jobs we let the output panel shrink to
                    // available width so the right-side tiles don't get cut
                    // off on tablet portrait. Video jobs keep a fixed 240px
                    // box. The "panel" treatment also kicks in when an image
                    // job is in flight with n>1 so the processing-state grid
                    // matches the eventual ready-state grid.
                    const isImgJob = job.outputType === 'image';
                    const imgOuts = job.imageOutputGcsPaths || [];
                    const imgInFlight = isImgJob && (job.status === 'processing' || job.status === 'queued');
                    const imgPlannedCount = imgInFlight ? Math.max(1, Number(job.n) || 1) : 0;
                    const imgGridCount = job.status === 'ready' ? imgOuts.length : imgPlannedCount;
                    const isImgPanel = isImgJob && imgGridCount > 0;
                    return (
                  <div style={{marginTop:14,display:"flex",gap:16,flexWrap:"wrap"}}>
                    {/* Video area */}
                    {/* Image-grid container grows to fit a 2-column tile
                        layout when there's more than one output, so x4
                        renders as a tidy 2x2 instead of cramming into
                        the 240px slot. For a single output, hold to a
                        fixed 240px so the details grid hugs the image
                        on the same row when there's room. */}
                    <div style={isImgPanel ? (imgGridCount > 1 ? {flex:'1 1 240px',maxWidth:240*2+8,minWidth:0} : {flexShrink:0}) : {flexShrink:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,color:T.text3,marginBottom:6}}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                        <span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Generation</span>
                      </div>
                      {(() => {
                        const boxStyle = {width:240,aspectRatio:"1/1",borderRadius:4,background:T.media,border:`1px solid ${T.mediaBorder}`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",position:"relative"};
                        const tileStyle = {...boxStyle, width:'100%'};
                        // Always 2 columns when there's more than one output, so
                        // an x4 job renders as a tidy 2x2 grid and never collapses
                        // to a single column.
                        const gridStyle = {display:'grid',gap:8,width:'100%',gridTemplateColumns: imgGridCount === 1 ? 'minmax(0, 240px)' : 'repeat(2, minmax(0, 1fr))'};
                        // Image-gen jobs render a responsive grid of tiles so
                        // the layout is identical whether outputs are ready,
                        // processing, or queued. Tiles auto-fit 1-up on narrow
                        // containers and 2-up on wider ones.
                        if (isImgPanel && job.status === 'ready') {
                          return (
                            <div style={gridStyle}>
                              {imgOuts.map((_, idx) => (
                                <div key={idx} style={tileStyle}>
                                  <AuthImage src={api.getOutputImageUrl(job.id, idx, { thumb: true })}
                                    onClick={() => setImagePreview({ jobId: job.id, model: job.model, count: imgOuts.length, index: idx, outputExt: imageJobExt(job) })}
                                    style={{width:'100%',height:'100%',borderRadius:4,objectFit:'contain',cursor:'pointer'}}/>
                                  <button onClick={(e)=>{e.stopPropagation();authDownloadImage(api.getOutputImageUrl(job.id, idx), `katana-ai-${job.id}-${idx+1}.${imageJobExt(job)}`);}}
                                    title={imgOuts.length === 1 ? `Download as ${imageJobExt(job).toUpperCase()}` : `Download image ${idx+1} as ${imageJobExt(job).toUpperCase()}`}
                                    style={{position:'absolute',bottom:6,right:6,width:26,height:26,borderRadius:6,background:'rgba(12,14,15,0.85)',border:`1px solid ${Y}44`,color:Y,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(8px)',transition:'background 0.15s, border-color 0.15s'}}
                                    onMouseEnter={e=>{e.currentTarget.style.background=`${Y}22`;e.currentTarget.style.borderColor=Y;}}
                                    onMouseLeave={e=>{e.currentTarget.style.background='rgba(12,14,15,0.85)';e.currentTarget.style.borderColor=`${Y}44`;}}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                  </button>
                                  {jobOffersJpgConvert(job) && (
                                    <button onClick={(e)=>{e.stopPropagation();authDownloadAsJpg(api.getOutputImageUrl(job.id, idx), `katana-ai-${job.id}-${idx+1}.jpg`);}}
                                      title={imgOuts.length === 1 ? "Download as JPG" : `Download image ${idx+1} as JPG`}
                                      style={{position:'absolute',bottom:6,right:38,height:26,padding:'0 8px',borderRadius:6,background:'rgba(12,14,15,0.85)',border:`1px solid ${Y}44`,color:Y,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:5,backdropFilter:'blur(8px)',fontSize:9,fontWeight:800,letterSpacing:0.5,fontFamily:LABEL_FONT,transition:'background 0.15s, border-color 0.15s'}}
                                      onMouseEnter={e=>{e.currentTarget.style.background=`${Y}22`;e.currentTarget.style.borderColor=Y;}}
                                      onMouseLeave={e=>{e.currentTarget.style.background='rgba(12,14,15,0.85)';e.currentTarget.style.borderColor=`${Y}44`;}}>
                                      <span>JPG</span>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        if (isImgPanel && imgInFlight) {
                          // Same grid + tile geometry as the ready state, just
                          // with a processing indicator inside each tile so the
                          // layout doesn't reflow when the outputs land.
                          return (
                            <div style={gridStyle}>
                              {Array.from({length: imgGridCount}).map((_, idx) => (
                                <div key={idx} style={tileStyle}>
                                  <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                                    <MiniCyberBars/>
                                    <ProcessingTimer job={job}/>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        if (job.status === "ready" && job.gcsPath) {
                          return (
                            <div style={boxStyle}>
                              <AuthVideo src={api.getVideoUrl(job.id)}
                                style={{maxWidth:"100%",maxHeight:"100%",borderRadius:4,objectFit:"contain"}}/>
                            </div>
                          );
                        }
                        return (
                          <div style={boxStyle}>
                            {(job.status === "processing" || job.status === "queued") ? (
                              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                                <MiniCyberBars/>
                                <ProcessingTimer job={job}/>
                              </div>
                            ) : job.status === "failed" ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5"><rect x="2" y="3" width="15" height="11" rx="1"/><polygon points="17 8 21 12 17 16"/></svg>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    {/* Details grid - sits at its intrinsic width next to
                        the output so labels left-align against the image
                        instead of stretching across the empty space. The
                        row's flexWrap on the parent still drops the grid
                        below on narrow viewports. */}
                    <div style={{minWidth:180,display:"flex",flexDirection:"column",gap:6,fontSize:11,paddingTop:22}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Job ID">{I.id}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>ID</span><span style={{color:T.text2,fontFamily:"monospace",fontSize:10}}>{job.id}</span></div>
                      <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Model">{I.model}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Model</span><ModelIcon model={job.model} s={14}/><span style={{color:T.text2}}>{job.model}</span></div>
                      {(() => { const ml = modeLabel(job.inputMode); return ml ? (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Input mode">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h18"/><polyline points="13 5 21 12 13 19"/></svg>
                          <span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Mode</span>
                          <span style={{color:T.text2}}>{ml.long}</span>
                        </div>
                      ) : null; })()}
                      <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Resolution"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg><span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Resolution</span><ResPill res={job.outputType==='image' ? imgResLabel(job.imageResolution || '1k') : getResolution(job.model)} small/></div>
                      {job.outputType !== 'image' && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Duration">{I.dur}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Duration</span><span style={{color:T.title}}>{job.duration}s</span></div>
                      )}
                      {job.outputType === 'image' && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Outputs">{I.outputs}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Outputs</span><span style={{color:T.title}}>×{(job.imageOutputGcsPaths||[]).length || job.n || 1}</span></div>
                      )}
                      <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Aspect Ratio">
                        {I.ratio}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Aspect Ratio</span>
                        {(!job.aspectRatio || job.aspectRatio === "auto" || !supportsRatio(job.model))
                          ? <span style={{color:T.text2}} title="Auto aspect ratio" alt="Auto aspect ratio">A</span>
                          : <span style={{color:T.text2}}>{job.aspectRatio}</span>
                        }
                      </div>
                      {job.outputType !== 'image' && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Audio">{I.audio}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Audio</span><span style={{color:T.text2}}>{(job.audio || isGrokModel(job.model)) ? "Yes" : "No"}</span></div>
                      )}
                      {supportsCfg(job.model) && job.cfgScale !== undefined && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="CFG Scale">{I.cfg}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>CFG</span><span style={{color:T.text2}}>{job.cfgScale}</span></div>
                      )}
                      {job.cost > 0 && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Cost">{I.cost}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Cost</span><span style={{display:"inline-flex",alignItems:"center",gap:1,color:CREDIT,fontWeight:700}}><CreditIcon size={11} color={CREDIT}/>{Math.round(job.cost / 0.002).toLocaleString()}</span></div>
                      )}
                      <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Created">{I.created}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Created</span><span style={{color:T.text2}}>{job.createdAt ? new Date(job.createdAt).toLocaleString() : "-"}</span></div>
                      {job.completedAt && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Completed">{I.completed}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Completed</span><span style={{color:T.text2}}>{new Date(job.completedAt).toLocaleString()}</span></div>
                      )}
                      {job.klingTaskId && (
                        <div style={{display:"flex",alignItems:"center",gap:8,color:T.text3}} title="Kling Task ID">{I.task}<span style={{fontFamily:LABEL_FONT,fontSize:10,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Task</span><span style={{color:T.text2,fontFamily:"monospace",fontSize:10}}>{job.klingTaskId}</span></div>
                      )}
                    </div>
                  </div>
                    );
                  })()}

                  {/* Error detail for failed jobs */}
                  {job.status === "failed" && job.error && (
                    <div style={{marginTop:10,padding:"10px 12px",borderRadius:8,background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,display:"flex",alignItems:"flex-start",gap:8}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:1}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                      <div style={{color:T.dangerText,fontSize:12,lineHeight:1.5,whiteSpace:"pre-wrap",fontFamily:"monospace"}}>{job.error}</div>
                    </div>
                  )}
                  {/* Action buttons */}
                  <div style={{marginTop:12,display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    {/* Reuse settings */}
                    <button onClick={e => handleReuse(e, job)} title="Reuse settings"
                      style={{padding:"6px 12px",borderRadius:8,background:`${Y}20`,color:Y,border:`1.5px solid ${Y}66`,cursor:"pointer",display:"flex",alignItems:"center",gap:6,transition:"all 0.15s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background=`${Y}35`;e.currentTarget.style.borderColor=Y;}} onMouseLeave={e=>{e.currentTarget.style.background=`${Y}20`;e.currentTarget.style.borderColor=`${Y}66`;}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                      <span style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",position:"relative",top:1}}>Reuse</span>
                    </button>
                    {/* Download images (image-gen jobs) - one button that
                        downloads all outputs sequentially. Icon only; the
                        title attribute carries the count. */}
                    {/* Download all as JPG (vector + transparent-PNG outputs only).
                        Sits to the LEFT of the native-format download button per
                        the convention used on the inline tiles. */}
                    {job.status === "ready" && job.outputType === 'image' && (job.imageOutputGcsPaths || []).length > 0 && jobOffersJpgConvert(job) && (
                      <button onClick={async () => {
                        const outs = job.imageOutputGcsPaths || [];
                        for (let i = 0; i < outs.length; i++) {
                          await authDownloadAsJpg(api.getOutputImageUrl(job.id, i), `katana-ai-${job.id}-${i+1}.jpg`);
                        }
                      }} title={(job.imageOutputGcsPaths || []).length === 1 ? "Download as JPG" : `Download all ${(job.imageOutputGcsPaths || []).length} images as JPG`}
                        style={{height:32,padding:'0 10px',borderRadius:8,background:`${Y}20`,color:Y,border:`1.5px solid ${Y}66`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6,fontFamily:LABEL_FONT,fontSize:11,fontWeight:700,letterSpacing:0.5,transition:"all 0.15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background=`${Y}35`;e.currentTarget.style.borderColor=Y;}} onMouseLeave={e=>{e.currentTarget.style.background=`${Y}20`;e.currentTarget.style.borderColor=`${Y}66`;}}>
                        <span>JPG</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </button>
                    )}
                    {job.status === "ready" && job.outputType === 'image' && (job.imageOutputGcsPaths || []).length > 0 && (
                      <button onClick={async () => {
                        const outs = job.imageOutputGcsPaths || [];
                        const ext = imageJobExt(job);
                        for (let i = 0; i < outs.length; i++) {
                          await authDownloadImage(api.getOutputImageUrl(job.id, i), `katana-ai-${job.id}-${i+1}.${ext}`);
                        }
                      }} title={(job.imageOutputGcsPaths || []).length === 1 ? `Download as ${imageJobExt(job).toUpperCase()}` : `Download all ${(job.imageOutputGcsPaths || []).length} images as ${imageJobExt(job).toUpperCase()}`}
                        style={{width:32,height:32,padding:0,borderRadius:8,background:`${Y}20`,color:Y,border:`1.5px solid ${Y}66`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background=`${Y}35`;e.currentTarget.style.borderColor=Y;}} onMouseLeave={e=>{e.currentTarget.style.background=`${Y}20`;e.currentTarget.style.borderColor=`${Y}66`;}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </button>
                    )}
                    {/* Download video — icon only */}
                    {job.status === "ready" && job.gcsPath && job.outputType !== 'image' && (
                      <button onClick={() => authDownload(api.getVideoUrl(job.id), `${job.id}.mp4`)} title="Download video"
                        style={{width:32,height:32,padding:0,borderRadius:8,background:`${Y}20`,color:Y,border:`1.5px solid ${Y}66`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background=`${Y}35`;e.currentTarget.style.borderColor=Y;}} onMouseLeave={e=>{e.currentTarget.style.background=`${Y}20`;e.currentTarget.style.borderColor=`${Y}66`;}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </button>
                    )}
                    {/* Delete — icon only, opens confirm dialog. Dimmed once the
                        job is with the provider; clicking then explains why. */}
                    <button onClick={()=>handleDelete(job.id)}
                      title={isJobSentToProvider(job) ? JOB_SENT_TO_PROVIDER_HINT : job.status === "queued" ? "Cancel job" : "Delete job"}
                      style={{width:32,height:32,padding:0,borderRadius:8,background:`${T.danger}20`,color:T.danger,border:`1.5px solid ${T.danger}66`,cursor:isJobSentToProvider(job)?"not-allowed":"pointer",opacity:isJobSentToProvider(job)?0.4:1,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}
                      onMouseEnter={e=>{if(isJobSentToProvider(job))return;e.currentTarget.style.background=`${T.danger}35`;e.currentTarget.style.borderColor=T.danger;}} onMouseLeave={e=>{e.currentTarget.style.background=`${T.danger}20`;e.currentTarget.style.borderColor=`${T.danger}66`;}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
      {hasMore && (
        <div style={{marginTop:16,padding:"12px 16px",borderRadius:10,background:T.media,color:T.text3,fontSize:10,lineHeight:1.5,textAlign:"left"}}>
          Showing the most recent {MAX_RECENT} generations. A complete list can be found in the{" "}
          <span onClick={()=>navigate("/library")} style={{color:T.title,cursor:"pointer",fontWeight:600,borderBottom:`1px solid ${T.title}40`}}>Library</span>.
        </div>
      )}
      {/* Image-output lightbox. Click background or close button to dismiss;
          arrow keys / chevrons step through outputs when there's more than one. */}
      {imagePreview && (
        <ImagePreviewModal
          jobId={imagePreview.jobId}
          model={imagePreview.model}
          outputExt={imagePreview.outputExt}
          index={imagePreview.index}
          count={imagePreview.count}
          onClose={() => setImagePreview(null)}
          onIndexChange={(i) => setImagePreview(p => p ? { ...p, index: i } : null)}
        />
      )}
    </div>
  );
}

// ── Lightbox for image-gen outputs ──────────────────────────────────────────
export function ImagePreviewModal({ jobId, model, outputExt, index, count, onClose, onIndexChange }) {
  // outputExt is pre-computed at click time (same imageJobExt the
  // download buttons use) so the modal can decide whether to offer
  // the secondary "JPG" download without needing the full job object.
  const ext = outputExt || imageJobExt({ model });
  const offersJpg = ext === 'svg' || ext === 'png';
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && count > 1) onIndexChange((index + 1) % count);
      else if (e.key === 'ArrowLeft'  && count > 1) onIndexChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onIndexChange]);

  // Portal to document.body so the modal escapes the route scroller's
  // stacking context (the scroller at zIndex:1 in the root context
  // creates its own stacking context, so the modal's internal z:9999
  // can't beat the page header's z:50 - which on tablets lets the
  // balance pill paint over the image and hide the action buttons).
  return ReactDOM.createPortal((
    <div onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}>
      {/* Prev/Next chevrons + counter sit at the viewport edges since the
          image can be any aspect ratio. */}
      {count > 1 && (
        <>
          <button onClick={(e)=>{e.stopPropagation();onIndexChange((index - 1 + count) % count);}} title="Previous (←)"
            style={{position:'absolute',left:16,top:'50%',transform:'translateY(-50%)',width:48,height:48,borderRadius:24,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.15)',color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button onClick={(e)=>{e.stopPropagation();onIndexChange((index + 1) % count);}} title="Next (→)"
            style={{position:'absolute',right:16,top:'50%',transform:'translateY(-50%)',width:48,height:48,borderRadius:24,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.15)',color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:2}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <div style={{position:'absolute',bottom:16,left:'50%',transform:'translateX(-50%)',padding:'6px 12px',borderRadius:12,background:'rgba(0,0,0,0.6)',color:'#fff',fontFamily:'monospace',fontSize:12,zIndex:2}}>
            {index + 1} / {count}
          </div>
        </>
      )}
      {/* Action buttons pinned to the modal's top-right with safe-area
          padding so they clear the iOS notch / Android status bar. They
          used to sit just above the image (column flex), which on a tall
          mobile viewport pushed them under the system UI and made them
          unreachable. */}
      <div onClick={(e)=>e.stopPropagation()} style={{position:'absolute', top:'max(12px, env(safe-area-inset-top, 0px))', right:'max(12px, env(safe-area-inset-right, 0px))', display:'flex', gap:8, alignItems:'center', zIndex:3}}>
        {offersJpg && (
          <button onClick={(e)=>{e.stopPropagation();authDownloadAsJpg(api.getOutputImageUrl(jobId, index), `katana-ai-${jobId}-${index+1}.jpg`);}} title="Download as JPG"
            style={{padding:'8px 14px',borderRadius:20,background:`${Y}22`,border:`1px solid ${Y}55`,color:Y,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontWeight:700}}>
            <span style={{fontSize:11,letterSpacing:0.5,textTransform:'uppercase'}}>JPG</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          </button>
        )}
        <button onClick={(e)=>{e.stopPropagation();authDownloadImage(api.getOutputImageUrl(jobId, index), `katana-ai-${jobId}-${index+1}.${ext}`);}} title={`Download as ${ext.toUpperCase()}`}
          style={{padding:'8px 14px',borderRadius:20,background:`${Y}22`,border:`1px solid ${Y}55`,color:Y,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontWeight:700}}>
          <span style={{fontSize:11,letterSpacing:0.5,textTransform:'uppercase'}}>{ext.toUpperCase()}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        </button>
        <button onClick={(e)=>{e.stopPropagation();onClose();}} title="Close (Esc)"
          style={{width:36,height:36,borderRadius:18,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.15)',color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      {/* Image. AuthImage shows a centered "LOADING IMAGE…" indicator while
          the blob is being fetched so the user knows the preview is on
          its way (large outputs can take a few seconds). */}
      <div onClick={(e)=>e.stopPropagation()} style={{position:'relative',maxWidth:'92vw',maxHeight:'82vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <AuthImage src={api.getOutputImageUrl(jobId, index)} alt={`Output ${index+1}`} loadingIndicator
          style={{maxWidth:'92vw',maxHeight:'82vh',borderRadius:6,boxShadow:'0 8px 40px rgba(0,0,0,0.6)'}}/>
      </div>
    </div>
  ), document.body);
}
